import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { retailAPI, authAPI } from '../api/client';
import { useAuthStore } from '../store';
import { formatKyivDateTime, formatKyivDate } from '../utils/kyivTime';
import ProductModal from './ProductModal';

// Посилання на товар на сайті: витягуємо тільки origin (для prom.ua домен містить шлях магазину)
function promProductUrl(domain, promId) {
  try { return `${new URL(domain).origin}/ua/p${promId}-krossovki.html`; }
  catch { return `${domain}/ua/p${promId}-krossovki.html`; }
}

// Конвертація дат для поля знижки Prom: у БД зберігаємо "ДД.ММ.РРРР" (формат Prom API),
// а <input type="date"> працює тільки з "РРРР-ММ-ДД".
function ddmmyyyyToInput(s) {
  if (!s) return '';
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(s.trim());
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
}
function inputToDdmmyyyy(s) {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  return m ? `${m[3]}.${m[2]}.${m[1]}` : null;
}
// Знижка Prom: дата початку завжди сьогодні, дата закінчення — не більше
// ніж на 30 днів пізніше (бекенд все одно мовчки виправить будь-яке інше
// значення — тут тільки для зручності: поле дати відразу обмежене в UI).
function todayInputDate() {
  return new Date().toISOString().slice(0, 10);
}
function maxDiscountEndInputDate() {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
}

// Валідація формату ALL SKU: ALL00000 / SM00000 (з опційним суфіксом постачальника)
// Порожній рядок вважається допустимим (прибрати прив'язку)
const _ALL_SKU_RE = /^(ALL|SM)\d{4,}[A-Za-zА-ЯҐЄІЇа-яґєії0-9 ]*$/i;
function isValidAllSku(val) {
  if (!val || !val.trim()) return true;
  return _ALL_SKU_RE.test(val.trim());
}

/**
 * Парсить значення характеристики з Prom-формату.
 * "[{'id': 4, 'value': {'ua': 'Чоловічі', 'ru': 'Чоловічі'}}]"
 * → "Чоловічі"
 */
function parseCharVal(v) {
  if (!v || typeof v !== 'string') return String(v ?? '');
  // Якщо не схоже на Python-список — повертаємо як є
  if (!v.startsWith('[') && !v.startsWith('{')) return v;
  try {
    const json = v
      .replace(/'/g, '"')
      .replace(/\bTrue\b/g, 'true')
      .replace(/\bFalse\b/g, 'false')
      .replace(/\bNone\b/g, 'null');
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed)) {
      return parsed.map(item => {
        const val = item?.value;
        if (val && typeof val === 'object') return val.ua || val.ru || Object.values(val)[0] || '';
        return typeof item === 'object' ? (item?.ua || item?.ru || JSON.stringify(item)) : String(item);
      }).filter(Boolean).join(', ');
    }
    if (parsed && typeof parsed === 'object') {
      return parsed.ua || parsed.ru || Object.values(parsed)[0] || v;
    }
    return String(parsed);
  } catch {
    return v;
  }
}

function fmtDate(iso) {
  return formatKyivDateTime(iso, 'uk-UA');
}

// ─── Error Boundary (diagnostic) ─────────────────────────────────────────────
class RetailErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 20, margin: 16, background: '#fee', border: '2px solid red', borderRadius: 8, color: '#900', fontFamily: 'monospace', fontSize: 13 }}>
          <strong>React error:</strong> {this.state.error.message}
          <pre style={{ fontSize: 11, marginTop: 8, whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto' }}>{this.state.error.stack}</pre>
          <button style={{ marginTop: 12, padding: '6px 14px', cursor: 'pointer' }} onClick={() => this.setState({ error: null })}>↻ Спробувати знову</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Вспомогательные компоненты
// ─────────────────────────────────────────────────────────────────────────────

function PlatformBadge({ platform }) {
  const map = {
    horoshop:    { label: 'Horoshop', cls: 'rt-badge-horoshop' },
    prom:        { label: 'Prom.ua',  cls: 'rt-badge-prom' },
    rozetka:     { label: 'Rozetka',  cls: 'rt-badge-rozetka' },
    woocommerce: { label: 'WordPress', cls: 'rt-badge-woocommerce' },
    opencart:    { label: 'OpenCart', cls: 'rt-badge-opencart' },
    shopify:     { label: 'Shopify', cls: 'rt-badge-shopify' },
  };
  const p = map[platform] || { label: platform, cls: 'rt-badge-other' };
  return <span className={`rt-badge ${p.cls}`}>{p.label}</span>;
}

// Підпис під полем форми "Новий товар" — які платформи реально використовують
// це поле при публікації (щоб не гадати, звідки береться build_product_payload).
function PF({ children }) {
  return <div className="rt-field-platforms">{children}</div>;
}

function platformColor(platform) {
  const map = {
    horoshop:    'var(--rt-horoshop)',
    prom:        'var(--rt-prom)',
    rozetka:     'var(--rt-rozetka)',
    woocommerce: 'var(--rt-woocommerce)',
    shopify:     'var(--rt-shopify)',
  };
  return map[platform] || 'var(--lg-text-3)';
}

// ─────────────────────────────────────────────────────────────────────────────
// Модалка добавления / редактирования сайта
// ─────────────────────────────────────────────────────────────────────────────

// Платформы, использующие 2 поля вместо одного API-токена
// (horoshop: логин+пароль; woocommerce: Consumer Key+Secret — те же 2 поля,
// просто с другими подписями)
const LOGIN_PASSWORD_PLATFORMS = new Set(['horoshop', 'woocommerce', 'opencart']);

function SiteModal({ site, platforms, onClose, onSaved }) {
  const isEdit = !!site;
  const [platform,   setPlatform]  = useState(site?.platform || (platforms[0]?.id || 'horoshop'));
  const [name,       setName]      = useState(site?.name     || '');
  const [domain,     setDomain]    = useState(site?.domain   || '');
  const [login,      setLogin]     = useState(site?.login    || '');
  const [password,   setPassword]  = useState('');
  const [tokenSet]                 = useState(!!site?.token_set);
  const [editSecret, setEditSecret] = useState(!site?.token_set);
  const [saving,     setSaving]    = useState(false);
  const [testing,    setTesting]   = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error,           setError]          = useState('');
  const [modTitleWithName, setModTitleWithName] = useState(
    site?.extra?.mod_title_with_name ?? true
  );
  const [trackStock, setTrackStock] = useState(
    site?.extra?.track_stock ?? false
  );
  const [horoshopWarehouse, setHoroshopWarehouse] = useState(
    site?.extra?.horoshop_warehouse || 'office'
  );
  const [sizeChar, setSizeChar] = useState(site?.extra?.size_char || '');
  const [sizeMapText, setSizeMapText] = useState(() => {
    const m = site?.extra?.size_map || {};
    return Object.entries(m).map(([s, id]) => `${s}:${id}`).join(', ');
  });
  const [showSubstitution, setShowSubstitution] = useState(
    site?.extra?.show_substitution ?? false
  );
  const [sizeFromArticle, setSizeFromArticle] = useState(
    site?.extra?.size_from_article ?? false
  );
  const [autoMapLoading, setAutoMapLoading] = useState(false);
  const [autoMapError,   setAutoMapError]   = useState('');
  const [autoMapInfo,    setAutoMapInfo]    = useState('');

  // Shopify: альтернативний спосіб отримати токен — через Client ID/Secret
  // (OAuth), для застосунків без прямого постійного токена (Partner Dashboard).
  const [shopifyClientId,     setShopifyClientId]     = useState(site?.extra?.shopify_client_id || '');
  const [shopifyClientSecret, setShopifyClientSecret] = useState('');
  const [oauthConnecting, setOauthConnecting] = useState(false);
  const [oauthError,      setOauthError]      = useState('');
  const [oauthInfo,       setOauthInfo]       = useState('');
  // Якщо сайту ще не було (isEdit=false) і "Підключити" вже створив його —
  // запам'ятовуємо id тут, інакше повторний клік створив би ДРУГИЙ сайт
  // (site-пропс лишається тим самим, яким був при відкритті модалки).
  const [createdSiteId, setCreatedSiteId] = useState(null);

  async function handleShopifyConnect() {
    if (!name.trim())   { setOauthError('Введіть назву сайту'); return; }
    if (!domain.trim()) { setOauthError('Введіть домен'); return; }
    setOauthConnecting(true); setOauthError(''); setOauthInfo('');
    try {
      let siteId = site?.id || createdSiteId;
      if (siteId) {
        // Сайт вже існує (редагування) — просто дозаписуємо Client ID/Secret,
        // не чекаючи загального "Зберегти".
        await retailAPI.updateSite(siteId, {
          extra: { shopify_client_id: shopifyClientId.trim() },
          token: shopifyClientSecret.trim(),
        });
      } else {
        // Нового сайту ще нема в БД — створюємо його прямо тут (Client
        // Secret іде як звичайний token, тимчасово, поки /oauth/callback
        // не замінить його на справжній access_token).
        const r = await retailAPI.createSite({
          platform: 'shopify', name: name.trim(), domain: domain.trim(), is_active: true,
          extra: { shopify_client_id: shopifyClientId.trim() },
          token: shopifyClientSecret.trim(),
        });
        siteId = r.data?.id;
        if (!siteId) throw new Error('Не вдалось створити сайт');
        setCreatedSiteId(siteId);
        onSaved(siteId);
      }
      const r2 = await retailAPI.shopifyOAuthStart(siteId);
      const url = r2.data?.authorize_url;
      if (!url) throw new Error('Не вдалось отримати посилання авторизації');
      window.open(url, '_blank', 'noopener');
      setOauthInfo('Відкрито нову вкладку — підтвердіть доступ у Shopify, потім закрийте ту вкладку і перезавантажте цю сторінку.');
    } catch (e) {
      setOauthError(e?.response?.data?.detail || e.message || 'Помилка підключення');
    } finally {
      setOauthConnecting(false);
    }
  }

  async function handleAutoSizeMap() {
    setAutoMapLoading(true); setAutoMapError(''); setAutoMapInfo('');
    try {
      // char_key — тільки підказка: якщо порожнє/неправильне, бекенд сам
      // перебирає відомі назви поля розміру й каже, яке реально спрацювало.
      const r = await retailAPI.autoSizeMap(site.id, sizeChar.trim());
      const map = r.data?.size_map || {};
      if (Object.keys(map).length === 0) {
        setAutoMapError('Не вдалось знайти розміри в жодному з відомих полів Horoshop — перевірте вручну через "Товари" сайту');
        return;
      }
      const newField = (r.data.field_changed && r.data.field) ? r.data.field : sizeChar.trim();
      setSizeMapText(Object.entries(map).map(([s, id]) => `${s}:${id}`).join(', '));
      setSizeChar(newField);
      // Зберігаємо одразу на сервер (не чекаємо, поки натиснуть загальний "Зберегти")
      // — інакше поле в формі виглядає оновленим, а насправді ще не збереглось.
      if (isEdit) {
        await retailAPI.updateSite(site.id, { extra: { size_char: newField, size_map: map } });
      }
      setAutoMapInfo(
        r.data.field_changed && r.data.field
          ? `Поле розміру насправді «${r.data.field}» — збережено разом з мапою розмірів`
          : 'Мапу розмірів збережено'
      );
    } catch (e) {
      setAutoMapError(e?.response?.data?.detail || 'Помилка запиту до Horoshop');
    } finally { setAutoMapLoading(false); }
  }

  const isLoginPass = LOGIN_PASSWORD_PLATFORMS.has(platform);

  async function handleSave() {
    if (!name.trim())   { setError('Введіть назву сайту'); return; }
    if (!domain.trim()) { setError('Введіть домен'); return; }
    if (isLoginPass && !login.trim()) { setError('Введіть логін'); return; }
    if (!tokenSet && !password.trim()) {
      setError(isLoginPass ? 'Введіть пароль' : 'Введіть API токен'); return;
    }
    setSaving(true); setError('');
    try {
      const size_map = {};
      sizeMapText.split(',').forEach(pair => {
        const [s, id] = pair.trim().split(':').map(x => x.trim());
        if (s && id && !isNaN(id)) size_map[s] = parseInt(id);
      });
      const extra = { mod_title_with_name: modTitleWithName, track_stock: trackStock, show_substitution: showSubstitution, size_from_article: sizeFromArticle };
      if (trackStock && horoshopWarehouse.trim()) extra.horoshop_warehouse = horoshopWarehouse.trim();
      if (sizeChar.trim()) extra.size_char = sizeChar.trim();
      if (Object.keys(size_map).length) extra.size_map = size_map;
      const body = { platform, name, domain, is_active: true, extra };
      if (isLoginPass) {
        body.login = login.trim();
        if (password.trim()) body.token = password.trim();
      } else {
        if (password.trim()) body.token = password.trim();
      }
      if (isEdit) {
        await retailAPI.updateSite(site.id, body);
        onSaved(null);
      } else {
        const r = await retailAPI.createSite(body);
        onSaved(r.data?.id ?? null);
      }
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    } finally { setSaving(false); }
  }

  async function handleTest() {
    if (!domain.trim())    { setError('Введіть домен'); return; }
    if (isLoginPass && !login.trim())    { setError('Введіть логін'); return; }
    if (!password.trim())  { setError(isLoginPass ? 'Введіть пароль' : 'Введіть токен'); return; }
    setTesting(true); setError(''); setTestResult(null);
    try {
      const payload = { platform, domain, token: password };
      if (isLoginPass) payload.login = login;
      const r = await retailAPI.testNewSite(payload);
      setTestResult({ ok: true, text: `Підключено. Категорій: ${r.data.categories_count ?? '?'}` });
    } catch (e) {
      setTestResult({ ok: false, text: e?.response?.data?.detail || String(e) });
    } finally { setTesting(false); }
  }

  // Сбросить пароль при смене платформы (чтобы не смешивать токены)
  function handlePlatformChange(id) {
    setPlatform(id);
    setPassword('');
    setLogin('');
    setTestResult(null);
  }

  return (
    <div className="rt-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="rt-modal">
        <div className="rt-modal-head">
          <span className="rt-modal-title">{isEdit ? 'Редагувати сайт' : 'Додати сайт'}</span>
          <button className="rt-icon-btn" onClick={onClose}>✕</button>
        </div>
        <div className="rt-modal-body">
          {!isEdit && (
            <div className="rt-field" style={{ marginBottom: 16 }}>
              <label className="rt-label">Платформа</label>
              <div className="rt-platform-grid">
                {platforms.map(p => (
                  <div key={p.id}
                    className={`rt-platform-opt ${platform === p.id ? 'selected' : ''}`}
                    onClick={() => handlePlatformChange(p.id)}
                  >
                    <PlatformBadge platform={p.id} />
                    <span className="rt-platform-opt-name">{p.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rt-form-grid2">
            <div className="rt-field rt-field-full">
              <label className="rt-label">Назва *</label>
              <input className="rt-input" placeholder="Мій магазин взуття" value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div className="rt-field rt-field-full">
              <label className="rt-label">Домен *</label>
              <input className="rt-input" placeholder={platform === 'shopify' ? 'myshop.myshopify.com' : 'myshop.horoshop.ua'} value={domain} onChange={e => setDomain(e.target.value)} />
            </div>

            {isLoginPass ? (
              <>
                <div className="rt-field rt-field-full">
                  <label className="rt-label">{platform === 'woocommerce' ? 'Consumer Key *' : platform === 'opencart' ? 'Username *' : 'Логін *'}</label>
                  <input className="rt-input" placeholder={platform === 'woocommerce' ? 'ck_...' : platform === 'opencart' ? 'API username' : 'email або логін'} value={login} onChange={e => setLogin(e.target.value)} autoComplete="username" />
                </div>
                <div className="rt-field rt-field-full">
                  <label className="rt-label">{platform === 'woocommerce' ? 'Consumer Secret *' : platform === 'opencart' ? 'Key *' : 'Пароль *'}</label>
                  {tokenSet && !editSecret ? (
                    <div className="rt-token-saved">
                      <span className="rt-token-dots">●●●●●●●●●●●●</span>
                      <span className="rt-token-ok">збережено</span>
                      <button className="rt-btn rt-btn-xs" onClick={() => { setEditSecret(true); setPassword(''); }}>Змінити</button>
                    </div>
                  ) : (
                    <input className="rt-input" type="password" placeholder={platform === 'woocommerce' ? 'cs_...' : 'Пароль від Horoshop'} value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" autoFocus={editSecret && tokenSet} />
                  )}
                </div>
              </>
            ) : (
              <div className="rt-field rt-field-full">
                <label className="rt-label">{platform === 'shopify' ? 'Admin API Access Token *' : 'API токен *'}</label>
                {tokenSet && !editSecret ? (
                  <div className="rt-token-saved">
                    <span className="rt-token-dots">●●●●●●●●●●●●</span>
                    <span className="rt-token-ok">збережено</span>
                    <button className="rt-btn rt-btn-xs" onClick={() => { setEditSecret(true); setPassword(''); }}>Змінити</button>
                  </div>
                ) : (
                  <input className="rt-input" type="password" placeholder={platform === 'shopify' ? 'shpat_...' : 'Введіть токен'} value={password} onChange={e => setPassword(e.target.value)} autoFocus={editSecret && tokenSet} />
                )}
              </div>
            )}
          </div>

          {platform === 'shopify' && (
            <div className="rt-field rt-field-full" style={{ marginTop: 10, padding: '10px 12px', background: 'var(--lg-hover, rgba(128,128,128,0.05))', borderRadius: 8, border: '1px solid var(--lg-border)' }}>
              <label className="rt-label" style={{ marginBottom: 4 }}>Немає постійного токена? Підключити через Client ID + Secret</label>
              <div style={{ fontSize: 12, color: 'var(--lg-text-3)', marginBottom: 10 }}>
                Для застосунків, створених через Partner Dashboard (там токен напряму не видають) —
                впишіть назву й домен вище, Client ID і Client Secret нижче, натисніть «Підключити»:
                {!isEdit && !createdSiteId && ' сайт збережеться автоматично, '}
                підтвердіть доступ у вкладці Shopify, що відкриється — токен після цього збережеться сам,
                так само як і звичайний.
              </div>
              <div className="rt-field rt-field-full" style={{ marginBottom: 8 }}>
                <label className="rt-label">Client ID</label>
                <input className="rt-input" placeholder="Client ID застосунку" value={shopifyClientId} onChange={e => setShopifyClientId(e.target.value)} />
              </div>
              <div className="rt-field rt-field-full" style={{ marginBottom: 8 }}>
                <label className="rt-label">Client Secret</label>
                <input className="rt-input" type="password" placeholder="Client Secret застосунку" value={shopifyClientSecret} onChange={e => setShopifyClientSecret(e.target.value)} />
              </div>
              <button className="rt-btn rt-btn-sm" type="button" disabled={oauthConnecting || !shopifyClientId.trim() || !shopifyClientSecret.trim()} onClick={handleShopifyConnect}>
                {oauthConnecting ? 'Підключення…' : 'Підключити'}
              </button>
              {oauthError && <div style={{ color: 'var(--lg-danger, #ef4444)', fontSize: 12, marginTop: 8 }}>{oauthError}</div>}
              {oauthInfo && <div style={{ color: 'var(--lg-text-2)', fontSize: 12, marginTop: 8 }}>{oauthInfo}</div>}
            </div>
          )}

          <div className="rt-field rt-field-full" style={{ marginTop: 14, padding: '10px 12px', background: 'var(--lg-hover, rgba(128,128,128,0.05))', borderRadius: 8, border: '1px solid var(--lg-border)', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label className="rt-label" style={{ marginBottom: 0 }}>Налаштування</label>

            <div className="rt-toggle-wrap" onClick={() => setModTitleWithName(v => !v)}>
              <div className={`rt-toggle ${modTitleWithName ? 'rt-toggle--on' : ''}`}><div className="rt-toggle-knob" /></div>
              <div>
                <div style={{ fontSize: 13, color: 'var(--lg-text)' }}>
                  {modTitleWithName ? 'Назва товару + розмір' : 'Тільки назва товару'}
                </div>
                <div className="rt-hint">
                  {modTitleWithName ? 'Наприклад: «Крос-36», «Крос-37»' : 'Наприклад: «Крос», «Крос»'}
                </div>
              </div>
            </div>

            <div className="rt-toggle-wrap" onClick={() => setTrackStock(v => !v)}>
              <div className={`rt-toggle ${trackStock ? 'rt-toggle--on' : ''}`}><div className="rt-toggle-knob" /></div>
              <div>
                <div style={{ fontSize: 13, color: 'var(--lg-text)' }}>
                  {trackStock ? 'З урахуванням залишків' : 'Без урахування залишків'}
                </div>
                <div className="rt-hint">
                  {trackStock ? 'Передаємо кількість по кожній позиції' : 'Передаємо тільки наявність і відображення'}
                </div>
              </div>
            </div>

            {trackStock && (
              <div className="rt-field" style={{ marginLeft: 32, marginBottom: 10 }}>
                <label className="rt-label">Назва складу в Horoshop</label>
                <input className="rt-input" style={{ width: 160 }}
                  value={horoshopWarehouse}
                  onChange={e => setHoroshopWarehouse(e.target.value)}
                  placeholder="office" />
                <div className="rt-hint">Як склад називається в кабінеті Horoshop → Склади</div>
              </div>
            )}

            {platform === 'prom' && (
              <div className="rt-toggle-wrap" onClick={() => setShowSubstitution(v => !v)}>
                <div className={`rt-toggle ${showSubstitution ? 'rt-toggle--on' : ''}`}><div className="rt-toggle-knob" /></div>
                <div>
                  <div style={{ fontSize: 13, color: 'var(--lg-text)' }}>
                    {showSubstitution ? 'Вкладка «Під заміну» видима користувачам' : 'Вкладка «Під заміну» тільки для адміна'}
                  </div>
                  <div className="rt-hint">Увімкніть коли функція готова до використання</div>
                </div>
              </div>
            )}

            {platform === 'horoshop' && (
              <div className="rt-toggle-wrap" onClick={() => setSizeFromArticle(v => !v)}>
                <div className={`rt-toggle ${sizeFromArticle ? 'rt-toggle--on' : ''}`}><div className="rt-toggle-knob" /></div>
                <div>
                  <div style={{ fontSize: 13, color: 'var(--lg-text)' }}>
                    {sizeFromArticle ? 'Розмір з артикулу (пріоритет)' : 'Розмір з поля API (rozmr тощо)'}
                  </div>
                  <div className="rt-hint">Увімкніть якщо розмір закодований у суфіксі артикулу: Q002-Чорний-<strong>38</strong></div>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label className="rt-label" style={{ marginBottom: 0 }}>Характеристика розміру</label>
              <input
                className="rt-input rt-input-sm"
                placeholder="rozmr"
                value={sizeChar}
                onChange={e => setSizeChar(e.target.value)}
              />
              <div className="rt-hint">Назва поля в API платформи (напр. rozmr)</div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label className="rt-label" style={{ marginBottom: 0 }}>Маппінг розмірів → ID</label>
                {platform === 'horoshop' && isEdit && (
                  <button type="button" className="rt-btn rt-btn-xs" disabled={autoMapLoading}
                    onClick={handleAutoSizeMap}>
                    {autoMapLoading ? 'Завантаження…' : '🔄 Автозаповнити з Horoshop'}
                  </button>
                )}
              </div>
              <textarea
                className="rt-input rt-textarea"
                rows={3}
                placeholder="35:3, 36:1, 37:2, 38:4, 39:5"
                value={sizeMapText}
                onChange={e => setSizeMapText(e.target.value)}
              />
              <div className="rt-hint">Формат: розмір:id через кому. Напр: 36:1, 37:2, 38:4</div>
              {autoMapError && <div className="rt-hint" style={{ color: '#f59e0b' }}>{autoMapError}</div>}
              {autoMapInfo && <div className="rt-hint" style={{ color: '#22c55e' }}>{autoMapInfo}</div>}
            </div>
          </div>

          {error && <div className="rt-error">{error}</div>}
          {testResult && (
            <div className={`rt-test-result ${testResult.ok ? 'rt-ok' : 'rt-err'}`}>
              {testResult.ok ? '✓' : '✗'} {testResult.text}
            </div>
          )}

          <div className="rt-modal-actions">
            <button className="rt-btn" onClick={handleTest} disabled={testing || (!password.trim() && !tokenSet)}>
              {testing ? 'Перевірка…' : '⚡ Перевірити'}
            </button>
            <div style={{ flex: 1 }} />
            <button className="rt-btn" onClick={onClose}>Скасувати</button>
            <button className="rt-btn rt-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Збереження…' : 'Зберегти'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Карточка сайта
// ─────────────────────────────────────────────────────────────────────────────

function SiteCard({ site, onEdit, onDelete, onTest, onOpen, isAdmin }) {
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState(null);

  async function handleTest() {
    setTesting(true); setTestMsg(null);
    try {
      const r = await onTest(site.id);
      setTestMsg({ ok: true, text: `Підключено (${r.categories_count ?? '?'} кат.)` });
    } catch (e) {
      setTestMsg({ ok: false, text: e?.response?.data?.detail || 'Ошибка' });
    } finally { setTesting(false); }
  }

  return (
    <div
      className={`rt-site-card rt-site-card--clickable ${site.is_active ? '' : 'rt-site-card--inactive'}`}
      onClick={() => onOpen(site)}
    >
      <div className="rt-site-card-top">
        <PlatformBadge platform={site.platform} />
        {isAdmin && <span className={`rt-status-dot ${site.is_active ? 'rt-status-dot--on' : ''}`} />}
      </div>
      <div className="rt-site-name">{site.name}</div>
      <div className="rt-site-domain">
        {site.domain
          ? <a href={site.domain.match(/^https?:\/\//) ? site.domain : `https://${site.domain}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}
               onMouseEnter={e => e.currentTarget.style.textDecoration='underline'}
               onMouseLeave={e => e.currentTarget.style.textDecoration='none'}
            >{site.domain}</a>
          : '—'}
      </div>
      {isAdmin && site.login && site.platform !== 'woocommerce' && <div className="rt-site-login">{site.login}</div>}
      {isAdmin && testMsg && (
        <div className={`rt-test-inline ${testMsg.ok ? 'rt-ok' : 'rt-err'}`}>
          {testMsg.ok ? '✓' : '✗'} {testMsg.text}
        </div>
      )}
      {isAdmin && (
        <div className="rt-site-actions" onClick={e => e.stopPropagation()}>
          <button className="rt-btn rt-btn-sm" onClick={() => onEdit(site)}>✎ Редагувати</button>
          <button className="rt-btn rt-btn-sm" onClick={handleTest} disabled={testing || !site.token_set}>
            {testing ? '…' : '⚡'}
          </button>
          <button className="rt-btn rt-btn-sm rt-btn-danger" onClick={() => onDelete(site)}>✕</button>
        </div>
      )}
      <div className="rt-site-open-hint">Натисніть для управління →</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Вкладка "Сайты"
// ─────────────────────────────────────────────────────────────────────────────

function SiteGroupsModal({ sites, groups, onClose, onChanged }) {
  const [list,      setList]      = useState(groups);
  const [editId,    setEditId]    = useState(null);   // null=новая
  const [editName,  setEditName]  = useState('');
  const [editSites, setEditSites] = useState([]);     // вибрані site_ids
  const [saving,    setSaving]    = useState(false);
  const [delId,     setDelId]     = useState(null);
  const [deleting,  setDeleting]  = useState(false);
  const [error,     setError]     = useState('');

  function openNew() {
    setEditId(null);
    setEditName('');
    setEditSites([]);
    setError('');
  }
  function openEdit(g) {
    setEditId(g.id);
    setEditName(g.name);
    setEditSites([...(g.site_ids || [])]);
    setError('');
  }
  function toggleSite(id) {
    setEditSites(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }

  async function saveGroup() {
    if (!editName.trim()) { setError("Вкажіть назву групи"); return; }
    setSaving(true); setError('');
    try {
      let res;
      if (editId === null) {
        res = await retailAPI.createSiteGroup({ name: editName.trim(), site_ids: editSites });
        setList(prev => [...prev, res.data]);
      } else {
        res = await retailAPI.updateSiteGroup(editId, { name: editName.trim(), site_ids: editSites });
        setList(prev => prev.map(g => g.id === editId ? res.data : g));
      }
      onChanged(editId === null ? res.data : null);  // null = оновлення, object = нова група
      setEditId(-1);  // сигнал "форма закрита"
    } catch { setError('Помилка збереження'); }
    finally { setSaving(false); }
  }

  async function deleteGroup() {
    setDeleting(true);
    try {
      await retailAPI.deleteSiteGroup(delId);
      setList(prev => prev.filter(g => g.id !== delId));
      onChanged(null);
      setDelId(null);
    } catch { setError('Помилка видалення'); }
    finally { setDeleting(false); }
  }

  const formOpen = editId !== -1 && editId !== undefined;

  return (
    <div className="rt-overlay" onClick={onClose}>
      <div className="rt-modal" style={{ maxWidth: 540, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
           onClick={e => e.stopPropagation()}>
        <div className="rt-modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Групи сайтів</h3>
          <button className="rt-btn rt-btn-sm" onClick={onClose}>✕</button>
        </div>

        {error && <div className="rt-error" style={{ marginBottom: 8 }}>{error}</div>}

        {/* Список груп */}
        <div style={{ flex: 1, overflowY: 'auto', marginBottom: 12 }}>
          {list.length === 0 && !formOpen && (
            <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '20px 0' }}>
              Груп ще немає. Натисніть «+ Нова група» щоб створити.
            </div>
          )}
          {list.map(g => (
            <div key={g.id} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 0', borderBottom: '1px solid var(--border)'
            }}>
              <span style={{ flex: 1, fontWeight: 500 }}>{g.name}</span>
              <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{(g.site_ids || []).length} сайт(ів)</span>
              <button className="rt-btn rt-btn-sm" onClick={() => openEdit(g)}>✏️</button>
              <button className="rt-btn rt-btn-sm" onClick={() => setDelId(g.id)}>🗑</button>
            </div>
          ))}
        </div>

        {/* Форма створення/редагування */}
        {formOpen && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              {editId === null ? 'Нова група' : 'Редагування групи'}
            </div>
            <input
              className="rt-input"
              placeholder="Назва групи…"
              value={editName}
              onChange={e => setEditName(e.target.value)}
              style={{ width: '100%', marginBottom: 10 }}
              autoFocus
            />
            <div style={{ marginBottom: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
              Сайти в групі:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12, maxHeight: 180, overflowY: 'auto' }}>
              {(() => {
                // Сайти що зайняті в ІНШИХ групах (не в поточній)
                const takenIds = new Set(
                  list
                    .filter(g => g.id !== editId)
                    .flatMap(g => g.site_ids || [])
                );
                const available = sites.filter(s => !takenIds.has(s.id));
                if (!available.length) return (
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Всі сайти вже розподілені по групах</span>
                );
                return available.map(s => {
                  const on = editSites.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      className={`rt-btn rt-btn-sm ${on ? 'rt-btn-primary' : ''}`}
                      onClick={() => toggleSite(s.id)}
                    >
                      {on ? '✓ ' : ''}{s.name || s.domain}
                    </button>
                  );
                });
              })()}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="rt-btn rt-btn-primary" onClick={saveGroup} disabled={saving}>
                {saving ? 'Збереження…' : 'Зберегти'}
              </button>
              <button className="rt-btn" onClick={() => setEditId(-1)}>Скасувати</button>
            </div>
          </div>
        )}

        <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
          <button className="rt-btn" onClick={openNew}>+ Нова група</button>
        </div>

        {/* Підтвердження видалення */}
        {delId !== null && (
          <div className="rt-overlay" onClick={() => setDelId(null)}>
            <div className="rt-modal rt-modal--sm" onClick={e => e.stopPropagation()}>
              <div className="rt-modal-body" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>⚠️</div>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Видалити групу?</div>
                <div className="rt-hint" style={{ marginBottom: 16 }}>Сайти нікуди не зникнуть — тільки група буде видалена.</div>
                <div className="rt-modal-actions" style={{ justifyContent: 'center' }}>
                  <button className="rt-btn" onClick={() => setDelId(null)}>Скасувати</button>
                  <button className="rt-btn rt-btn-danger-fill" onClick={deleteGroup} disabled={deleting}>
                    {deleting ? 'Видалення…' : 'Видалити'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Масові налаштування оновлень для групи ───────────────────────────────────
function BulkSyncConfigModal({ group, onClose }) {
  const siteCount = (group.site_ids || []).length;

  // Прапорець "застосувати" + значення для кожного поля
  const [fields, setFields] = useState({
    auto_stock_enabled:     { apply: false, value: true },
    stock_interval_minutes: { apply: false, value: 30 },
    price_update_enabled:   { apply: false, value: false },
    price_only_increase:    { apply: false, value: false },
    auto_enabled:           { apply: false, value: true },
    interval_minutes:       { apply: false, value: 60 },
  });

  const [saving,  setSaving]  = useState(false);
  const [result,  setResult]  = useState(null); // { updated: N }
  const [error,   setError]   = useState('');

  function toggle(key) {
    setFields(f => ({ ...f, [key]: { ...f[key], apply: !f[key].apply } }));
  }
  function setValue(key, val) {
    setFields(f => ({ ...f, [key]: { ...f[key], value: val } }));
  }

  async function handleSave() {
    const body = {};
    Object.entries(fields).forEach(([key, { apply, value }]) => {
      if (apply) body[key] = value;
    });
    if (Object.keys(body).length === 0) { setError('Оберіть хоча б один параметр'); return; }
    setSaving(true); setError(''); setResult(null);
    try {
      const r = await retailAPI.bulkGroupSyncConfig(group.id, body);
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Помилка');
    } finally { setSaving(false); }
  }

  const ROW_STYLE = { display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0',
                      borderBottom: '1px solid var(--border-color,#e5e7eb)' };
  const LABEL_STYLE = { flex: 1, fontSize: 13, color: 'var(--text)' };

  return (
    <div className="rt-modal-overlay" onClick={onClose}>
      <div className="rt-modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="rt-modal-header">
          <span>⚙️ Масові налаштування — {group.name}</span>
          <button className="rt-modal-close" onClick={onClose}>×</button>
        </div>
        <div style={{ padding: '12px 16px 4px', fontSize: 12, color: 'var(--muted,#888)' }}>
          Застосується до всіх <b>{siteCount}</b> сайтів групи. Відзначте тільки ті параметри, які хочете змінити.
        </div>

        <div style={{ padding: '4px 16px 16px' }}>
          {/* Авто-оновлення наявності */}
          <div style={ROW_STYLE}>
            <input type="checkbox" checked={fields.auto_stock_enabled.apply} onChange={() => toggle('auto_stock_enabled')} style={{ width:15, height:15, cursor:'pointer', flexShrink:0 }} />
            <span style={LABEL_STYLE}>Авто-оновлення наявності</span>
            <div className={`rt-toggle ${fields.auto_stock_enabled.value ? 'rt-toggle--on' : ''}`}
                 style={{ opacity: fields.auto_stock_enabled.apply ? 1 : 0.35, cursor: fields.auto_stock_enabled.apply ? 'pointer' : 'default' }}
                 onClick={() => fields.auto_stock_enabled.apply && setValue('auto_stock_enabled', !fields.auto_stock_enabled.value)}>
              <div className="rt-toggle-knob" />
            </div>
            <span style={{ fontSize: 12, color: 'var(--muted,#888)', minWidth: 42 }}>
              {fields.auto_stock_enabled.value ? 'Увімк.' : 'Вимк.'}
            </span>
          </div>

          {/* Інтервал наявності */}
          <div style={ROW_STYLE}>
            <input type="checkbox" checked={fields.stock_interval_minutes.apply} onChange={() => toggle('stock_interval_minutes')} style={{ width:15, height:15, cursor:'pointer', flexShrink:0 }} />
            <span style={LABEL_STYLE}>Інтервал наявності (хв)</span>
            <select className="rt-input rt-select" style={{ width: 90, opacity: fields.stock_interval_minutes.apply ? 1 : 0.35 }}
                    disabled={!fields.stock_interval_minutes.apply}
                    value={fields.stock_interval_minutes.value}
                    onChange={e => setValue('stock_interval_minutes', parseInt(e.target.value))}>
              {[10,15,20,30,45,60,90,120,180,240].map(v => <option key={v} value={v}>{v} хв</option>)}
            </select>
          </div>

          {/* Оновлення ціни */}
          <div style={ROW_STYLE}>
            <input type="checkbox" checked={fields.price_update_enabled.apply} onChange={() => toggle('price_update_enabled')} style={{ width:15, height:15, cursor:'pointer', flexShrink:0 }} />
            <span style={LABEL_STYLE}>Оновлення ціни</span>
            <div className={`rt-toggle ${fields.price_update_enabled.value ? 'rt-toggle--on' : ''}`}
                 style={{ opacity: fields.price_update_enabled.apply ? 1 : 0.35, cursor: fields.price_update_enabled.apply ? 'pointer' : 'default' }}
                 onClick={() => fields.price_update_enabled.apply && setValue('price_update_enabled', !fields.price_update_enabled.value)}>
              <div className="rt-toggle-knob" />
            </div>
            <span style={{ fontSize: 12, color: 'var(--muted,#888)', minWidth: 42 }}>
              {fields.price_update_enabled.value ? 'Увімк.' : 'Вимк.'}
            </span>
          </div>

          {/* Тільки підвищення ціни */}
          <div style={ROW_STYLE}>
            <input type="checkbox" checked={fields.price_only_increase.apply} onChange={() => toggle('price_only_increase')} style={{ width:15, height:15, cursor:'pointer', flexShrink:0 }} />
            <span style={LABEL_STYLE}>Тільки підвищення ціни</span>
            <div className={`rt-toggle ${fields.price_only_increase.value ? 'rt-toggle--on' : ''}`}
                 style={{ opacity: fields.price_only_increase.apply ? 1 : 0.35, cursor: fields.price_only_increase.apply ? 'pointer' : 'default' }}
                 onClick={() => fields.price_only_increase.apply && setValue('price_only_increase', !fields.price_only_increase.value)}>
              <div className="rt-toggle-knob" />
            </div>
            <span style={{ fontSize: 12, color: 'var(--muted,#888)', minWidth: 42 }}>
              {fields.price_only_increase.value ? 'Увімк.' : 'Вимк.'}
            </span>
          </div>

          {/* Авто-синхронізація товарів */}
          <div style={ROW_STYLE}>
            <input type="checkbox" checked={fields.auto_enabled.apply} onChange={() => toggle('auto_enabled')} style={{ width:15, height:15, cursor:'pointer', flexShrink:0 }} />
            <span style={LABEL_STYLE}>Авто-синхронізація товарів</span>
            <div className={`rt-toggle ${fields.auto_enabled.value ? 'rt-toggle--on' : ''}`}
                 style={{ opacity: fields.auto_enabled.apply ? 1 : 0.35, cursor: fields.auto_enabled.apply ? 'pointer' : 'default' }}
                 onClick={() => fields.auto_enabled.apply && setValue('auto_enabled', !fields.auto_enabled.value)}>
              <div className="rt-toggle-knob" />
            </div>
            <span style={{ fontSize: 12, color: 'var(--muted,#888)', minWidth: 42 }}>
              {fields.auto_enabled.value ? 'Увімк.' : 'Вимк.'}
            </span>
          </div>

          {/* Інтервал синхронізації */}
          <div style={{ ...ROW_STYLE, borderBottom: 'none' }}>
            <input type="checkbox" checked={fields.interval_minutes.apply} onChange={() => toggle('interval_minutes')} style={{ width:15, height:15, cursor:'pointer', flexShrink:0 }} />
            <span style={LABEL_STYLE}>Інтервал синхронізації товарів (хв)</span>
            <select className="rt-input rt-select" style={{ width: 90, opacity: fields.interval_minutes.apply ? 1 : 0.35 }}
                    disabled={!fields.interval_minutes.apply}
                    value={fields.interval_minutes.value}
                    onChange={e => setValue('interval_minutes', parseInt(e.target.value))}>
              {[15,30,60,120,180,240,360,720,1440].map(v => <option key={v} value={v}>{v} хв</option>)}
            </select>
          </div>

          {error && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 10 }}>{error}</div>}
          {result && (
            <div style={{ color: '#22c55e', fontSize: 13, marginTop: 10 }}>
              ✅ Оновлено {result.updated} сайт(ів)
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="rt-btn rt-btn-secondary" onClick={onClose}>Закрити</button>
            <button className="rt-btn rt-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Зберігаємо...' : `Застосувати до ${siteCount} сайтів`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SitesTab({ sites, platforms, onRefresh, onSelectSite }) {
  const [modal,    setModal]   = useState(null);
  const [delSite,  setDelSite] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const _u = useAuthStore((state) => state.user);
  const isAdmin       = !!(_u?.is_admin && !_u?.impersonated_by);
  const canCreateSite = isAdmin || !!(_u?.permissions?.includes('retail_site_admin'));

  // ── Групи сайтів ────────────────────────────────────────────────────────────
  const [groups,         setGroups]         = useState([]);
  const [activeGroupId,  setActiveGroupId]  = useState(() => {
    const v = localStorage.getItem('retail_active_group_id');
    return v ? parseInt(v, 10) : null;
  });
  const [groupsModal,    setGroupsModal]    = useState(false);
  const [bulkConfigModal, setBulkConfigModal] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    retailAPI.getSiteGroups().then(r => setGroups(r.data)).catch(() => {});
  }, [isAdmin]);

  function selectGroup(id) {
    setActiveGroupId(id);
    if (id === null) localStorage.removeItem('retail_active_group_id');
    else             localStorage.setItem('retail_active_group_id', String(id));
  }

  function handleGroupChanged() {
    // Перезавантажуємо список груп
    retailAPI.getSiteGroups().then(r => {
      setGroups(r.data);
      // якщо активна група видалена — скидаємо на "Всі"
      const ids = r.data.map(g => g.id);
      if (activeGroupId !== null && !ids.includes(activeGroupId)) selectGroup(null);
    }).catch(() => {});
  }

  const activeGroup   = groups.find(g => g.id === activeGroupId) || null;
  const filteredSites = activeGroup
    ? sites.filter(s => (activeGroup.site_ids || []).includes(s.id))
    : sites;

  async function handleTest(siteId) {
    const r = await retailAPI.testSite(siteId);
    return r.data;
  }

  async function confirmDelete() {
    setDeleting(true);
    try {
      await retailAPI.deleteSite(delSite.id);
      setDelSite(null);
      onRefresh();
    } finally { setDeleting(false); }
  }

  return (
    <div>
      <div className="rt-section-head">
        <div>
          <div className="rt-section-title">Настроенные сайты</div>
          <div className="rt-hint">Управление подключениями к платформам</div>
        </div>
        {canCreateSite && (
          <button className="rt-btn rt-btn-primary" onClick={() => setModal({ mode: 'add' })}>+ Добавить сайт</button>
        )}
      </div>

      {/* ── Панель груп (тільки адмін) ── */}
      {isAdmin && (groups.length > 0 || true) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14, alignItems: 'center' }}>
          <button
            className={`rt-btn rt-btn-sm ${activeGroupId === null ? 'rt-btn-primary' : ''}`}
            onClick={() => selectGroup(null)}
          >
            Всі сайти
          </button>
          {groups.map(g => (
            <button
              key={g.id}
              className={`rt-btn rt-btn-sm ${activeGroupId === g.id ? 'rt-btn-primary' : ''}`}
              onClick={() => selectGroup(g.id)}
            >
              {g.name}
              <span style={{ marginLeft: 5, opacity: 0.6, fontSize: 11 }}>
                {(g.site_ids || []).length}
              </span>
            </button>
          ))}
          {activeGroupId !== null && activeGroup && (
            <button
              className="rt-btn rt-btn-sm"
              style={{ marginLeft: 4 }}
              onClick={() => setBulkConfigModal(true)}
              title={`Масові налаштування оновлень для групи «${activeGroup.name}»`}
            >
              📋 Оновлення
            </button>
          )}
          <button
            className="rt-btn rt-btn-sm"
            style={{ marginLeft: 4, opacity: 0.7 }}
            onClick={() => setGroupsModal(true)}
            title="Керувати групами"
          >
            ⚙️ Групи
          </button>
        </div>
      )}

      {(() => {
        const renderCard = s => (
          <SiteCard key={s.id} site={s}
            isAdmin={isAdmin}
            onEdit={s => setModal({ mode: 'edit', site: s })}
            onDelete={setDelSite}
            onTest={handleTest}
            onOpen={onSelectSite}
          />
        );
        const addCard = canCreateSite && (
          <div className="rt-add-card" onClick={() => setModal({ mode: 'add' })}>
            <span className="rt-add-icon">＋</span>
            <span className="rt-add-label">Добавить сайт</span>
            <span className="rt-hint" style={{ fontSize: 11, textAlign: 'center' }}>Horoshop, Prom.ua и другие</span>
          </div>
        );
        const emptyGroupMsg = filteredSites.length === 0 && activeGroup && (
          <div style={{ color: 'var(--text-secondary)', padding: '20px 0' }}>
            Група «{activeGroup.name}» порожня. Додайте сайти через ⚙️ Групи.
          </div>
        );

        // Розбивка на «з автосинхронізацією» / «без» — тільки для адміна
        if (!isAdmin) {
          return (
            <>
              <div className="rt-sites-grid">
                {filteredSites.map(renderCard)}
                {addCard}
              </div>
              {emptyGroupMsg}
            </>
          );
        }

        // 3 групи замість 2: повністю автоматизовані / без синку каталогу
        // взагалі / синк каталогу є, а авто-оновлення наявності — нема.
        const fullAutoSites  = filteredSites.filter(s => s.auto_enabled && s.auto_stock_enabled);
        const noSyncSites    = filteredSites.filter(s => !s.auto_enabled);
        const noStockSites   = filteredSites.filter(s => s.auto_enabled && !s.auto_stock_enabled);
        return (
          <>
            <div className="rt-label" style={{ marginBottom: 8 }}>
              🔄 З автосинхронізацією та Авто-оновлення наявності{fullAutoSites.length > 0 ? ` (${fullAutoSites.length})` : ''}
            </div>
            {fullAutoSites.length > 0 ? (
              <div className="rt-sites-grid">{fullAutoSites.map(renderCard)}</div>
            ) : (
              <div className="rt-hint" style={{ marginBottom: 16 }}>Немає повністю автоматизованих сайтів</div>
            )}

            <div className="rt-label" style={{ marginBottom: 8, marginTop: 20 }}>
              ⏸ Без автосинхронізації{noSyncSites.length > 0 ? ` (${noSyncSites.length})` : ''}
            </div>
            {noSyncSites.length > 0 ? (
              <div className="rt-sites-grid">{noSyncSites.map(renderCard)}</div>
            ) : (
              <div className="rt-hint" style={{ marginBottom: 16 }}>Немає сайтів без автосинхронізації</div>
            )}

            <div className="rt-label" style={{ marginBottom: 8, marginTop: 20 }}>
              ⏸ Без Авто-оновлення{noStockSites.length > 0 ? ` (${noStockSites.length})` : ''}
            </div>
            <div className="rt-sites-grid">
              {noStockSites.map(renderCard)}
              {addCard}
            </div>

            {emptyGroupMsg}
          </>
        );
      })()}

      {modal && (
        <SiteModal
          site={modal.site || null}
          platforms={platforms}
          onClose={() => setModal(null)}
          onSaved={async (newSiteId) => {
            // Якщо створено новий сайт і є активна група — додаємо його туди
            if (newSiteId && activeGroupId) {
              const grp = groups.find(g => g.id === activeGroupId);
              if (grp) {
                const newIds = [...(grp.site_ids || []), newSiteId];
                try {
                  const res = await retailAPI.updateSiteGroup(activeGroupId, { site_ids: newIds });
                  setGroups(prev => prev.map(g => g.id === activeGroupId ? res.data : g));
                } catch {}
              }
            }
            onRefresh();
          }}
        />
      )}

      {delSite && (
        <div className="rt-overlay" onClick={() => setDelSite(null)}>
          <div className="rt-modal rt-modal--sm" onClick={e => e.stopPropagation()}>
            <div className="rt-modal-body" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>⚠️</div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Удалить сайт?</div>
              <div className="rt-hint" style={{ marginBottom: 20 }}>«{delSite.name}» будет удалён безвозвратно.</div>
              <div className="rt-modal-actions" style={{ justifyContent: 'center' }}>
                <button className="rt-btn" onClick={() => setDelSite(null)}>Отмена</button>
                <button className="rt-btn rt-btn-danger-fill" onClick={confirmDelete} disabled={deleting}>
                  {deleting ? 'Удаление…' : 'Удалить'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {groupsModal && (
        <SiteGroupsModal
          sites={sites}
          groups={groups}
          onClose={() => setGroupsModal(false)}
          onChanged={handleGroupChanged}
        />
      )}
      {bulkConfigModal && activeGroup && (
        <BulkSyncConfigModal
          group={activeGroup}
          onClose={() => setBulkConfigModal(false)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Панель публикации (правая колонка)
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────

function PhotoUploader({ productTitle, photos, onChange }) {
  const [urlInput,     setUrlInput]     = useState('');
  const [dropActive,   setDropActive]   = useState(false);
  const [dragOverIdx,  setDragOverIdx]  = useState(null);
  const [uploadError,  setUploadError]  = useState('');
  const fileRef  = useRef(null);
  const dragIdx  = useRef(null);  // ref — не тригерить ре-рендер, завжди актуальний

  function makeName(index, title) {
    const base = (title ?? productTitle).trim() || 'Фото';
    return `${base}-${index + 1}`;
  }

  function renamed(arr, title) {
    return arr.map((p, i) => ({ ...p, name: makeName(i, title) }));
  }

  // Capture-фаза на document: спрацьовує ДО того як браузер вирішить відкрити файл.
  // Bubble-фаза (window) запізнюється — браузер встигає почати навігацію.
  useEffect(() => {
    const block = e => e.preventDefault();
    document.addEventListener('dragover', block, true);
    document.addEventListener('drop',     block, true);
    return () => {
      document.removeEventListener('dragover', block, true);
      document.removeEventListener('drop',     block, true);
    };
  }, []);

  useEffect(() => {
    if (photos.length === 0) return;
    onChange(prev => prev.map((p, i) => ({ ...p, name: makeName(i, productTitle) })));
  }, [productTitle]); // eslint-disable-line

  async function uploadFiles(files) {
    setUploadError('');
    for (const file of files) {
      const placeholder = { url: '', name: makeName(photos.length), loading: true };
      onChange(prev => renamed([...prev, placeholder]));
      try {
        const fd = new FormData();
        fd.append('file', file);
        const r = await retailAPI.uploadPhoto(fd);
        onChange(prev => renamed(prev.map(p => p.loading && p.url === '' ? { url: r.data.url, name: p.name, loading: false } : p)));
      } catch (e) {
        onChange(prev => renamed(prev.filter(p => !(p.loading && p.url === ''))));
        const detail = e?.response?.data?.detail;
        const detailStr = !detail
          ? null
          : typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
              ? detail.map(d => d.msg || JSON.stringify(d)).join('; ')
              : JSON.stringify(detail);
        const msg = detailStr
          || (e?.response?.status === 413 ? 'Файл занадто великий (макс. 50 МБ)' : `Помилка завантаження: ${e?.message || e}`);
        setUploadError(String(msg));
      }
    }
  }

  function handleDropZone(e) {
    e.preventDefault();
    setDropActive(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length) uploadFiles(files);
  }

  function handleFileInput(e) {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
    if (files.length) uploadFiles(files);
    e.target.value = '';
  }

  function addUrl() {
    const url = urlInput.trim();
    if (!url) return;
    onChange(prev => renamed([...prev, { url, name: makeName(prev.length) }]));
    setUrlInput('');
  }

  function removePhoto(idx) {
    onChange(prev => renamed(prev.filter((_, i) => i !== idx)));
  }

  // Drag-to-reorder
  function onCardDragStart(e, idx) {
    dragIdx.current = idx;
    e.dataTransfer.effectAllowed = 'move';
  }
  function onCardDragOver(e, idx) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  }
  function onCardDrop(e, idx) {
    e.preventDefault();
    e.stopPropagation();
    const from = dragIdx.current;
    dragIdx.current = null;
    setDragOverIdx(null);
    if (from === null || from === idx) return;
    onChange(prev => {
      const arr = [...prev];
      if (from < 0 || from >= arr.length) return prev;  // захист від неvalid індексу
      const [item] = arr.splice(from, 1);
      arr.splice(idx, 0, item);
      return renamed(arr);
    });
  }
  function onCardDragEnd() { dragIdx.current = null; setDragOverIdx(null); }

  return (
    <div className="rt-photo-uploader">
      <div
        className={`rt-photo-dropzone${dropActive ? ' rt-photo-dropzone--over' : ''}`}
        onDragOver={e => { e.preventDefault(); if (dragIdx.current === null) setDropActive(true); }}
        onDragLeave={() => setDropActive(false)}
        onDrop={handleDropZone}
        onClick={() => fileRef.current?.click()}
      >
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={handleFileInput} />
        ⬆ Перетягніть фото або натисніть для вибору
      </div>

      <div className="rt-photo-url-row">
        <input
          className="rt-input"
          placeholder="https://example.com/photo.jpg"
          value={urlInput}
          onChange={e => setUrlInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addUrl()}
        />
        <button className="rt-btn rt-btn-sm" onClick={addUrl}>Додати URL</button>
      </div>

      {uploadError && (
        <div className="rt-error" style={{ marginTop: 6 }}>{uploadError}</div>
      )}

      {photos.length > 0 && (
        <div
          className="rt-photo-grid"
          onDragOver={e => e.preventDefault()}
          onDrop={e => {
            e.preventDefault();
            // файл з ОС впав між карточками — передаємо в дропзону
            if (dragIdx.current === null) {
              const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
              if (files.length) uploadFiles(files);
            }
          }}
        >
          {photos.map((p, i) => (
            <div
              key={i}
              className={`rt-photo-card${i === 0 ? ' rt-photo-card--main' : ''}${dragOverIdx === i ? ' rt-photo-card--dragover' : ''}${p.loading ? ' rt-photo-card--loading' : ''}`}
              draggable={!p.loading}
              onDragStart={e => onCardDragStart(e, i)}
              onDragOver={e => onCardDragOver(e, i)}
              onDrop={e => onCardDrop(e, i)}
              onDragEnd={onCardDragEnd}
            >
              {i === 0 && <div className="rt-photo-badge">Головне фото</div>}
              <button className="rt-photo-del" onClick={() => removePhoto(i)}>✕</button>
              {p.loading
                ? <div className="rt-photo-spinner">↻</div>
                : <img
                    src={p.url}
                    alt={p.name}
                    className="rt-photo-thumb"
                    onError={e => e.currentTarget.classList.add('rt-photo-thumb--broken')}
                  />
              }
              <div className="rt-photo-name">{p.name}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

// Ключ характеристики → людська назва (для відображення у warnings)
const _CHAR_KEY_LABELS = {
  gender: 'Стать', material: 'Матеріал', season: 'Сезон',
  height: 'Висота', features: 'Особливості', model: 'Модель',
};

/**
 * CharFieldWithMismatch — обгортка CharCombo з inline підказками по сайтах.
 * Показує під полем список сайтів, де вибране значення відсутнє у довіднику,
 * з вибором альтернативного значення для кожного такого сайту.
 */
function CharFieldWithMismatch({ charKey, value, onChange, options, placeholder,
                                  sites, charDataBySite, charOverrides, onOverride,
                                  fullWidth }) {
  // Для показу вручну (кнопкою нижче) — навіть коли немає ні mismatch, ні
  // вже обраного override, щоб можна було ЗАЗДАЛЕГІДЬ обрати інше значення
  // для конкретного сайту, а не тільки коли щось "не збіглось".
  const [manuallyShown, setManuallyShown] = useState(false);

  // Усі сайти, де є словник значень для цього поля (Horoshop) — з розрахованим
  // статусом: чи є mismatch і чи вже обраний override.
  const siteRows = (sites || []).map(site => {
    if (site.platform !== 'horoshop') return null;
    const hsName = (site.extra?.char_names?.[charKey] || '').trim();
    if (!hsName) return null;
    const entries = charDataBySite?.[site.id]?.char_ids?.[hsName] || [];
    if (!entries.length) return null;   // немає довідника — не можемо перевірити/підібрати
    const ovVal = (charOverrides?.[site.id] || {})[charKey] || '';
    const effectiveVal = (ovVal || value || '').trim();
    const isMismatch = !!effectiveVal && !entries.some(e => (e.ua || '').trim().toLowerCase() === effectiveVal.toLowerCase());
    return { site, entries, ovVal, isMismatch };
  }).filter(Boolean);

  // Показуємо рядок сайту: якщо є реальний mismatch, АБО вже обрано своє
  // значення (щоб вибір лишався видимим і НЕ зникав, щойно перестає бути
  // mismatch'ем — саме це й було поламано), АБО натиснули "показати всі".
  const visibleRows = siteRows.filter(r => r.isMismatch || r.ovVal || manuallyShown);
  const hiddenCount = siteRows.length - visibleRows.length;

  return (
    <div className={`rt-field${fullWidth ? ' rt-field-full' : ''}`}>
      <CharCombo value={value} onChange={onChange} options={options} placeholder={placeholder} />
      {visibleRows.length > 0 && (
        <div style={{ marginTop: 5, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {visibleRows.map(({ site, entries, ovVal, isMismatch }) => {
            const available = entries.map(e => e.ua).filter(Boolean);
            return (
              <div key={site.id} style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 10, color: isMismatch ? '#c97c00' : 'var(--rt-ok)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {isMismatch ? '⚠' : '✓'} {site.name}:
                </span>
                <select
                  className="rt-input"
                  style={{ fontSize: 11, padding: '1px 4px', height: 24, flex: 1, minWidth: 0 }}
                  value={ovVal}
                  onChange={e => onOverride(site.id, charKey, e.target.value)}
                >
                  <option value="">— надіслати «{value}» як є —</option>
                  {available.map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      )}
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setManuallyShown(true)}
          style={{ marginTop: 4, fontSize: 10, color: 'var(--lg-text-3)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          Обрати окреме значення ще для {hiddenCount} сайт{hiddenCount === 1 ? 'у' : 'ів'}…
        </button>
      )}
    </div>
  );
}

// Ключі, що йдуть через CharFieldWithMismatch (довідник значень Horoshop) —
// решта ключів у charOverrides вважаються звичайними полями common
// (PerSiteOverrides нижче) і підміняються напряму, без довідника.
const _CHAR_OVERRIDE_KEYS = new Set(['model', 'season', 'material', 'gender', 'height', 'features']);

// Для будь-якого звичайного поля (назва, ціна, опис, артикул...) — можливість
// задати ОКРЕМЕ значення для конкретного сайту, без прив'язки до жодного
// довідника (на відміну від CharFieldWithMismatch, тут нема "mismatch" —
// просто "хочу для цього сайту інакше"). Прихована за посиланням, щоб не
// захаращувати форму — розкривається по кліку і лишається видимою, поки є
// хоч одне обране значення (той самий принцип, що й у CharFieldWithMismatch).
function PerSiteOverrides({ fieldKey, baseValue, sites, charOverrides, onOverride, multiline = false, alwaysShow = false }) {
  const [shown, setShown] = useState(alwaysShow);
  const activeSites = (sites || []).filter(s => s.is_active);
  const withOverride = activeSites.filter(s => ((charOverrides?.[s.id] || {})[fieldKey] || '') !== '');
  const visible = shown ? activeSites : withOverride;
  const InputTag = multiline ? 'textarea' : 'input';

  // Якщо сайтів взагалі нема (жодного не відмічено праворуч для публікації) —
  // натискання кнопки нижче нічого б не показало (visible лишився б
  // порожнім) і виглядало б так, ніби кнопка не працює. Пояснюємо прямо,
  // а не мовчимо.
  if (activeSites.length === 0) {
    return (
      <div style={{ fontSize: 10, color: 'var(--lg-text-3)', padding: '3px 0 0' }}>
        ⚙ Щоб задати окреме значення для сайту — спочатку відмітьте хоч один сайт праворуч
      </div>
    );
  }

  if (visible.length === 0) {
    return (
      <button type="button" onClick={() => setShown(true)}
        style={{ fontSize: 10, color: 'var(--lg-text-3)', background: 'none', border: 'none', cursor: 'pointer', padding: '3px 0 0' }}>
        ⚙ Інше значення для окремого сайту…
      </button>
    );
  }
  return (
    <div style={{ marginTop: 5, display: 'flex', flexDirection: 'column', gap: 4 }}>
      {visible.map(s => {
        const ovVal = (charOverrides?.[s.id] || {})[fieldKey] || '';
        return (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ fontSize: 10, color: ovVal ? 'var(--rt-ok)' : 'var(--lg-text-3)', whiteSpace: 'nowrap', flexShrink: 0, minWidth: 60 }}>
              {ovVal ? '✓' : ''} {s.name}:
            </span>
            <InputTag
              className="rt-input"
              style={{ fontSize: 11, padding: '2px 6px', height: multiline ? 50 : 24, flex: 1, minWidth: 0 }}
              value={ovVal}
              placeholder={baseValue}
              onChange={e => onOverride(s.id, fieldKey, e.target.value)}
            />
          </div>
        );
      })}
    </div>
  );
}

function PublishPanel({ sites, product, variants, photos, sitePhotos = {},
                        groups = [], activeGroupId = null, onGroupChange,
                        charDataBySite = {}, charOverrides = {},
                        initialCheckedSiteIds = null, onCheckedChange, womanSiteIds = [] }) {
  const activeSites = sites.filter(s => s.is_active);

  // Сайти відфільтровані по групі
  const activeGroup  = groups.find(g => g.id === activeGroupId) || null;
  const visibleSites = activeGroup
    ? activeSites.filter(s => (activeGroup.site_ids || []).includes(s.id))
    : activeSites;

  function selectGroup(gid) {
    if (gid === null) localStorage.removeItem('retail_active_group_id');
    else              localStorage.setItem('retail_active_group_id', String(gid));
    onGroupChange?.(gid);
    // Автоматично відмічаємо тільки сайти з групи
    const group = groups.find(g => g.id === gid);
    if (gid === null) {
      setChecked(new Set(activeSites.map(s => s.id)));
    } else if (group) {
      setChecked(new Set(activeSites.filter(s => (group.site_ids || []).includes(s.id)).map(s => s.id)));
    }
    setResults(null);
  }

  const [checked,    setChecked]    = useState(() => {
    // initialCheckedSiteIds — конкретний набір сайтів-цілей (напр. з кнопки
    // "Добавить товар" на екрані "Відсутні товари"), ПЕРЕВАЖАЄ над звичайним
    // відновленням останньої обраної групи з localStorage.
    if (initialCheckedSiteIds) {
      const ids = new Set(initialCheckedSiteIds);
      return new Set(activeSites.filter(s => ids.has(s.id)).map(s => s.id));
    }
    const v = localStorage.getItem('retail_active_group_id');
    return v ? new Set() : new Set(activeSites.map(s => s.id));
  });
  // Віддаємо актуальний набір відмічених сайтів нагору (ProductTab) — щоб
  // PerSiteOverrides для звичайних полів (назва/ціна/опис — ліва колонка,
  // окремий компонент від цієї панелі) теж показував лише сайти групи/
  // відмічені для публікації, а не взагалі всі активні сайти.
  useEffect(() => { onCheckedChange?.(checked); }, [checked]); // eslint-disable-line
  // Ініціалізуємо overrides з дефолтних категорій базових налаштувань сайту
  const [overrides,  setOverrides]  = useState(() => {
    const init = {};
    activeSites.forEach(s => {
      const defCat = s.extra?.default_category_id;
      if (defCat) init[s.id] = { category_id: String(defCat) };
    });
    return init;
  });
  const [categories,   setCategories]   = useState({});
  const [catsLoading,  setCatsLoading]  = useState({});
  const [expanded,     setExpanded]     = useState(() => new Set(activeSites.map(s => s.id)));
  const [publishing,   setPublishing]   = useState(false);
  const [results,      setResults]      = useState(null);
  // initialCheckedSiteIds приходить з ПІЗНІШОГО асинхронного запиту (add-targets)
  // — на момент першого рендера PublishPanel його ще нема (useState-ініціалізатор
  // вище цього не ловить, бо спрацьовує лише один раз при монтуванні), тому
  // застосовуємо ще й ефектом — коли проп РЕАЛЬНО приходить.
  useEffect(() => {
    if (!initialCheckedSiteIds) return;
    const ids = new Set(initialCheckedSiteIds);
    setChecked(new Set(activeSites.filter(s => ids.has(s.id)).map(s => s.id)));
  }, [initialCheckedSiteIds]); // eslint-disable-line

  // Коли групи завантажились (прийшли через props) — застосовуємо збережену групу до checked
  // (але НЕ якщо прийшов конкретний initialCheckedSiteIds — той має пріоритет
  // і не повинен бути перезаписаний пізнім асинхронним завантаженням груп).
  useEffect(() => {
    if (initialCheckedSiteIds) return;
    if (!groups.length) return;
    const v = localStorage.getItem('retail_active_group_id');
    const gid = v ? parseInt(v, 10) : null;
    if (gid !== null) {
      const group = groups.find(g => g.id === gid);
      if (group) {
        setChecked(new Set(activeSites.filter(s => (group.site_ids || []).includes(s.id)).map(s => s.id)));
      } else {
        localStorage.removeItem('retail_active_group_id');
        onGroupChange?.(null);
      }
    }
  }, [groups]); // eslint-disable-line

  // Перевіряє чи є у сайту невирішені mismatch (для ⚠ badge)
  function hasMismatch(site) {
    if (site.platform !== 'horoshop') return false;
    const char_names = site.extra?.char_names || {};
    const char_ids   = (charDataBySite[site.id] || {}).char_ids || {};
    return Object.keys(_CHAR_KEY_LABELS).some(key => {
      const hsName = (char_names[key] || '').trim();
      if (!hsName) return false;
      const entries = char_ids[hsName] || [];
      if (!entries.length) return false;
      const overrideVal = ((charOverrides[site.id] || {})[key] || '').trim();
      const val = (overrideVal || product[key] || '').trim();
      if (!val) return false;
      return !entries.some(e => (e.ua || '').trim().toLowerCase() === val.toLowerCase());
    });
  }

  // Загрузить категории для сайта
  const loadCategories = useCallback(async (siteId, forceRefresh = false) => {
    if (!forceRefresh && (categories[siteId] || catsLoading[siteId])) return;
    setCatsLoading(prev => ({ ...prev, [siteId]: true }));
    try {
      const r = await retailAPI.getCategories(siteId, forceRefresh);
      setCategories(prev => ({ ...prev, [siteId]: r.data }));
    } catch {
      setCategories(prev => ({ ...prev, [siteId]: [] }));
    } finally {
      setCatsLoading(prev => ({ ...prev, [siteId]: false }));
    }
  }, [categories, catsLoading]);

  // Загрузить категории для всех активных сайтов при монтировании
  useEffect(() => {
    activeSites.forEach(s => loadCategories(s.id));
  }, []); // eslint-disable-line

  function toggleSite(id) {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else { next.add(id); loadCategories(id); }
      return next;
    });
  }

  function toggleExpand(id) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function setOverride(siteId, key, val) {
    setOverrides(prev => ({ ...prev, [siteId]: { ...(prev[siteId] || {}), [key]: val } }));
  }

  // Обрали категорію на одному сайті — підказуємо ту саму категорію для
  // ІНШИХ відмічених сайтів, якщо раніше вони вже разом використовувались
  // в одній успішній публікації (запам'ятовується на бекенді автоматично).
  // Не чіпаємо сайти, де категорія вже задана вручну.
  async function handleCategoryChange(site, val) {
    setOverride(site.id, 'category_id', val);
    if (!val) return;
    const others = [...checked].filter(id => id !== site.id && !overrides[id]?.category_id);
    if (!others.length) return;
    try {
      const r = await retailAPI.getCategoryLinkSuggest(site.id, val, others);
      const sug = r.data?.suggestions || {};
      Object.entries(sug).forEach(([tid, catId]) => {
        const targetId = parseInt(tid, 10);
        setOverrides(prev => {
          if (prev[targetId]?.category_id) return prev;  // оператор уже встиг обрати сам, поки чекали відповідь
          return { ...prev, [targetId]: { ...(prev[targetId] || {}), category_id: catId } };
        });
      });
    } catch {}
  }

  async function handlePublish() {
    if (checked.size === 0) return;
    setPublishing(true); setResults(null);
    const mods = variants.filter(v => v.title.trim()).map(v => ({
      title:     v.title.trim(),
      size:      (v.size || '').trim(),
      code:      v.code.trim(),
      price:     parseFloat(v.price) || 0,
      old_price: v.old_price ? parseFloat(v.old_price) : null,
      quantity:  parseInt(v.quantity) || 0,
    }));
    const productData = {
      title:                product.title,
      title_ua:             product.title_ua,
      title_ru:             product.title_ru,
      price:                parseFloat(product.price) || 0,
      old_price:            product.old_price ? parseFloat(product.old_price) : null,
      currency:             product.currency || 'UAH',
      code:                 product.code,
      description:          product.description,
      description_ru:       product.description_ru,
      desc_marketplace_ua:  product.desc_marketplace_ua,
      desc_marketplace_ru:  product.desc_marketplace_ru,
      available:            product.available,
      brand:                product.brand,
      model:                product.model,
      color:                product.color,
      material:             product.material,
      gender:               product.gender,
      season:               product.season,
      features:             product.features,
      popularity:           product.popularity,
      html_title_ua:        product.html_title_ua,
      html_title_ru:        product.html_title_ru,
      html_title_en:        product.html_title_en,
      meta_keywords_ua:     product.meta_keywords_ua,
      meta_keywords_ru:     product.meta_keywords_ru,
      meta_keywords_en:     product.meta_keywords_en,
      meta_description_ua:  product.meta_description_ua,
      meta_description_ru:  product.meta_description_ru,
      meta_description_en:  product.meta_description_en,
      h1_ua:                product.h1_ua,
      h1_ru:                product.h1_ru,
      h1_en:                product.h1_en,
      publish_facebook:     product.publish_facebook,
      publish_kasta:        product.publish_kasta,
      sticker_sale:         product.sticker_sale,
      sticker_new:          product.sticker_new,
      sticker_hit:          product.sticker_hit,
      modifications:        mods,
    };
    // Спільні фото — fallback для тих сайтів, де не задані свої
    const commonPhotoUrls = photos.map(p => p.url).filter(Boolean);
    // Головний захист: публікуємо ТІЛЬКИ сайти що видимі (visibleSites) і відмічені.
    // Перетин checked ∩ visibleSites — навіть якщо checked містить зайві id.
    const visibleIds = new Set(visibleSites.map(s => s.id));
    const targets = [...checked].filter(id => visibleIds.has(id)).map(id => {
      const siteOv  = overrides[id] || {};
      const allOv   = charOverrides[id] || {};
      // charOverrides тепер несе ДВА різновиди: ключі-характеристики
      // (CharFieldWithMismatch — model/season/material/gender/height/features,
      // йдуть у char_overrides, обробляються всередині build_product_payload)
      // і звичайні поля common (title_ru, price, ... — PerSiteOverrides,
      // йдуть напряму в overrides, підміняють common на бекенді).
      const charOv = {}, fieldOv = {};
      Object.entries(allOv).forEach(([k, v]) => {
        if (v == null) return;
        (_CHAR_OVERRIDE_KEYS.has(k) ? charOv : fieldOv)[k] = v;
      });
      const mergedCharOv = { ...(siteOv.char_overrides || {}), ...charOv };
      // Фото: власні для сайту або спільні
      const ownPhotos = (sitePhotos[id] || []).map(p => p.url).filter(Boolean);
      return {
        site_id:   id,
        photos:    ownPhotos.length ? ownPhotos : commonPhotoUrls,
        overrides: {
          ...siteOv,
          ...fieldOv,
          ...(Object.keys(mergedCharOv).length ? { char_overrides: mergedCharOv } : {}),
        },
      };
    });
    // Унісекс на Prom (профіль «Макс»): жіноча версія — окремий «сайт» w<id> у формі;
    // в Prom обидві версії (M і W) йдуть ОДНИМ файлом імпорту.
    const womanIds = (womanSiteIds || []).filter(id => checked.has(id) && visibleIds.has(id));
    for (const id of womanIds) {
      const wo = charOverrides[`w${id}`] || {};
      if (!(wo.title_ru || '').trim() || !(wo.title_ua || '').trim() || !(wo.code || '').trim() || !(sitePhotos[`w${id}`] || []).length) {
        const nm = (sites.find(x => x.id === id) || {}).name || id;
        setResults([{ site_id: 0, site_name: `${nm} (жін.)`, ok: false, error: 'Для жіночої версії заповніть артикул, назву (RU і UA) та фото' }]);
        setPublishing(false);
        return;
      }
    }
    targets.forEach(t => {
      if (!womanIds.includes(t.site_id)) return;
      const wo = charOverrides[`w${t.site_id}`] || {};
      const wf = {};
      Object.entries(wo).forEach(([k, v]) => { if (v != null && v !== '' && !_CHAR_OVERRIDE_KEYS.has(k)) wf[k] = v; });
      t.overrides = { ...t.overrides, gender: 'Чоловіча' };
      t.extra_variant = { ...wf, gender: 'Жіноча', photos: sitePhotos[`w${t.site_id}`].map(p => p.url).filter(Boolean) };
    });
    try {
      const r = await retailAPI.publish({ product: productData, targets });
      setResults(r.data.results);
    } catch (e) {
      setResults([{ site_id: 0, site_name: 'Ошибка', ok: false, error: e?.response?.data?.detail || String(e) }]);
    } finally { setPublishing(false); }
  }

  const checkedCount = checked.size;

  return (
    <div className="rt-publish-panel">
      <div className="rt-panel-title">
        Публікація
        <span className="rt-panel-subtitle">вибрано {checkedCount} з {visibleSites.length}</span>
      </div>

      {/* ── Фільтр по групах (показуємо якщо є хоч одна група) ── */}
      {groups.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
          <button
            className={`rt-btn rt-btn-xs${activeGroupId === null ? ' rt-btn-primary' : ''}`}
            onClick={() => selectGroup(null)}
          >Всі</button>
          {groups.map(g => (
            <button
              key={g.id}
              className={`rt-btn rt-btn-xs${activeGroupId === g.id ? ' rt-btn-primary' : ''}`}
              onClick={() => selectGroup(g.id)}
            >{g.name}</button>
          ))}
        </div>
      )}

      {activeSites.length === 0 ? (
        <div className="rt-empty">Спочатку додайте сайт у вкладці «Сайти»</div>
      ) : (
        <div className="rt-targets">
          {visibleSites.map(site => {
            const isChecked  = checked.has(site.id);
            const isExpanded = expanded.has(site.id);
            const cats       = categories[site.id] || [];
            const loading    = catsLoading[site.id];
            const ov         = overrides[site.id] || {};
            const result     = results?.find(r => r.site_id === site.id);

            return (
              <div key={site.id} className={`rt-target ${isChecked ? 'rt-target--checked' : ''} ${result ? (result.ok ? 'rt-target--ok' : 'rt-target--fail') : ''}`}>
                <div className="rt-target-head">
                  <div className="rt-target-check" onClick={() => toggleSite(site.id)}>
                    {isChecked && '✓'}
                  </div>
                  <div className="rt-target-dot" style={{ background: platformColor(site.platform) }} />
                  <div className="rt-target-info" onClick={() => isChecked && toggleExpand(site.id)} style={{ cursor: isChecked ? 'pointer' : 'default' }}>
                    <div className="rt-target-name">
                      {site.name}
                      {site.extra?.photo_format && (
                        <span className="rt-photo-format-badge">{site.extra.photo_format}</span>
                      )}
                      {(sitePhotos[site.id] || []).length > 0 && (
                        <span title="Власні фото для цього сайту" style={{
                          marginLeft: 5, fontSize: 10, padding: '1px 6px',
                          background: 'rgba(99,102,241,0.12)', color: '#6366f1',
                          borderRadius: 8, fontWeight: 600,
                        }}>📷 {sitePhotos[site.id].length}</span>
                      )}
                      {isChecked && !result && hasMismatch(site) && (
                        <span title="Деякі значення відсутні у довіднику — замінено у формі" style={{ marginLeft: 4, color: '#c97c00', fontSize: 13 }}>⚠</span>
                      )}
                    </div>
                    <div className="rt-target-meta">{site.domain}</div>
                  </div>
                  {result && (
                    <span className={result.ok ? 'rt-result-ok' : 'rt-result-fail'}>
                      {result.ok ? `✓ ${result.platform_id || 'OK'}` : '✗'}
                    </span>
                  )}
                  {isChecked && !result && (
                    <span className="rt-target-arrow" onClick={() => toggleExpand(site.id)}>
                      {isExpanded ? '▲' : '▼'}
                    </span>
                  )}
                </div>

                {isChecked && isExpanded && !result && (
                  <div className="rt-target-expand">
                    <div className="rt-field">
                      <label className="rt-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        Категорія *
                        {site.platform === 'horoshop' && (
                          <button
                            className="rt-btn rt-btn-sm"
                            title="Оновити список категорій"
                            style={{ padding: '1px 6px', fontSize: 11, lineHeight: 1.4 }}
                            disabled={loading}
                            onClick={() => loadCategories(site.id, true)}
                          >↺</button>
                        )}
                      </label>
                      {loading ? (
                        <div className="rt-hint">Завантаження…</div>
                      ) : (
                        <select className="rt-input rt-select" value={ov.category_id || ''} onChange={e => handleCategoryChange(site, e.target.value)}>
                          <option value="">— не вибрано —</option>
                          {cats.map(c => (
                            <option key={c.id} value={c.external_id ?? c.id}>{c.title || c.external_id || c.id}</option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div className="rt-field">
                      <label className="rt-label">Додаткові категорії</label>
                      {loading ? null : (
                        <MultiCatPicker
                          cats={cats}
                          value={ov.alt_category_ids || []}
                          onChange={ids => setOverride(site.id, 'alt_category_ids', ids)}
                        />
                      )}
                    </div>

                    {site.platform === 'prom' && (
                      <div className="rt-field rt-field-full">
                        <label className="rt-label">Назва характеристики розміру</label>
                        <input
                          className="rt-input"
                          placeholder="Виберіть розмір"
                          value={ov.size_attr_name || ''}
                          onChange={e => setOverride(site.id, 'size_attr_name', e.target.value)}
                        />
                        <div className="rt-hint">
                          У кожного акаунту Prom вона може називатись по-різному — подивіться
                          в іншому вже опублікованому товарі цього ж акаунту. Порожньо → «Виберіть розмір».
                        </div>
                      </div>
                    )}

                  </div>
                )}

                {result && !result.ok && (
                  <div className="rt-target-expand rt-target-expand--err">
                    {result.error}
                  </div>
                )}

                {result && result.ok && result.warnings?.length > 0 && (
                  <div className="rt-target-expand" style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--lg-text-2)' }}>
                    {result.warnings.map((w, i) => (
                      <div key={i} style={{ color: '#c97c00', marginBottom: 2 }}>⚠ {w}</div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <button
        className="rt-btn rt-btn-primary rt-btn-publish"
        onClick={handlePublish}
        disabled={publishing || checkedCount === 0}
      >
        {publishing ? 'Публікація…' : `Опублікувати на ${checkedCount} сайт${checkedCount === 1 ? 'і' : 'ах'}`}
      </button>

      {results && results.every(r => r.ok) && (
        <div className="rt-publish-success">✓ Опубліковано на всіх вибраних сайтах</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Вкладка "Новый товар"
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_VARIANT = () => ({ title: '', size: '', code: '', price: '', old_price: '', quantity: '0' });

// Парсимо рядок розмірів: "36-41", "36,37,38", "36-38,40,42-44" → масив рядків
function parseSizes(str) {
  const sizes = [];
  const parts = str.split(',').map(s => s.trim()).filter(Boolean);
  for (const part of parts) {
    const rangeMatch = part.match(/^(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)$/);
    if (rangeMatch) {
      const from = parseFloat(rangeMatch[1]);
      const to   = parseFloat(rangeMatch[2]);
      const step = Number.isInteger(from) && Number.isInteger(to) ? 1 : 0.5;
      for (let s = from; s <= to + 0.001; s += step) {
        sizes.push(Number.isInteger(s) ? String(Math.round(s)) : s.toFixed(1));
      }
    } else if (part) {
      sizes.push(part);
    }
  }
  return [...new Set(sizes)]; // унікальні
}

// ─────────────────────────────────────────────────────────────────────────────
// CharCombo — комбобокс з підказками з існуючих значень
// ─────────────────────────────────────────────────────────────────────────────

function CharCombo({ value, onChange, options = [], placeholder = '' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const filtered = options.filter(o => o && o.toLowerCase().includes((value || '').toLowerCase()));

  useEffect(() => {
    function onOut(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onOut);
    return () => document.removeEventListener('mousedown', onOut);
  }, []);

  return (
    <div className="rt-char-combo" ref={ref}>
      <input
        className="rt-input"
        value={value}
        placeholder={placeholder}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
      />
      {open && filtered.length > 0 && (
        <div className="rt-combo-dropdown">
          {filtered.slice(0, 15).map(o => (
            <div
              key={o}
              className="rt-combo-item"
              onMouseDown={e => { e.preventDefault(); onChange(o); setOpen(false); }}
            >
              {o}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MultiCatPicker({ cats = [], value = [], onChange }) {
  // Використовуємо external_id якщо є (реальний ID на платформі), інакше DB id
  const catKey  = c => String(c.external_id ?? c.id);
  const selIds  = value.map(String);
  const selected  = cats.filter(c => selIds.includes(catKey(c)));
  const available = cats.filter(c => !selIds.includes(catKey(c)));

  function remove(key) { onChange(selIds.filter(v => v !== key)); }
  function add(e) {
    const v = e.target.value;
    if (v) onChange([...selIds, v]);
    e.target.value = '';
  }

  return (
    <div className="rt-multicat">
      {selected.length > 0 && (
        <div className="rt-multicat-chips">
          {selected.map(c => (
            <span key={c.id} className="rt-multicat-chip">
              {c.title || catKey(c)}
              <button className="rt-multicat-chip-x" onClick={() => remove(catKey(c))} type="button">×</button>
            </span>
          ))}
        </div>
      )}
      {available.length > 0 && (
        <select className="rt-input rt-select rt-multicat-add" defaultValue="" onChange={add}>
          <option value="">+ Додати категорію…</option>
          {available.map(c => (
            <option key={c.id} value={catKey(c)}>{c.title || catKey(c)}</option>
          ))}
        </select>
      )}
      {available.length === 0 && selected.length > 0 && (
        <span className="rt-hint" style={{ fontSize: 11 }}>Всі категорії додані</span>
      )}
    </div>
  );
}

// Каталог (DBProduct.season) зберігає сезон у СВОЄМУ вільному тексті — реально
// в базі лише 3 значення (перевірено на даних: "Весна-Осень-Лето" — 21397 шт,
// "Зима" — 4013, "Лето" — 1074), і жодне буквально не збігається зі словником
// сезонів, який реально використовують сайти (Демисезон/Зима/Літо/Всесезон).
// Без цього маппінгу автозаповнення форми при "Добавить товар" підставляло б
// "Весна-Осень-Лето" як є — такого значення на сайтах просто нема, там це
// називається "Демисезон".
const _CATALOG_SEASON_MAP = {
  'весна-осень-лето': 'Демісезон',
  'лето':             'Літо',
  'зима':             'Зима',
};
function mapCatalogSeason(raw) {
  if (!raw) return '';
  return _CATALOG_SEASON_MAP[raw.trim().toLowerCase()] || raw;
}

// Категорія сезону (demi/winter/summer) з сирого каталожного значення — і
// словник регулярок, що впізнають цю категорію в РЕАЛЬНИХ значеннях сайту,
// хай би як саме сайт це назвав (укр./рос. написання, з великої літери тощо).
function _classifySeasonCategory(raw) {
  const s = (raw || '').trim().toLowerCase();
  if (!s) return null;
  if (s === 'зима') return 'winter';
  if (s === 'лето') return 'summer';
  if (s === 'весна-осень-лето') return 'demi';
  return null;
}
const _SEASON_CATEGORY_RE = {
  demi:   /дем[іи]сезон|весна.?осінь|весна.?осень/i,
  winter: /зима/i,
  summer: /л[іi]то/i,
};

// Каталог (DBProduct.sizes) зберігає розміри через ПРОБІЛ ("40 41 42 43 44 45"),
// а parseSizes() вище розуміє лише коми + дефіс-діапазони ("40-45, 47") — без
// пробілів узагалі. Підставити сирий рядок як є в поле "Розміри" означало б,
// що й сама форма показує незручний довгий список, і натискання "Сгенерувати
// з розмірів" створило б ОДИН зламаний варіант (увесь рядок як один "розмір"),
// а не 6 окремих — тому стискаємо суцільні пробіги в діапазон перед підставленням.
function compressSizesToRanges(spaceSeparated) {
  if (!spaceSeparated) return '';
  const tokens = spaceSeparated.trim().split(/\s+/);
  const nums = tokens.map(Number);
  if (tokens.some((t, i) => !Number.isFinite(nums[i]))) return spaceSeparated;  // не схоже на чисті числа — лишаємо як є
  const sorted = [...new Set(nums)].sort((a, b) => a - b);
  const groups = [];
  let start = sorted[0], prev = sorted[0];
  for (let i = 1; i <= sorted.length; i++) {
    const cur = sorted[i];
    if (cur === prev + 1) { prev = cur; continue; }
    groups.push(start === prev ? `${start}` : `${start}-${prev}`);
    start = prev = cur;
  }
  return groups.join(', ');
}

// Евристика "стать за діапазоном розмірів" — за словами користувача, межа
// НЕЧІТКА (чоловічі бувають і з 40-го, жіночі — і до 42-го), тому це лише
// ЗРУЧНИЙ ЗА ЗАМОВЧУВАННЯМ ВАРІАНТ, а не правило — оператор завжди може
// поправити вручну (і авто-здогад більше НЕ підміняє значення, щойно
// оператор хоч раз сам його змінив, див. genderTouchedRef у ProductTab).
// 36-40 — явно жіночий діапазон, 42+ — явно чоловічий, 40-42 — зона
// накладання, де із самих лише розмірів напевно не скажеш → Унісекс.
function guessGenderFromSizes(sizesStr) {
  if (!sizesStr) return '';
  const nums = (sizesStr.match(/\d+(?:[.,]\d+)?/g) || []).map(s => parseFloat(s.replace(',', '.')));
  if (!nums.length) return '';
  const min = Math.min(...nums), max = Math.max(...nums);
  // Уточнено користувачем: 40-45 — чоловічий діапазон (нижня межа саме 40),
  // 39 сам по собі — вже жіночий. Якщо діапазон зачіпає ОБИДВІ зони (і низ
  // до 39, і верх 40+) — Унісекс.
  const touchesWomen = min <= 39;
  const touchesMen   = max >= 40;
  if (touchesWomen && touchesMen) return 'Унісекс';
  if (touchesWomen) return 'Жіноча';
  if (touchesMen) return 'Чоловіча';
  return '';
}

// ─────────────────────────────────────────────────────────────────────────────
// Вкладка "Новий товар"
// ─────────────────────────────────────────────────────────────────────────────

function ProductTab({ sites, prefill = null }) {
  // ── Групи сайтів (shared з PublishPanel через props)
  const [groups,        setGroups]        = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(() => {
    const v = localStorage.getItem('retail_active_group_id');
    return v ? parseInt(v, 10) : null;
  });

  useEffect(() => {
    retailAPI.getSiteGroups().then(r => setGroups(r.data)).catch(() => {});
  }, []);

  // Набір реально відмічених (checked) сайтів з PublishPanel — щоб
  // PerSiteOverrides у лівій колонці (назва/ціна/опис) показували лише ЦІ
  // сайти, а не взагалі всі активні. null, поки PublishPanel ще не
  // змонтувався/не повідомив — PerSiteOverrides у цей момент falls back на
  // всі активні сайти (рідкісна й коротка мить до першого ефекту).
  const [checkedSiteIds, setCheckedSiteIds] = useState(null);

  // ── Ціль додавання з екрану "Відсутні товари" (кнопка "Добавить товар") —
  // конкретні site_id, куди реально можна додати ЦЕЙ товар (вже без тих, де
  // він є, і лише платформи з підтримкою створення — рахує бекенд).
  const [prefillTargetIds, setPrefillTargetIds] = useState(null);
  const [prefillTargets,   setPrefillTargets]   = useState(null);  // повний список з платформами — для підказки "не підтримується"
  useEffect(() => {
    if (!prefill?.sku || !prefill?.originSiteId) return;
    retailAPI.getMissingProductAddTargets(prefill.originSiteId, prefill.sku)
      .then(r => {
        const targets = r.data.targets || [];
        setPrefillTargets(targets);
        setPrefillTargetIds(targets.filter(t => t.supported).map(t => t.site_id));
      })
      .catch(() => { setPrefillTargets([]); setPrefillTargetIds([]); });
  }, [prefill?.sku, prefill?.originSiteId]);

  // ── Основна інформація
  const [titleRu,    setTitleRu]    = useState(() => prefill?.name || '');
  const [titleUa,    setTitleUa]    = useState('');
  const [price,      setPrice]      = useState('');
  const [oldPrice,   setOldPrice]   = useState('');
  const [currency,   setCurrency]   = useState('UAH');
  const [code,       setCode]       = useState(() => prefill?.sku || '');
  const [available,  setAvailable]  = useState(false);

  // ── Характеристики
  const [brand,      setBrand]      = useState(() => prefill?.brand || '');
  const [model,      setModel]      = useState(() => prefill?.model || '');
  const [popularity, setPopularity] = useState('');
  const [season,     setSeason]     = useState(() => mapCatalogSeason(prefill?.season));
  const [features,   setFeatures]   = useState('');
  const [color,      setColor]      = useState(() => prefill?.color || '');
  const [material,   setMaterial]   = useState('');
  const [gender,     setGender]     = useState(() => guessGenderFromSizes(prefill?.sizes));
  const [height,     setHeight]     = useState('');

  // ── Описи
  const [description,       setDescription]       = useState('');
  const [descriptionRu,     setDescriptionRu]     = useState('');
  const [descMarketplaceRu, setDescMarketplaceRu] = useState('');
  const [descMarketplaceUa, setDescMarketplaceUa] = useState('');
  const [keywordsUa,        setKeywordsUa]        = useState('');   // Prom <keywords_ua>
  const [keywordsRu,        setKeywordsRu]        = useState('');   // Prom <keywords>

  // ── SEO (UA / RU / EN)
  const [htmlTitleUa,        setHtmlTitleUa]        = useState('');
  const [htmlTitleRu,        setHtmlTitleRu]        = useState('');
  const [htmlTitleEn,        setHtmlTitleEn]        = useState('');
  const [metaKeywordsUa,     setMetaKeywordsUa]     = useState('');
  const [metaKeywordsRu,     setMetaKeywordsRu]     = useState('');
  const [metaKeywordsEn,     setMetaKeywordsEn]     = useState('');
  const [metaDescriptionUa,  setMetaDescriptionUa]  = useState('');
  const [metaDescriptionRu,  setMetaDescriptionRu]  = useState('');
  const [metaDescriptionEn,  setMetaDescriptionEn]  = useState('');
  const [h1Ua,               setH1Ua]               = useState('');
  const [h1Ru,               setH1Ru]               = useState('');
  const [h1En,               setH1En]               = useState('');

  // ── Маркетплейси / стікери
  const [publishFacebook, setPublishFacebook] = useState(false);
  const [publishKasta,    setPublishKasta]    = useState(false);
  const [stickerSale,     setStickerSale]     = useState(false);
  const [stickerNew,      setStickerNew]      = useState(false);
  const [stickerHit,      setStickerHit]      = useState(false);

  // ── Фото / розміри / варіанти
  const [photos,      setPhotos]      = useState(() =>
    prefill?.photoUrl ? [{ url: prefill.photoUrl, name: (prefill.name || 'Фото') + '-1' }] : []
  );
  const [sitePhotos,  setSitePhotos]  = useState({});  // per-site: { siteId: [{url,name,loading}] }
  const [genNameLoading, setGenNameLoading] = useState(false);
  const [genNameError,   setGenNameError]   = useState('');
  const _aiUser = useAuthStore((state) => state.user);
  const canUseAi = !!(_aiUser?.is_admin && !_aiUser?.impersonated_by) || !!(_aiUser?.permissions?.includes('ai_access'));

  const handleGenerateTitle = async () => {
    const photoUrl = photos[0]?.url;
    if (!photoUrl) { setGenNameError('Спочатку додайте фото'); return; }
    setGenNameError('');
    setGenNameLoading(true);
    try {
      const res = await retailAPI.generateProductName(photoUrl, (prefill?.name || titleRu || titleUa || '').trim());
      setTitleRu(res.data.name);
    } catch (e) {
      setGenNameError(e.response?.data?.detail || 'Помилка генерації назви');
    }
    setGenNameLoading(false);
  };

  const [genAllLoading, setGenAllLoading] = useState(false);
  const [genAllError,   setGenAllError]   = useState('');

  const handleGenerateAll = async () => {
    const photoUrl = photos[0]?.url;
    if (!photoUrl) { setGenAllError('Спочатку додайте фото'); return; }
    if (realOverrideSites.length === 0) {
      setGenAllError('Спочатку відмітьте сайти праворуч — для кожного сайту буде згенеровано свою назву');
      return;
    }
    const hasFilled = [titleRu, titleUa, material, height, features, description, descriptionRu,
      descMarketplaceUa, descMarketplaceRu, keywordsUa, keywordsRu, htmlTitleUa, htmlTitleRu, htmlTitleEn,
      metaKeywordsUa, metaKeywordsRu, metaKeywordsEn, metaDescriptionUa, metaDescriptionRu, metaDescriptionEn,
      h1Ua, h1Ru, h1En].some(v => (v || '').trim());
    if (hasFilled && !window.confirm('Частина полів уже заповнена — перезаписати їх згенерованими?')) return;
    setGenAllError('');
    setGenAllLoading(true);
    try {
      const res = await retailAPI.generateProductFields(
        photoUrl,
        { brand, model, color, season, gender, catalog_name: (prefill?.name || titleRu || titleUa || '').trim(), title: (titleRu || titleUa || '').trim() },
        { materials: charValues.materials, heights: charValues.heights, features: charValues.features },
        realOverrideSites.map(s => ({ id: s.id, name: s.name, domain: s.domain })),
        gender === 'Унісекс' && womanActive,
      );
      const f = res.data.fields || {};
      if (f.women_title_ru || f.women_title_ua) {
        womanSites.forEach(w => {
          setCharOverride(`w${w.id}`, 'title_ru', f.women_title_ru || '');
          setCharOverride(`w${w.id}`, 'title_ua', f.women_title_ua || '');
        });
      }
      // Назви у форматі конкретних сайтів → «свої значення для сайту»
      Object.entries(f.site_titles || {}).forEach(([sid, t]) => {
        if (t.title_ru) setCharOverride(Number(sid), 'title_ru', t.title_ru);
        if (t.title_ua) setCharOverride(Number(sid), 'title_ua', t.title_ua);
      });
      setTitleRu(f.men_title_ru || f.title_ru || ''); setTitleUa(f.men_title_ua || f.title_ua || '');
      setMaterial(f.material || ''); setHeight(f.height || ''); setFeatures(f.features || '');
      setDescription(f.description_ua || ''); setDescriptionRu(f.description_ru || '');
      setDescMarketplaceUa(f.desc_marketplace_ua || ''); setDescMarketplaceRu(f.desc_marketplace_ru || '');
      setKeywordsUa(f.keywords_ua || ''); setKeywordsRu(f.keywords_ru || '');
      setHtmlTitleUa(f.html_title_ua || ''); setHtmlTitleRu(f.html_title_ru || ''); setHtmlTitleEn(f.html_title_en || '');
      setMetaKeywordsUa(f.meta_keywords_ua || ''); setMetaKeywordsRu(f.meta_keywords_ru || ''); setMetaKeywordsEn(f.meta_keywords_en || '');
      setMetaDescriptionUa(f.meta_description_ua || ''); setMetaDescriptionRu(f.meta_description_ru || ''); setMetaDescriptionEn(f.meta_description_en || '');
      setH1Ua(f.h1_ua || ''); setH1Ru(f.h1_ru || ''); setH1En(f.h1_en || '');
    } catch (e) {
      setGenAllError(e.response?.data?.detail || 'Помилка генерації полів');
    }
    setGenAllLoading(false);
  };

  // Підбір фото в Photo Search — відкривається у невеликому popup-вікні (НЕ
  // iframe: сесійна cookie /settings має samesite=lax і браузер не пускає її
  // у cross-site iframe, через що вхід усередині iframe зацикливався). Popup
  // — звичайна top-level навігація, cookie працюють як завжди; обране фото
  // прилітає назад через postMessage.
  useEffect(() => {
    function onPhotoPickMessage(e) {
      if (e.origin !== 'https://localhost:5001') return;
      if (e.data?.type !== 'alldrop-photo-pick' || !e.data.url) return;
      setPhotos(prev => [...prev, { url: e.data.url, name: `Photo Search-${prev.length + 1}` }]);
    }
    window.addEventListener('message', onPhotoPickMessage);
    return () => window.removeEventListener('message', onPhotoPickMessage);
  }, []);
  const [sizes,       setSizes]       = useState(() => compressSizesToRanges(prefill?.sizes));
  // Чи оператор хоч раз сам поправив "Стать" — якщо так, більше НЕ
  // підміняємо її автоматично при зміні розмірів (інакше ручний вибір
  // постійно перебивало б здогадом по розмірах).
  const genderTouchedRef = useRef(false);
  function setGenderManual(val) { genderTouchedRef.current = true; setGender(val); }
  useEffect(() => {
    if (genderTouchedRef.current) return;
    const guess = guessGenderFromSizes(sizes);
    if (guess) setGender(guess);
  }, [sizes]); // eslint-disable-line react-hooks/exhaustive-deps
  const [modWithName, setModWithName] = useState(true);
  const [variants,    setVariants]    = useState([EMPTY_VARIANT()]);

  // ── Значення характеристик із сайтів
  // Базові значення — показуються навіть якщо БД порожня
  const _DEFAULT_CHAR = {
    seasons:   ['Весна-Осінь', 'Демісезон', 'Зима', 'Літо', 'Всесезон'],
    colors:    ['Білий', 'Бежевий', 'Блакитний', 'Золотий', 'Зелений', 'Коричневий', 'Мультикольоровий',
                'Помаранчевий', 'Рожевий', 'Синій', 'Сірий', 'Срібний', 'Фіолетовий', 'Чорний', 'Червоний'],
    materials: ['Гума', 'Еко-шкіра', 'Замша', 'Нубук', 'Пластик', 'Силікон', 'Текстиль', 'Шкіра'],
    genders:   ['Чоловіча', 'Жіноча', 'Унісекс', 'Дитяча'],
    features:  [],
  };
  const _DEFAULT_HEIGHTS = ['Low', 'Mid', 'High', 'На шпильці', 'На танкетці',
    'На платформі', 'Без підбора', 'Низький', 'Середній', 'Високий'];

  const [charValues, setCharValues] = useState({
    brands: [], seasons: _DEFAULT_CHAR.seasons, colors: _DEFAULT_CHAR.colors,
    models: [], materials: _DEFAULT_CHAR.materials, genders: _DEFAULT_CHAR.genders,
    features: [], heights: _DEFAULT_HEIGHTS,
    models_by_brand: {},
  });
  // Дані характеристик по кожному сайту окремо — для mismatch перевірок
  const [charDataBySite, setCharDataBySite] = useState({});   // {site_id: {char_ids, brands,...}}
  // Per-site overrides для характеристик: {site_id: {charKey: altValue}}
  const [charOverrides,  setCharOverrides]  = useState({});

  function setCharOverride(siteId, key, val) {
    setCharOverrides(prev => ({
      ...prev,
      [siteId]: { ...(prev[siteId] || {}), [key]: val || undefined },
    }));
  }

  // Автоматичний артикул для сайтів профілю «Макс»: префікс бренду з таблиці +
  // номер з ALL-коду (Horoshop: ac-17710, Prom: ac-17710M/W). Пишемо у «свої
  // значення для сайту» поля «Код», але не чіпаємо те, що користувач змінив руками.
  const autoCodeRef = useRef({});
  // Унісекс на Prom (профіль «Макс»): товар додається двічі — чоловічий (M) і жіночий (W).
  const [womanCodes,  setWomanCodes]  = useState({});   // {siteId: 'ac-17710W'}
  const [articleNote, setArticleNote] = useState('');
  const articleSitesKey = (checkedSiteIds ? [...checkedSiteIds] : []).sort((x, y) => x - y).join(',');
  useEffect(() => {
    const name = (prefill?.name || titleRu || titleUa || '').trim();
    const sitesPayload = (checkedSiteIds ? sites.filter(s => checkedSiteIds.has(s.id)) : [])
      .map(s => ({ id: s.id, platform: s.platform }));
    if (!name || !code.trim() || sitesPayload.length === 0) { setArticleNote(''); setWomanCodes({}); return; }
    const t = setTimeout(async () => {
      try {
        const res = await retailAPI.articlePreview({ name, code, gender, sites: sitesPayload });
        const articles = res.data?.articles || {};
        const womenArt = res.data?.women_articles || {};
        setWomanCodes(womenArt);
        // жіноча версія — як окремий «сайт» w<id>: свій артикул у «власних значеннях»
        Object.entries(womenArt).forEach(([sid, art]) => {
          const wid = `w${sid}`;
          const cur = (charOverrides[wid] || {}).code || '';
          if (!cur || cur === autoCodeRef.current[wid]) {
            autoCodeRef.current[wid] = art;
            setCharOverride(wid, 'code', art);
          }
        });
        Object.entries(articles).forEach(([sid, art]) => {
          const id = Number(sid);
          const cur = (charOverrides[id] || {}).code || '';
          if (!cur || cur === autoCodeRef.current[id]) {
            autoCodeRef.current[id] = art;
            setCharOverride(id, 'code', art);
          }
        });
        setArticleNote('');
      } catch (e) { /* артикул не критичний — лишається ручним */ }
    }, 500);
    return () => clearTimeout(t);
  }, [prefill?.name, titleRu, titleUa, code, gender, articleSitesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Довідники характеристик: підтягуємо тільки з кешу (без звернення до Horoshop API)
  // Реальне оновлення — тільки через кнопку в "Базових налаштуваннях"
  useEffect(() => {
    if (!sites.length) return;
    Promise.allSettled(sites.map(s => retailAPI.getCharValues(s.id)))
      .then(results => {
        const perSite = {};
        const merged = {
          brands: new Set(), models: new Set(), features: new Set(),
          seasons:   new Set(_DEFAULT_CHAR.seasons),
          colors:    new Set(_DEFAULT_CHAR.colors),
          materials: new Set(_DEFAULT_CHAR.materials),
          genders:   new Set(_DEFAULT_CHAR.genders),
          heights:   new Set(_DEFAULT_HEIGHTS),
          models_by_brand: {},
        };
        results.forEach((r, i) => {
          if (r.status !== 'fulfilled') return;
          const d = r.value.data;
          perSite[sites[i].id] = d;
          ['brands','seasons','colors','models','materials','features','heights'].forEach(k => {
            (d[k] || []).forEach(v => merged[k].add(v));
          });
          (d.genders || []).forEach(v => merged.genders.add(v));
          Object.entries(d.models_by_brand || {}).forEach(([brand, mods]) => {
            if (!merged.models_by_brand[brand]) merged.models_by_brand[brand] = new Set();
            mods.forEach(m => merged.models_by_brand[brand].add(m));
          });
        });
        setCharDataBySite(perSite);
        // Автопідбір сезону ПІД КОЖЕН сайт окремо (тільки коли прийшли з
        // "Добавить товар" — prefill): різні сайти/платформи зберігають
        // сезон по-різному написаним (на реальному скріні користувача —
        // "Демісезон" українською, а не "Демисезон" як у нас у глобальному
        // маппінгу) — тому для Horoshop-сайтів з власним довідником значень
        // (char_ids) підбираємо ТОЧНЕ значення САМЕ цього сайту автоматично,
        // а не покладаємось на один спільний текст для всіх.
        if (prefill?.season) {
          const category = _classifySeasonCategory(prefill.season);
          if (category) {
            sites.forEach((s, i) => {
              if (s.platform !== 'horoshop') return;
              const hsName = (s.extra?.char_names?.season || '').trim();
              if (!hsName) return;
              const entries = perSite[s.id]?.char_ids?.[hsName] || [];
              const match = entries.map(e => e.ua).find(v => _SEASON_CATEGORY_RE[category].test(v || ''));
              if (match) setCharOverride(s.id, 'season', match);
            });
          }
        }
        setCharValues({
          brands:          [...merged.brands].sort(),
          seasons:         [...merged.seasons].sort(),
          colors:          [...merged.colors].sort(),
          models:          [...merged.models].sort(),
          materials:       [...merged.materials].sort(),
          features:        [...merged.features].sort(),
          genders:         [...merged.genders],
          heights:         [...merged.heights],
          models_by_brand: Object.fromEntries(
            Object.entries(merged.models_by_brand).map(([b, m]) => [b, [...m].sort()])
          ),
        });
      });
  }, [sites]); // eslint-disable-line

  // Модели фильтруем по выбранному бренду
  const modelOptions = brand && charValues.models_by_brand[brand]?.length
    ? charValues.models_by_brand[brand]
    : charValues.models;

  const effectiveTitle = (titleUa || titleRu).trim();

  function addVariant()             { setVariants(v => [...v, EMPTY_VARIANT()]); }
  function removeVariant(i)         { setVariants(v => v.filter((_, j) => j !== i)); }
  function updateVariant(i, f, val) { setVariants(v => v.map((x, j) => j === i ? { ...x, [f]: val } : x)); }

  function generateFromSizes() {
    const parsed = parseSizes(sizes);
    if (!parsed.length) return;
    const base = code.trim() || 'item';
    setVariants(parsed.map(s => ({
      title:     modWithName ? (effectiveTitle ? `${effectiveTitle}-${s}` : s) : (effectiveTitle || s),
      size:      s,
      code:      `${base}-${s}`,
      price:     price || '',
      old_price: oldPrice || '',
      quantity:  '0',
    })));
  }

  useEffect(() => {
    if (!sizes.trim()) return;
    const parsed = parseSizes(sizes);
    if (!parsed.length) return;
    const base = code.trim() || 'item';
    setVariants(parsed.map(s => ({
      title:     modWithName ? (effectiveTitle ? `${effectiveTitle}-${s}` : s) : (effectiveTitle || s),
      size:      s,
      code:      `${base}-${s}`,
      price:     price || '',
      old_price: oldPrice || '',
      quantity:  '0',
    })));
  }, [sizes, modWithName, effectiveTitle, code, price, oldPrice]); // eslint-disable-line react-hooks/exhaustive-deps

  // Сайти для PerSiteOverrides у лівій колонці — ті самі, що реально
  // відмічені (checked) у PublishPanel праворуч, а не взагалі всі активні.
  // checkedSiteIds===null лише на коротку мить до першого ефекту PublishPanel.
  const realOverrideSites = checkedSiteIds
    ? sites.filter(s => checkedSiteIds.has(s.id))
    : sites.filter(s => s.is_active);

  const womanSiteIds = Object.keys(womanCodes).map(Number)
    .filter(id => checkedSiteIds && checkedSiteIds.has(id));
  const womanSites = sites.filter(s => womanSiteIds.includes(s.id));
  const womanActive = gender === 'Унісекс' && womanSites.length > 0;

  // Унісекс на Prom: сайт «роздвоюється» на чоловічу (чол.) і жіночу (жін.) версії —
  // у формі вони виглядають як два окремі сайти зі своїми артикулом, назвами і фото.
  const manIds = new Set(womanSites.map(w => w.id));
  const withVersions = (list) => !womanActive ? list : list.flatMap(st => manIds.has(st.id)
    ? [{ ...st, name: `${st.name} (чол.)` }, { ...st, id: `w${st.id}`, realId: st.id, name: `${st.name} (жін.)` }]
    : [st]);
  const overrideSites = withVersions(realOverrideSites);

  const product = {
    title: effectiveTitle,
    title_ru: titleRu, title_ua: titleUa,
    price, old_price: oldPrice, currency, code, description, available,
    description_ru: descriptionRu,
    brand, model,
    popularity: popularity !== '' ? (parseInt(popularity) || null) : null,
    season, features, color, material, gender, height,
    desc_marketplace_ru: descMarketplaceRu,
    desc_marketplace_ua: descMarketplaceUa,
    keywords_ua: keywordsUa, keywords_ru: keywordsRu,
    html_title_ua: htmlTitleUa, html_title_ru: htmlTitleRu, html_title_en: htmlTitleEn,
    meta_keywords_ua: metaKeywordsUa, meta_keywords_ru: metaKeywordsRu, meta_keywords_en: metaKeywordsEn,
    meta_description_ua: metaDescriptionUa, meta_description_ru: metaDescriptionRu, meta_description_en: metaDescriptionEn,
    h1_ua: h1Ua, h1_ru: h1Ru, h1_en: h1En,
    publish_facebook: publishFacebook, publish_kasta: publishKasta,
    sticker_sale: stickerSale, sticker_new: stickerNew, sticker_hit: stickerHit,
  };

  return (
    <div
      className="rt-product-layout"
      onDragOver={e => e.preventDefault()}
      onDrop={e => { e.preventDefault(); }}
    >
      {/* ── Ліва колонка: форма ── */}
      <div className="rt-product-left">

        {/* Основна інформація */}
        <div className="rt-form-card">
          <div className="rt-form-card-title">Основна інформація</div>
          {canUseAi && (
            <div style={{ margin: '0 0 10px' }}>
              <button
                type="button"
                title="Заповнити назви, характеристики, описи та SEO по фото (Claude)"
                disabled={genAllLoading || !photos[0]?.url}
                onClick={handleGenerateAll}
                style={{ padding: '6px 12px', borderRadius: 6 }}
              >
                {genAllLoading ? 'Генерація… (до хвилини)' : '✨ Згенерувати всі поля'}
              </button>
              {genAllError && <p style={{ color: 'var(--lg-danger, #e5484d)', fontSize: 12, margin: '4px 0 0' }}>{genAllError}</p>}
            </div>
          )}
          <div className="rt-form-grid">
            <div className="rt-field">
              <label className="rt-label">Назва (RU) *</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input className="rt-input" placeholder="Nike Air Max 90 Black" value={titleRu} onChange={e => setTitleRu(e.target.value)} style={{ flex: 1 }} />
                {canUseAi && (
                  <button
                    type="button"
                    title="Згенерувати назву по фото (Claude)"
                    disabled={genNameLoading || !photos[0]?.url}
                    onClick={handleGenerateTitle}
                    style={{ padding: '0 10px', borderRadius: 6 }}
                  >
                    {genNameLoading ? '...' : '✨'}
                  </button>
                )}
              </div>
              {genNameError && <p style={{ color: 'var(--lg-danger, #e5484d)', fontSize: 12, margin: '2px 0 0' }}>{genNameError}</p>}
              <PF>Horoshop, Prom</PF>
              <PerSiteOverrides fieldKey="title_ru" baseValue={titleRu} sites={overrideSites} charOverrides={charOverrides} onOverride={setCharOverride} alwaysShow />
            </div>
            <div className="rt-field">
              <label className="rt-label">Назва (UA)</label>
              <input className="rt-input" placeholder="Nike Air Max 90 Black" value={titleUa} onChange={e => setTitleUa(e.target.value)} />
              <PF>Horoshop, Prom</PF>
              <PerSiteOverrides fieldKey="title_ua" baseValue={titleUa} sites={overrideSites} charOverrides={charOverrides} onOverride={setCharOverride} alwaysShow />
            </div>
            <div className="rt-field">
              <label className="rt-label">Артикул</label>
              <input className="rt-input" placeholder="ALL01234" value={code} onChange={e => setCode(e.target.value)} />
              <PF>Horoshop, Prom</PF>
              <PerSiteOverrides fieldKey="code" baseValue={code} sites={overrideSites} charOverrides={charOverrides} onOverride={setCharOverride} alwaysShow />
              {articleNote && <p style={{ fontSize: 11, color: 'var(--lg-text-3)', margin: '4px 0 0' }}>{articleNote}</p>}
            </div>
            <div className="rt-field">
              <label className="rt-label">Наявність</label>
              <div className="rt-toggle-wrap" onClick={() => setAvailable(v => !v)}>
                <div className={`rt-toggle ${available ? 'rt-toggle--on' : ''}`}><div className="rt-toggle-knob" /></div>
                <span>{available ? 'В наявності' : 'Немає'}</span>
              </div>
              <PF>Horoshop, Prom</PF>
            </div>
            <div className="rt-field">
              <label className="rt-label">Ціна ₴ *</label>
              <input className="rt-input" type="number" min="0" placeholder="1500" value={price} onChange={e => setPrice(e.target.value)} />
              <PF>Horoshop, Prom</PF>
              <PerSiteOverrides fieldKey="price" baseValue={price} sites={overrideSites} charOverrides={charOverrides} onOverride={setCharOverride} />
            </div>
            <div className="rt-field">
              <label className="rt-label">Стара ціна ₴</label>
              <input className="rt-input" type="number" min="0" placeholder="—" value={oldPrice} onChange={e => setOldPrice(e.target.value)} />
              <PF>Horoshop, Prom</PF>
            </div>
            <div className="rt-field">
              <label className="rt-label">Валюта</label>
              <select className="rt-input" value={currency} onChange={e => setCurrency(e.target.value)}>
                {['UAH','EUR','USD','PLN','MDL'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <PF>Horoshop, Prom</PF>
            </div>
          </div>
        </div>

        {/* Характеристики */}
        <div className="rt-form-card">
          <div className="rt-form-card-title">Характеристики</div>
          <div className="rt-form-grid">
            <div className="rt-field">
              <label className="rt-label">Бренд</label>
              <CharCombo value={brand} onChange={setBrand} options={charValues.brands} placeholder="Nike" />
              <PF>Horoshop, Prom</PF>
            </div>
            <div className="rt-field">
              <label className="rt-label">Модель</label>
              <CharFieldWithMismatch
                charKey="model" value={model} onChange={setModel}
                options={modelOptions} placeholder="Air Max 90"
                sites={sites} charDataBySite={charDataBySite}
                charOverrides={charOverrides} onOverride={setCharOverride}
              />
              <PF>Horoshop, Prom</PF>
            </div>
            <div className="rt-field">
              <label className="rt-label">Сезон</label>
              <CharFieldWithMismatch
                charKey="season" value={season} onChange={setSeason}
                options={charValues.seasons} placeholder="Демісезон"
                sites={sites} charDataBySite={charDataBySite}
                charOverrides={charOverrides} onOverride={setCharOverride}
              />
              <PF>Horoshop, Prom</PF>
            </div>
            <div className="rt-field">
              <label className="rt-label">Колір</label>
              <CharCombo value={color} onChange={setColor} options={charValues.colors} placeholder="Чорний" />
              <PF>Horoshop, Prom</PF>
            </div>
            <div className="rt-field">
              <label className="rt-label">Матеріал</label>
              <CharFieldWithMismatch
                charKey="material" value={material} onChange={setMaterial}
                options={charValues.materials} placeholder="Шкіра"
                sites={sites} charDataBySite={charDataBySite}
                charOverrides={charOverrides} onOverride={setCharOverride}
              />
              <PF>Horoshop, Prom</PF>
            </div>
            <div className="rt-field">
              <label className="rt-label">Стать</label>
              <CharFieldWithMismatch
                charKey="gender" value={gender} onChange={setGenderManual}
                options={charValues.genders} placeholder="Жіночий"
                sites={sites} charDataBySite={charDataBySite}
                charOverrides={charOverrides} onOverride={setCharOverride}
              />
              <PF>Horoshop, Prom</PF>
            </div>
            <div className="rt-field">
              <label className="rt-label">Висота</label>
              <CharFieldWithMismatch
                charKey="height" value={height} onChange={setHeight}
                options={charValues.heights} placeholder="Low"
                sites={sites} charDataBySite={charDataBySite}
                charOverrides={charOverrides} onOverride={setCharOverride}
              />
              <PF>Horoshop</PF>
            </div>
            <div className="rt-field">
              <label className="rt-label">Популярність <span className="rt-hint-inline">1–100</span></label>
              <input className="rt-input" type="number" min="1" max="100" placeholder="50" value={popularity} onChange={e => setPopularity(e.target.value)} />
              <PF>Поки не використовується жодною платформою</PF>
            </div>
            <div className="rt-field rt-field-full">
              <label className="rt-label">Особливості</label>
              <CharFieldWithMismatch
                charKey="features" value={features} onChange={setFeatures}
                options={charValues.features || []} placeholder="Водовідштовхуюче покриття"
                sites={sites} charDataBySite={charDataBySite}
                charOverrides={charOverrides} onOverride={setCharOverride}
                fullWidth
              />
              <PF>Horoshop</PF>
            </div>
          </div>
        </div>

        {/* Розміри та варіанти */}
        <div className="rt-form-card">
          <div className="rt-form-card-title">Розміри та варіанти</div>
          <div className="rt-form-grid">
            <div className="rt-field rt-field-full">
              <label className="rt-label">Розміри <span className="rt-hint-inline">36-41 або 36,38,40</span></label>
              <div className="rt-sizes-row">
                <input
                  className="rt-input"
                  placeholder="36-41"
                  value={sizes}
                  onChange={e => setSizes(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && generateFromSizes()}
                />
                <button className="rt-btn rt-btn-sm" onClick={generateFromSizes} title="Сформувати варіанти">↻</button>
              </div>
              <div className="rt-sizes-toggle" onClick={() => setModWithName(v => !v)}>
                <div className={`rt-toggle rt-toggle--xs ${modWithName ? 'rt-toggle--on' : ''}`}>
                  <div className="rt-toggle-knob" />
                </div>
                <span className="rt-hint" style={{ margin: 0 }}>
                  {modWithName
                    ? `Назва варіанту: «${effectiveTitle || 'Назва'}-36»`
                    : `Назва варіанту: «36»`}
                </span>
              </div>
              <PF>Horoshop, Prom</PF>
            </div>
          </div>
          <div className="rt-tbl-wrap">
            <table className="rt-table">
              <thead>
                <tr>
                  <th>Назва варіанту</th><th>Розмір</th><th>Артикул</th>
                  <th>Ціна ₴</th><th>Стара ціна</th><th>Кількість</th><th></th>
                </tr>
              </thead>
              <tbody>
                {variants.map((v, i) => (
                  <tr key={i}>
                    <td><input className="rt-input rt-input-sm" placeholder="Black / 42" value={v.title}     onChange={e => updateVariant(i, 'title',     e.target.value)} /></td>
                    <td><input className="rt-input rt-input-sm rt-input-num" placeholder="36"               value={v.size}      onChange={e => updateVariant(i, 'size',      e.target.value)} /></td>
                    <td><input className="rt-input rt-input-sm" placeholder="SKU-001"    value={v.code}      onChange={e => updateVariant(i, 'code',      e.target.value)} /></td>
                    <td><input className="rt-input rt-input-sm rt-input-num" type="number" placeholder="1500" value={v.price}     onChange={e => updateVariant(i, 'price',     e.target.value)} /></td>
                    <td><input className="rt-input rt-input-sm rt-input-num" type="number" placeholder="—"   value={v.old_price} onChange={e => updateVariant(i, 'old_price', e.target.value)} /></td>
                    <td><input className="rt-input rt-input-sm rt-input-num" type="number" min="0"           value={v.quantity}  onChange={e => updateVariant(i, 'quantity',  e.target.value)} /></td>
                    <td><button className="rt-icon-btn rt-icon-btn-del" onClick={() => removeVariant(i)}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 8 }}>
            <button className="rt-btn rt-btn-sm" onClick={addVariant}>+ Додати варіант</button>
          </div>
        </div>

        {/* Описи */}
        <div className="rt-form-card">
          <div className="rt-form-card-title">Описи</div>
          <div className="rt-form-grid">
            <div className="rt-field rt-field-full">
              <label className="rt-label">Опис товару UA</label>
              <textarea className="rt-input rt-textarea" rows={3} placeholder="Загальний опис (UA)…" value={description} onChange={e => setDescription(e.target.value)} />
              <PF>Horoshop, Prom</PF>
              <PerSiteOverrides fieldKey="description" baseValue={description} sites={overrideSites} charOverrides={charOverrides} onOverride={setCharOverride} multiline />
            </div>
            <div className="rt-field rt-field-full">
              <label className="rt-label">Опис товару RU</label>
              <textarea className="rt-input rt-textarea" rows={3} placeholder="Загальний опис (RU)…" value={descriptionRu} onChange={e => setDescriptionRu(e.target.value)} />
              <PF>Horoshop, Prom</PF>
            </div>
            <div className="rt-field rt-field-full">
              <label className="rt-label">Опис для маркетплейсів UA</label>
              <textarea className="rt-input rt-textarea" rows={3} placeholder="Опис для Rozetka, Prom…" value={descMarketplaceUa} onChange={e => setDescMarketplaceUa(e.target.value)} />
              <PF>Horoshop</PF>
            </div>
            <div className="rt-field rt-field-full">
              <label className="rt-label">Опис для маркетплейсів RU</label>
              <textarea className="rt-input rt-textarea" rows={3} placeholder="Описание для Розетки…" value={descMarketplaceRu} onChange={e => setDescMarketplaceRu(e.target.value)} />
              <PF>Horoshop</PF>
            </div>
            <div className="rt-field rt-field-full">
              <label className="rt-label">Ключові слова (Prom) UA</label>
              <textarea className="rt-input rt-textarea" rows={2} placeholder="nike air max 90, кросівки жіночі, найк аір макс…" value={keywordsUa} onChange={e => setKeywordsUa(e.target.value)} />
              <PF>Prom</PF>
            </div>
            <div className="rt-field rt-field-full">
              <label className="rt-label">Ключові слова (Prom) RU</label>
              <textarea className="rt-input rt-textarea" rows={2} placeholder="nike air max 90, кроссовки женские, найк аир макс…" value={keywordsRu} onChange={e => setKeywordsRu(e.target.value)} />
              <PF>Prom</PF>
            </div>
          </div>
        </div>

        {/* SEO */}
        <div className="rt-form-card">
          <div className="rt-form-card-title">SEO</div>
          <div className="rt-form-grid">
            {/* HTML Title */}
            <div className="rt-field rt-field-full rt-seo-group">
              <label className="rt-label">HTML Title</label>
              <div className="rt-seo-langs">
                <span className="rt-seo-lang-label">UA</span>
                <input className="rt-input" placeholder="Nike Air Max 90 — купити в Україні" value={htmlTitleUa} onChange={e => setHtmlTitleUa(e.target.value)} />
                <span className="rt-seo-lang-label">RU</span>
                <input className="rt-input" placeholder="Nike Air Max 90 — купить в Украине" value={htmlTitleRu} onChange={e => setHtmlTitleRu(e.target.value)} />
                <span className="rt-seo-lang-label">EN</span>
                <input className="rt-input" placeholder="Nike Air Max 90 — buy in Ukraine" value={htmlTitleEn} onChange={e => setHtmlTitleEn(e.target.value)} />
              </div>
              <PF>Horoshop</PF>
            </div>
            {/* META Keywords */}
            <div className="rt-field rt-field-full rt-seo-group">
              <label className="rt-label">META Keywords</label>
              <div className="rt-seo-langs">
                <span className="rt-seo-lang-label">UA</span>
                <input className="rt-input" placeholder="nike, air max, кросівки" value={metaKeywordsUa} onChange={e => setMetaKeywordsUa(e.target.value)} />
                <span className="rt-seo-lang-label">RU</span>
                <input className="rt-input" placeholder="nike, air max, кроссовки" value={metaKeywordsRu} onChange={e => setMetaKeywordsRu(e.target.value)} />
                <span className="rt-seo-lang-label">EN</span>
                <input className="rt-input" placeholder="nike, air max, sneakers" value={metaKeywordsEn} onChange={e => setMetaKeywordsEn(e.target.value)} />
              </div>
              <PF>Horoshop</PF>
            </div>
            {/* META Description */}
            <div className="rt-field rt-field-full rt-seo-group">
              <label className="rt-label">META Description</label>
              <div className="rt-seo-langs">
                <span className="rt-seo-lang-label">UA</span>
                <textarea className="rt-input rt-textarea" rows={2} placeholder="Купити Nike Air Max 90 за ціною…" value={metaDescriptionUa} onChange={e => setMetaDescriptionUa(e.target.value)} />
                <span className="rt-seo-lang-label">RU</span>
                <textarea className="rt-input rt-textarea" rows={2} placeholder="Купить Nike Air Max 90 по цене…" value={metaDescriptionRu} onChange={e => setMetaDescriptionRu(e.target.value)} />
                <span className="rt-seo-lang-label">EN</span>
                <textarea className="rt-input rt-textarea" rows={2} placeholder="Buy Nike Air Max 90 at a price…" value={metaDescriptionEn} onChange={e => setMetaDescriptionEn(e.target.value)} />
              </div>
              <PF>Horoshop</PF>
            </div>
            {/* H1 */}
            <div className="rt-field rt-field-full rt-seo-group">
              <label className="rt-label">H1 заголовок</label>
              <div className="rt-seo-langs">
                <span className="rt-seo-lang-label">UA</span>
                <input className="rt-input" placeholder="Nike Air Max 90 Black" value={h1Ua} onChange={e => setH1Ua(e.target.value)} />
                <span className="rt-seo-lang-label">RU</span>
                <input className="rt-input" placeholder="Nike Air Max 90 Black" value={h1Ru} onChange={e => setH1Ru(e.target.value)} />
                <span className="rt-seo-lang-label">EN</span>
                <input className="rt-input" placeholder="Nike Air Max 90 Black" value={h1En} onChange={e => setH1En(e.target.value)} />
              </div>
              <PF>Horoshop</PF>
            </div>
          </div>
        </div>

        {/* Маркетплейси + Стікери */}
        <div className="rt-form-card">
          <div className="rt-form-card-title">Маркетплейси та стікери</div>
          <div className="rt-form-grid">
            <div className="rt-field">
              <label className="rt-label">Facebook Feed</label>
              <div className="rt-toggle-wrap" onClick={() => setPublishFacebook(v => !v)}>
                <div className={`rt-toggle ${publishFacebook ? 'rt-toggle--on' : ''}`}><div className="rt-toggle-knob" /></div>
                <span>{publishFacebook ? 'Увімкнено' : 'Вимкнено'}</span>
              </div>
              <PF>Поки не використовується жодною платформою</PF>
            </div>
            <div className="rt-field">
              <label className="rt-label">Kasta</label>
              <div className="rt-toggle-wrap" onClick={() => setPublishKasta(v => !v)}>
                <div className={`rt-toggle ${publishKasta ? 'rt-toggle--on' : ''}`}><div className="rt-toggle-knob" /></div>
                <span>{publishKasta ? 'Увімкнено' : 'Вимкнено'}</span>
              </div>
              <PF>Поки не використовується жодною платформою</PF>
            </div>
            <div className="rt-field">
              <label className="rt-label">Стікер «Розпродаж»</label>
              <div className="rt-toggle-wrap" onClick={() => setStickerSale(v => !v)}>
                <div className={`rt-toggle ${stickerSale ? 'rt-toggle--on' : ''}`}><div className="rt-toggle-knob" /></div>
                <span>{stickerSale ? 'Так' : 'Ні'}</span>
              </div>
              <PF>Horoshop</PF>
            </div>
            <div className="rt-field">
              <label className="rt-label">Стікер «Новинка»</label>
              <div className="rt-toggle-wrap" onClick={() => setStickerNew(v => !v)}>
                <div className={`rt-toggle ${stickerNew ? 'rt-toggle--on' : ''}`}><div className="rt-toggle-knob" /></div>
                <span>{stickerNew ? 'Так' : 'Ні'}</span>
              </div>
              <PF>Horoshop</PF>
            </div>
            <div className="rt-field">
              <label className="rt-label">Стікер «Хіт»</label>
              <div className="rt-toggle-wrap" onClick={() => setStickerHit(v => !v)}>
                <div className={`rt-toggle ${stickerHit ? 'rt-toggle--on' : ''}`}><div className="rt-toggle-knob" /></div>
                <span>{stickerHit ? 'Так' : 'Ні'}</span>
              </div>
              <PF>Horoshop</PF>
            </div>
          </div>
        </div>

        {/* Фото */}
        <div className="rt-form-card">
          <div className="rt-form-card-title">Фото</div>
          {/* Спільні фото — підставляються для всіх сайтів де немає власних */}
          {(() => {
            const allActive = sites.filter(s => s.is_active);
            const ag = groups.find(g => g.id === activeGroupId) || null;
            const cnt = ag ? allActive.filter(s => (ag.site_ids || []).includes(s.id)).length : allActive.length;
            return cnt > 1 ? (
              <div style={{ fontSize: 11, color: 'var(--lg-text-3)', padding: '0 0 8px', fontStyle: 'italic' }}>
                Спільні — для сайтів без окремого завантаження
              </div>
            ) : null;
          })()}
          {prefill?.sku && (
            <div className="rt-hint" style={{ marginBottom: 8 }}>
              <a href="#" onClick={e => {
                e.preventDefault();
                const url = `https://localhost:5001/gallery?pick=1&sku=${encodeURIComponent(prefill.sku)}`;
                const w = window.open(url, 'alldropPhotoPicker', 'width=1100,height=820,menubar=no,toolbar=no,location=no,status=no');
                if (w) w.focus();
              }}>
                🔍 Підібрати фото в Photo Search (підтверджена галерея)
              </a>
              {' '}(потрібен активний вхід на localhost:5001; відкриється галерея за артикулом цього товару,
              за потреби введіть там артикул схожого товару — обране фото одразу додасться сюди).
            </div>
          )}
          <PhotoUploader productTitle={effectiveTitle} photos={photos} onChange={setPhotos} />
          <PF>Horoshop, Prom</PF>

          {/* Per-site фото — тільки сайти активної групи (або всі якщо групи немає) */}
          {(() => {
            const allActive = sites.filter(s => s.is_active);
            const activeGroup = groups.find(g => g.id === activeGroupId) || null;
            const photoSites = activeGroup
              ? allActive.filter(s => (activeGroup.site_ids || []).includes(s.id))
              : allActive;
            const photoList = withVersions(photoSites);
            if (photoList.length <= 1) return null;
            return photoList.map(site => {
              const sp     = sitePhotos[site.id] || [];
              const pColor = platformColor(site.platform);
              return (
                <div key={site.id} style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--lg-border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: pColor, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--lg-text)' }}>{site.name}</span>
                    {site.extra?.photo_format && (
                      <span className="rt-photo-format-badge">{site.extra.photo_format}</span>
                    )}
                    {sp.length === 0 && (
                      <span style={{ fontSize: 11, color: 'var(--lg-text-3)', fontStyle: 'italic' }}>
                        використовуються спільні {photos.length > 0 ? `(${photos.length} шт.)` : ''}
                      </span>
                    )}
                    {sp.length > 0 && (
                      <button
                        className="rt-btn rt-btn-xs"
                        style={{ marginLeft: 'auto', color: 'var(--lg-text-3)', fontSize: 11 }}
                        onClick={() => setSitePhotos(p => ({ ...p, [site.id]: [] }))}
                      >✕ скинути</button>
                    )}
                  </div>
                  <PhotoUploader
                    productTitle={effectiveTitle}
                    photos={sp}
                    onChange={p => setSitePhotos(prev => ({ ...prev, [site.id]: p }))}
                  />
                </div>
              );
            });
          })()}
        </div>


      </div>

      {/* ── Права колонка: публікація ── */}
      <div className="rt-product-right">
        {prefill && prefillTargets && prefillTargets.some(t => !t.supported) && (
          <div className="rt-form-card" style={{ fontSize: 13, color: '#a66', background: '#fff8f0' }}>
            Не підтримується автоматичне створення для: {prefillTargets.filter(t => !t.supported).map(t => t.name).join(', ')}
            {' '}(платформа поки не вміє створювати нові товари — тільки оновлювати існуючі).
          </div>
        )}
        <div className="rt-form-card">
          <PublishPanel
            sites={sites} product={product} variants={variants} photos={photos}
            sitePhotos={sitePhotos}
            groups={groups} activeGroupId={activeGroupId} onGroupChange={setActiveGroupId}
            charDataBySite={charDataBySite}
            charOverrides={charOverrides}
            initialCheckedSiteIds={prefillTargetIds}
            onCheckedChange={setCheckedSiteIds}
            womanSiteIds={womanActive ? womanSites.map(w => w.id) : []}
          />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Вкладка "Товары сайта"
// ─────────────────────────────────────────────────────────────────────────────

function EmptyStockTab({ site }) {
  const [tab,      setTab]      = useState('all'); // 'all' | '3m' | '6m'
  const [items,    setItems]    = useState([]);
  const [total,    setTotal]    = useState(0);
  const [loading,  setLoading]  = useState(false);
  const [offset,   setOffset]   = useState(0);
  const LIMIT = 100;

  const monthsMap = { all: 0, '3m': 3, '6m': 6 };

  async function load(t = tab, off = 0) {
    setLoading(true);
    try {
      const r = await retailAPI.getEmptyStock(site.id, { months: monthsMap[t], offset: off, limit: LIMIT });
      setItems(off === 0 ? r.data.items : prev => [...prev, ...r.data.items]);
      setTotal(r.data.total);
      setOffset(off);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }

  useEffect(() => { load(tab, 0); }, [tab]); // eslint-disable-line

  function selectTab(t) { setTab(t); setItems([]); setOffset(0); }

  function daysEmpty(iso) {
    if (!iso) return '—';
    const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    if (d < 30) return `${d} дн.`;
    const m = Math.floor(d / 30);
    return `${m} міс.`;
  }

  function formatDate(iso) {
    return formatKyivDate(iso, 'uk-UA');
  }

  const tabs = [
    { id: 'all', label: 'Всі' },
    { id: '3m',  label: '> 3 місяці' },
    { id: '6m',  label: '> 6 місяців' },
  ];

  return (
    <div style={{ marginTop: 4 }}>
      {/* Підтаби */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button
            key={t.id}
            className={`rt-btn rt-btn-sm${tab === t.id ? ' rt-btn-primary' : ''}`}
            onClick={() => selectTab(t.id)}
          >{t.label}</button>
        ))}
        <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--lg-text-3)', alignSelf: 'center' }}>
          {loading ? 'Завантаження…' : `${total} товарів`}
        </span>
      </div>

      {!loading && items.length === 0 && (
        <div className="rt-empty">
          {tab === 'all' ? 'Немає товарів з нульовим наявністю 🎉' : 'Немає товарів у цьому діапазоні'}
        </div>
      )}

      {items.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="rt-table" style={{ minWidth: 560 }}>
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Артикул</th>
                <th>Назва</th>
                <th>Пусто з</th>
                <th>Тривалість</th>
                <th>Варіантів</th>
              </tr>
            </thead>
            <tbody>
              {items.map(p => (
                <tr key={p.id}>
                  <td style={{ padding: '4px 8px' }}>
                    {p.photo
                      ? <img src={p.photo} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }} />
                      : <div style={{ width: 32, height: 32, background: 'var(--lg-hover)', borderRadius: 4 }} />
                    }
                  </td>
                  <td>
                    <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.article_site || '—'}</span>
                    {p.all_sku && (
                      <div style={{ fontSize: 11, color: 'var(--lg-text-3)' }}>{p.all_sku}</div>
                    )}
                  </td>
                  <td style={{ maxWidth: 220 }}>
                    <div style={{ fontSize: 12, color: 'var(--lg-text)' }}>{p.title_ua || p.title_ru || '—'}</div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{formatDate(p.stock_empty_since)}</td>
                  <td>
                    <span style={{
                      fontSize: 12, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
                      background: tab === '6m' ? 'rgba(255,59,48,0.10)' : tab === '3m' ? 'rgba(255,149,0,0.12)' : 'var(--lg-hover)',
                      color: tab === '6m' ? '#ff3b30' : tab === '3m' ? '#c97c00' : 'var(--lg-text-2)',
                    }}>{daysEmpty(p.stock_empty_since)}</span>
                  </td>
                  <td style={{ textAlign: 'center', fontSize: 12, color: 'var(--lg-text-3)' }}>{p.variants_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {items.length < total && (
        <button
          className="rt-btn rt-btn-sm"
          style={{ marginTop: 10 }}
          disabled={loading}
          onClick={() => load(tab, offset + LIMIT)}
        >Завантажити ще ({total - items.length})</button>
      )}
    </div>
  );
}


function BrokenBindingsRow({ site, product, onFixed }) {
  const [editing,  setEditing]  = useState(false);
  const [skuInput, setSkuInput] = useState(product.all_sku || '');
  const [saving,   setSaving]   = useState(false);
  const [err,      setErr]      = useState('');

  async function save() {
    const val = skuInput.trim();
    if (!val) return;
    setSaving(true);
    setErr('');
    try {
      await retailAPI.fixProductBinding(site.id, product.id, val);
      onFixed(product.id); // прибираємо з таблиці
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Помилка збереження');
      setSaving(false);
    }
  }

  function cancel() { setEditing(false); setSkuInput(product.all_sku || ''); setErr(''); }

  return (
    <tr>
      <td style={{ padding: '4px 8px' }}>
        {product.photo
          ? <img src={product.photo} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }} />
          : <div style={{ width: 32, height: 32, background: 'var(--lg-hover)', borderRadius: 4 }} />
        }
      </td>
      <td>
        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{product.article_site || '—'}</span>
      </td>
      <td style={{ maxWidth: 180 }}>
        <div style={{ fontSize: 12 }}>{product.title_ua || product.title_ru || '—'}</div>
      </td>
      <td>
        {editing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', gap: 4 }}>
              <input
                className="rt-input"
                style={{ fontFamily: 'monospace', fontSize: 12, padding: '2px 6px', width: 150 }}
                value={skuInput}
                onChange={e => { setSkuInput(e.target.value); setErr(''); }}
                onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
                autoFocus
                placeholder="ALL12345"
              />
              <button
                className="rt-btn rt-btn-sm rt-btn-primary"
                disabled={saving || !skuInput.trim()}
                onClick={save}
                title="Зберегти (Enter)"
              >✓</button>
              <button
                className="rt-btn rt-btn-sm"
                disabled={saving}
                onClick={cancel}
                title="Скасувати (Esc)"
              >✕</button>
            </div>
            {err && <div style={{ fontSize: 11, color: 'var(--rt-error,#ff3b30)' }}>{err}</div>}
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--rt-error,#ff3b30)' }}>
              {product.all_sku}
            </span>
            {product.base_sku !== product.all_sku && (
              <span style={{ fontSize: 11, color: 'var(--lg-text-3)' }}>→ base: {product.base_sku}</span>
            )}
            <button
              className="rt-btn rt-btn-xs"
              style={{ marginLeft: 4 }}
              onClick={() => setEditing(true)}
            >✏️ Виправити</button>
          </div>
        )}
      </td>
      <td style={{ fontSize: 12, color: 'var(--rt-error,#ff3b30)' }}>
        SKU не знайдений в каталозі
      </td>
    </tr>
  );
}

function BrokenBindingsTab({ site }) {
  const [items,   setItems]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset,  setOffset]  = useState(0);
  const LIMIT = 100;

  async function load(off = 0) {
    setLoading(true);
    try {
      const r = await retailAPI.getBrokenBindings(site.id, { offset: off, limit: LIMIT });
      setItems(off === 0 ? r.data.items : prev => [...prev, ...r.data.items]);
      setTotal(r.data.total);
      setOffset(off);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }

  useEffect(() => { load(0); }, []); // eslint-disable-line

  // Видаляємо рядок після успішного виправлення
  function handleFixed(productId) {
    setItems(prev => prev.filter(p => p.id !== productId));
    setTotal(prev => Math.max(0, prev - 1));
  }

  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <span style={{ fontSize: 12, color: 'var(--lg-text-3)' }}>
          {loading ? 'Завантаження…' : `${total} товарів з невалідною прив'язкою`}
        </span>
        <button className="rt-btn rt-btn-sm" disabled={loading} onClick={() => load(0)}>Оновити</button>
      </div>

      {!loading && items.length === 0 && (
        <div className="rt-empty">Зламаних прив'язок немає 🎉</div>
      )}

      {items.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="rt-table" style={{ minWidth: 580 }}>
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Артикул сайту</th>
                <th>Назва</th>
                <th>all_sku / виправлення</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {items.map(p => (
                <BrokenBindingsRow
                  key={p.id}
                  site={site}
                  product={p}
                  onFixed={handleFixed}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {items.length < total && (
        <button
          className="rt-btn rt-btn-sm"
          style={{ marginTop: 10 }}
          disabled={loading}
          onClick={() => load(offset + LIMIT)}
        >Завантажити ще ({total - items.length})</button>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Швидка пряма прив'язка ПРЯМО з вікна редагування товару сайту — товар постачальника
// (без ALL SKU) → цей самий article_site, в обхід каталогу. Для одиничних товарів
// (уцінка/шлюб і т.п.), для яких звичайна прив'язка через ALL SKU не підходить.
// ─────────────────────────────────────────────────────────────────────────────
function DirectLinkQuickAdd({ site, articleSite, onLinked }) {
  const [open,       setOpen]       = useState(false);
  const [query,      setQuery]      = useState('');
  const [results,    setResults]    = useState([]);
  const [searching,  setSearching]  = useState(false);
  const [picked,     setPicked]     = useState(null);
  const [saving,     setSaving]     = useState(false);
  const timerRef = useRef(null);

  function handleSearch(val) {
    setQuery(val);
    setPicked(null);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (val.trim().length < 3) { setResults([]); return; }
    timerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await retailAPI.searchSupplierItems(val.trim());
        setResults(r.data || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 350);
  }

  async function handleSave() {
    if (!picked || !articleSite) return;
    setSaving(true);
    try {
      await retailAPI.createDirectLink(site.id, {
        article_site: articleSite,
        supplier_item_id: picked.id,
      });
      setOpen(false); setQuery(''); setResults([]); setPicked(null);
      if (onLinked) onLinked();
    } catch { alert('Помилка збереження прив\'язки'); }
    finally { setSaving(false); }
  }

  if (!open) {
    return (
      <button className="rt-btn rt-btn-xs" style={{ marginTop: 6 }} onClick={() => setOpen(true)}>
        🔗 Прив'язати напряму до товару постачальника
      </button>
    );
  }

  return (
    <div style={{ marginTop: 8, padding: 8, border: '1px solid var(--lg-border)', borderRadius: 6 }}>
      <div className="rt-hint" style={{ marginBottom: 6 }}>
        Наявність/ціна артикулу <strong>{articleSite}</strong> відтепер рахуватимуться
        напряму з ОДНОГО обраного товару постачальника, в обхід ALL SKU.
      </div>
      <input className="rt-input" placeholder="Пошук товару постачальника (частина артикулу/назви)…"
        value={query} onChange={e => handleSearch(e.target.value)} autoFocus
        style={{ marginBottom: 6 }}
      />
      {searching && <div className="rt-hint">Пошук…</div>}
      {!searching && results.length > 0 && !picked && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 180, overflowY: 'auto',
                      border: '1px solid var(--lg-border)', borderRadius: 6, marginBottom: 6 }}>
          {results.map(r => (
            <button key={r.id} onClick={() => setPicked(r)}
              style={{ textAlign: 'left', padding: '6px 8px', fontSize: 12, background: 'none', border: 'none',
                        borderBottom: '1px solid var(--lg-border)', cursor: 'pointer', color: 'var(--lg-text)' }}>
              <div style={{ fontWeight: 500 }}>{r.supplier_code}</div>
              <div style={{ opacity: 0.7 }}>
                {r.supplier_name} · {r.price ? `${r.price}$` : '—'} · {r.sizes || 'без розмірів'}
                {r.product_sku ? ` · SKU: ${r.product_sku}` : ''}
              </div>
            </button>
          ))}
        </div>
      )}
      {picked && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 6,
                      padding: '6px 8px', background: 'var(--lg-hover, rgba(0,0,0,.04))', borderRadius: 6 }}>
          <span style={{ flex: 1 }}>✓ {picked.supplier_code} ({picked.supplier_name})</span>
          <button onClick={() => { setPicked(null); setQuery(''); setResults([]); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--rt-danger, #c0392b)' }}>✕</button>
        </div>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <button className="rt-btn rt-btn-sm rt-btn-primary" disabled={!picked || saving} onClick={handleSave}>
          {saving ? 'Збереження…' : '+ Прив\'язати'}
        </button>
        <button className="rt-btn rt-btn-sm" onClick={() => setOpen(false)}>Скасувати</button>
      </div>
    </div>
  );
}


function SiteProductsTab({ site }) {
  const selectedSiteId = site.id;
  const [siteTab,    setSiteTab]    = useState('products'); // 'products' | 'empty-stock' | 'broken-bindings'
  const [products,   setProducts]   = useState([]);
  const [total,      setTotal]      = useState(0);
  const [loading,    setLoading]    = useState(false);
  const [syncing,    setSyncing]    = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [search,        setSearch]        = useState('');
  const [linked,        setLinked]        = useState(null);
  const [ignored,       setIgnored]       = useState(false);   // true = вкладка "Приховані"
  const [ignoredCount,  setIgnoredCount]  = useState(0);       // бейдж на вкладці
  const [offset,        setOffset]        = useState(0);
  // modal state
  const [editProduct,     setEditProduct]     = useState(null);
  const [editAllSku,      setEditAllSku]      = useState('');
  const [editTitleUa,     setEditTitleUa]     = useState('');
  const [editTitleRu,     setEditTitleRu]     = useState('');
  const [editPrice,       setEditPrice]       = useState('');
  const [editAvailable,   setEditAvailable]   = useState(true);
  const [editSaving,      setEditSaving]      = useState(false);
  const [pushing,         setPushing]         = useState(false);
  const [pushResult,      setPushResult]      = useState(null);
  const [editError,       setEditError]       = useState('');
  const [variants,        setVariants]        = useState([]);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [bulkLinkOpen,   setBulkLinkOpen]   = useState(false);
  const [bulkLinkText,   setBulkLinkText]   = useState('');
  const [bulkLinkResult, setBulkLinkResult] = useState(null);
  const [bulkLinkLoading, setBulkLinkLoading] = useState(false);
  const LIMIT = 50;

  const loadStatus = useCallback(async (siteId) => {
    if (!siteId) return;
    try {
      const r = await retailAPI.getSyncStatus(siteId);
      setSyncStatus(r.data);
    } catch {}
  }, []);

  const loadProducts = useCallback(async (siteId, opts = {}) => {
    if (!siteId) return;
    setLoading(true);
    setLoadError(false);
    const { q = search, lnk = linked, ign = ignored, off = 0 } = opts;
    try {
      const params = { q, offset: off, limit: LIMIT, section: 'shoes' };
      if (ign) {
        params.ignored = true;   // вкладка "Приховані"
      } else {
        params.ignored = false;  // звичайний список
        if (lnk === true)  params.linked = true;
        if (lnk === false) params.linked = false;
      }
      const r = await retailAPI.getSiteProducts(siteId, params);
      setProducts(r.data.items);
      setTotal(r.data.total);
      setOffset(off);
      // Оновлюємо лічильник прихованих (тихо)
      if (!ign) {
        retailAPI.getSiteProducts(siteId, { ignored: true, limit: 1, section: 'shoes' })
          .then(rr => setIgnoredCount(rr.data.total))
          .catch(() => {});
      }
    } catch { setLoadError(true); }
    finally { setLoading(false); }
  }, [search, linked, ignored]);

  useEffect(() => {
    loadStatus(selectedSiteId);
    loadProducts(selectedSiteId, { q: '', lnk: null, off: 0 });
    setSearch('');
    setLinked(null);
    setOffset(0);
  }, [selectedSiteId]); // eslint-disable-line

  // Polling статуса пока идёт синхронизация
  useEffect(() => {
    if (!syncStatus?.running && !syncing) return;
    const id = setInterval(async () => {
      const r = await retailAPI.getSyncStatus(selectedSiteId).catch(() => null);
      if (r) {
        setSyncStatus(r.data);
        if (!r.data.running) {
          setSyncing(false);
          loadProducts(selectedSiteId, { q: search, lnk: linked, off: offset });
        }
      }
    }, 3000);
    return () => clearInterval(id);
  }, [syncStatus?.running, syncing, selectedSiteId]); // eslint-disable-line

  async function handleSync(resume = false) {
    if (!selectedSiteId) return;
    setSyncing(true);
    try {
      await retailAPI.syncProducts(selectedSiteId, resume);
      setSyncStatus(s => ({ ...(s || {}), running: true, progress_page: 0, progress_total: 0, progress_upserted: 0 }));
    } catch (e) {
      setSyncing(false);
      alert(e?.response?.data?.detail || 'Помилка запуску синхронізації');
    }
  }

  async function handleFullSync() {
    if (!selectedSiteId) return;
    setSyncing(true);
    try {
      await retailAPI.syncProductsFull(selectedSiteId);
      setSyncStatus(s => ({ ...(s || {}), running: true, progress_page: 0, progress_total: 0, progress_upserted: 0 }));
    } catch (e) {
      setSyncing(false);
      alert(e?.response?.data?.detail || 'Помилка запуску синхронізації');
    }
  }

  async function handleImportAll() {
    if (!selectedSiteId) return;
    const isResume = syncStatus?.import_all_resumable;
    const msg = isResume
      ? 'Продовжити завантаження всіх товарів з місця зупинки?\n\nРозміри заповнюватимуться поступово при наступних Авто-синках.'
      : 'Запустити отримання всіх товарів без розмірів?\n\nДля великих сайтів це може тривати кілька годин у фоні.\nРозміри заповнюватимуться поступово при наступних Авто-синках.';
    if (!window.confirm(msg)) return;
    setSyncing(true);
    try {
      const r = await retailAPI.importAllNoGql(selectedSiteId);
      const data = r.data || {};
      if (data.status === 'already_running') {
        setSyncStatus(s => ({ ...(s || {}), running: true }));
        return;
      }
      setSyncStatus(s => ({ ...(s || {}), running: true, progress_page: 0, progress_upserted: 0, sync_mode: 'import_all_no_gql' }));
    } catch (e) {
      setSyncing(false);
      alert('Помилка запуску: ' + (e?.response?.data?.detail || e?.message || e));
    }
  }

  async function handleFillSizes() {
    if (!selectedSiteId) return;
    if (!window.confirm('Запустити GQL-запити розмірів для всіх варіантів де розмір ще не отримано?\n\nМоже тривати довго для великих сайтів.')) return;
    setSyncing(true);
    try {
      const r = await retailAPI.fillPromSizes(selectedSiteId);
      const data = r.data || {};
      if (data.status === 'already_running') {
        setSyncStatus(s => ({ ...(s || {}), running: true }));
        return;
      }
      setSyncStatus(s => ({ ...(s || {}), running: true, progress_phase: 'sizes', progress_size_done: 0, progress_size_total: data.pending || 0 }));
    } catch (e) {
      setSyncing(false);
      alert('Помилка: ' + (e?.response?.data?.detail || e?.message || e));
    }
  }

  async function handleRecheckDuplicateSizes() {
    if (!selectedSiteId) return;
    if (!window.confirm('Перевірити товари з підозрілими дублікатами розмірів (різні розміри, один з яких повторюється)?\n\nОднорозмірні товари (кілька кольорів одного розміру) не чіпаємо.')) return;
    setSyncing(true);
    try {
      const r = await retailAPI.recheckDuplicateSizes(selectedSiteId);
      const data = r.data || {};
      if (data.status === 'already_running') {
        setSyncStatus(s => ({ ...(s || {}), running: true }));
        return;
      }
      if (data.status === 'done' && !data.pending) {
        setSyncing(false);
        alert('Підозрілих дублікатів не знайдено — все гаразд!');
        return;
      }
      setSyncStatus(s => ({ ...(s || {}), running: true, progress_phase: 'sizes', progress_size_done: 0, progress_size_total: data.pending || 0 }));
    } catch (e) {
      setSyncing(false);
      alert('Помилка: ' + (e?.response?.data?.detail || e?.message || e));
    }
  }

  async function handleStopSync() {
    try { await retailAPI.stopSync(selectedSiteId); } catch {}
  }

  function handleSearch(val) {
    setSearch(val);
    loadProducts(selectedSiteId, { q: val, lnk: linked, off: 0 });
  }

  function handleLinkedFilter(val) {
    setLinked(val);
    setIgnored(false);
    loadProducts(selectedSiteId, { q: search, lnk: val, ign: false, off: 0 });
  }

  function handleIgnoredFilter(val) {
    setIgnored(val);
    if (val) {
      setLinked(null);
      loadProducts(selectedSiteId, { q: search, ign: true, off: 0 });
    } else {
      loadProducts(selectedSiteId, { q: search, lnk: linked, ign: false, off: 0 });
    }
  }

  async function toggleIgnore(sp, ignoreVal) {
    try {
      await retailAPI.updateSiteProduct(site.id, sp.id, { is_ignored: ignoreVal });
      setProducts(prev => prev.filter(p => p.id !== sp.id));
      setTotal(t => Math.max(0, t - 1));
      setIgnoredCount(c => ignoreVal ? c + 1 : Math.max(0, c - 1));
    } catch (e) {
      alert('Помилка: ' + (e?.response?.data?.detail || String(e)));
    }
  }

  async function togglePriceManual(sp) {
    const newVal = !sp.price_manual;
    try {
      await retailAPI.updateSiteProduct(site.id, sp.id, { price_manual: newVal });
      setProducts(prev => prev.map(p => p.id === sp.id ? { ...p, price_manual: newVal } : p));
    } catch (e) {
      alert('Помилка: ' + (e?.response?.data?.detail || String(e)));
    }
  }

  function handlePage(dir) {
    const newOff = Math.max(0, offset + dir * LIMIT);
    loadProducts(selectedSiteId, { q: search, lnk: linked, ign: ignored, off: newOff });
  }

  async function handleBulkLink() {
    if (!bulkLinkText.trim()) return;
    setBulkLinkLoading(true);
    setBulkLinkResult(null);
    try {
      const r = await retailAPI.bulkLink(selectedSiteId, bulkLinkText);
      setBulkLinkResult(r.data);
      // Після успішної масової привязки переключаємось на «Прив'язані» щоб одразу бачити результат
      if ((r.data?.updated || 0) > 0) {
        setLinked(true);
        setIgnored(false);
        loadProducts(selectedSiteId, { q: search, lnk: true, ign: false, off: 0 });
      } else {
        loadProducts(selectedSiteId, { q: search, lnk: linked, off: 0 });
      }
    } catch (e) {
      setBulkLinkResult({ error: e?.response?.data?.detail || 'Помилка' });
    } finally {
      setBulkLinkLoading(false);
    }
  }

  async function handleClearAndSync() {
    if (!selectedSiteId) return;
    if (!window.confirm('Видалити всі товари сайту з бази та запустити синхронізацію заново?\n\nПрив\'язки all_sku та ручні зміни будуть ВТРАЧЕНІ.')) return;
    try {
      await retailAPI.clearProducts(selectedSiteId);
      setProducts([]);
      setTotal(0);
      setSyncStatus(s => ({ ...(s || {}), last_sync_at: null, last_sync_count: null, last_error: null }));
      await handleSync(false);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Помилка очищення');
    }
  }

  const _authUser = useAuthStore((state) => state.user);
  const _isRealAdmin = !!(_authUser?.is_admin && !_authUser?.impersonated_by);
  const _hasSupplierSuggest = !!(_authUser?.permissions?.includes('supplier_suggest'));

  // Розширена інфо про товар (виробник, характеристики) — збережена у localStorage
  const [showChars, setShowChars] = useState(() => {
    try { return localStorage.getItem('rt_show_chars') === '1'; } catch { return false; }
  });
  function toggleShowChars() {
    setShowChars(v => {
      const next = !v;
      try { localStorage.setItem('rt_show_chars', next ? '1' : '0'); } catch {}
      return next;
    });
  }

  // Suggestions (підбір постачальника)
  const [suggestions,        setSuggestions]        = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestPreview,     setSuggestPreview]     = useState(null); // {photo, product_sku, supplier_code, supplier_name}
  // Lightbox (повний перегляд фото)
  const [lightboxUrl, setLightboxUrl] = useState(null);

  async function openEdit(sp) {
    setEditProduct(sp);
    setEditAllSku(sp.all_sku || '');
    setEditTitleUa(sp.title_ua || '');
    setEditTitleRu(sp.title_ru || '');
    setEditPrice(sp.price != null ? String(sp.price) : '');
    setEditAvailable(sp.available !== false);
    setEditError('');
    setPushResult(null);
    setSuggestions([]);
    setVariants([]);
    setVariantsLoading(true);
    try {
      const r = await retailAPI.getSiteProductVariants(selectedSiteId, sp.id, { available_only: false });
      setVariants(r.data);
    } catch {}
    finally { setVariantsLoading(false); }
    // Авто-підбір постачальника якщо є право і артикул
    if (_hasSupplierSuggest && sp.article_site) {
      setSuggestionsLoading(true);
      try {
        const r = await retailAPI.supplierSuggestions(sp.article_site);
        setSuggestions(r.data || []);
      } catch {}
      finally { setSuggestionsLoading(false); }
    }
  }

  function closeEdit() {
    setEditProduct(null);
    setPushResult(null);
    setEditError('');
    setSuggestions([]);
  }

  async function saveLocal() {
    if (!editProduct) return;
    if (!isValidAllSku(editAllSku)) {
      setEditError('Невірний формат ALL SKU. Допустимо: ALL00000, SM00000, ALL00000Назва, SM00000Назва');
      return;
    }
    setEditSaving(true); setEditError('');
    try {
      const r = await retailAPI.updateSiteProduct(selectedSiteId, editProduct.id, {
        // editAllSku як є (навіть "") — бекенд відрізняє "не чіпали" від "очистили навмисно"
        all_sku:   editAllSku.trim(),
        title_ua:  editTitleUa,
        title_ru:  editTitleRu,
        price:     editPrice !== '' ? parseFloat(editPrice) : null,
        available: editAvailable,
      });
      // Якщо фільтр "Без прив'язки" і тепер є артикул — видаляємо зі списку
      if (linked === false && r.data.all_sku) {
        setProducts(prev => prev.filter(p => p.id !== r.data.id));
      } else {
        setProducts(prev => prev.map(p => p.id === r.data.id ? r.data : p));
      }
      closeEdit();
    } catch (e) {
      setEditError(e?.response?.data?.detail || 'Помилка збереження');
    } finally { setEditSaving(false); }
  }

  async function saveAndPush() {
    if (!editProduct) return;
    if (!isValidAllSku(editAllSku)) {
      setEditError('Невірний формат ALL SKU. Допустимо: ALL00000, SM00000, ALL00000Назва, SM00000Назва');
      return;
    }
    setPushing(true); setPushResult(null); setEditError('');
    try {
      // Save to DB first
      const saved = await retailAPI.updateSiteProduct(selectedSiteId, editProduct.id, {
        all_sku:   editAllSku.trim(),
        title_ua:  editTitleUa,
        title_ru:  editTitleRu,
        price:     editPrice !== '' ? parseFloat(editPrice) : null,
        available: editAvailable,
      });
      setProducts(prev => prev.map(p => p.id === saved.data.id ? saved.data : p));
      setEditProduct(saved.data);
      // Push to site
      const r = await retailAPI.pushProductToSite(selectedSiteId, editProduct.id);
      setPushResult({ ok: true, text: `✓ Відправлено на сайт (${r.data.sent} позицій)` });
    } catch (e) {
      setPushResult({ ok: false, text: e?.response?.data?.detail || 'Помилка відправки' });
    } finally { setPushing(false); }
  }

  return (
    <div>
      {/* Шапка с кнопкой синхронизации */}
      <div className="rt-section-head">
        <div>
          <div className="rt-section-title">
            Товари сайту
            {total > 0 && <span className="rt-tab-badge" style={{ marginLeft: 8 }}>{total}</span>}
          </div>
          {syncStatus && _isRealAdmin && (
            <div className="rt-hint" style={{ marginTop: 4 }}>
              {syncStatus.running ? (
                syncStatus.progress_size_done != null
                  ? `⏳ Розміри: ${syncStatus.progress_size_done} / ${syncStatus.progress_size_total ?? '?'} груп`
                  : syncStatus.sync_mode === 'import_all_no_gql' && syncStatus.progress_page > 0
                    ? `📥 Сторінка ${syncStatus.progress_page} · зібрано ${syncStatus.progress_variants ?? 0} варіантів`
                    : syncStatus.progress_page > 0
                      ? `⏳ Сторінка ${syncStatus.progress_page}${
                          syncStatus.horoshop_total_pages
                            ? ` / ~${syncStatus.horoshop_total_pages}`
                            : ''
                        } · збережено ${syncStatus.progress_upserted} тов.`
                      : '⏳ Підключення до сайту…'
              ) : syncStatus.last_sync_at
                ? `Оновлено: ${fmtDate(syncStatus.last_sync_at)} · ${syncStatus.last_sync_count || 0} товарів`
                : 'Ще не синхронізовано'
              }
              {!syncStatus.running && syncStatus.last_error && (
                <span style={{ color: 'var(--rt-danger)', marginLeft: 8 }}>⚠ {syncStatus.last_error}</span>
              )}
              {site.platform === 'prom' && !syncStatus.running && (
                <span style={{ marginLeft: 8, opacity: 0.7 }}>
                  {syncStatus.last_full_sync_at
                    ? `· Повний скан: ${fmtDate(syncStatus.last_full_sync_at)}`
                    : syncStatus.last_sync_offset > 0
                      ? `· Повний скан: в процесі (сторінка ~${Math.round(syncStatus.last_sync_offset / 100)})`
                      : '· Повний скан: не виконувався'
                  }
                </span>
              )}
              {site.platform !== 'prom' && !syncStatus.running && syncStatus.horoshop_catalog_end != null && (
                <span style={{ marginLeft: 8, opacity: 0.7 }}>
                  {`· Каталог: ${syncStatus.horoshop_catalog_end} тов. (~${syncStatus.horoshop_total_pages} стор.)`}
                </span>
              )}
              {site.platform !== 'prom' && !syncStatus.running && syncStatus.horoshop_catalog_end == null && syncStatus.last_sync_offset > 0 && (
                <span style={{ marginLeft: 8, opacity: 0.7 }}>
                  {`· Каталог: не проскановано до кінця (зупинено на стор. ~${Math.ceil(syncStatus.last_sync_offset / 500)})`}
                </span>
              )}
            </div>
          )}
        </div>
        {_isRealAdmin && (
        <div style={{ display: 'flex', gap: 8 }}>
          {(syncing || syncStatus?.running) && (
            <button className="rt-btn rt-btn-danger rt-btn-sm" onClick={handleStopSync}>
              ✕ Зупинити
            </button>
          )}
          {site.platform !== 'prom' && !syncing && !syncStatus?.running && (syncStatus?.last_sync_offset > 0) && (
            <button className="rt-btn rt-btn-sm"
              onClick={() => handleSync(true)}
              disabled={!site.token_set}
              title={`Продовжити з offset ${syncStatus.last_sync_offset} (стор. ~${Math.ceil(syncStatus.last_sync_offset / 500) + 1}${syncStatus.horoshop_total_pages ? ` з ${syncStatus.horoshop_total_pages}` : ''})`}
            >
              ▶ Продовжити
            </button>
          )}
          {_isRealAdmin && site.platform === 'prom' && !syncing && !syncStatus?.running && (
            <button className="rt-btn rt-btn-sm rt-btn-danger"
              onClick={handleClearAndSync}
              disabled={!site.token_set}
              title="Видалити всі товари з БД і запустити синхронізацію заново"
            >
              ⚠ Очистити і переcинхронізувати
            </button>
          )}
          {site.platform === 'prom' && !syncing && !syncStatus?.running && (
            <button className="rt-btn rt-btn-sm"
              onClick={handleFullSync}
              disabled={!site.token_set}
              title="Примусово запустити повний прохід по всіх товарах з початку (скидає поточний прогрес)"
            >▶ Повний прохід</button>
          )}
          {site.platform === 'prom' && !syncing && !syncStatus?.running && (
            <button className="rt-btn rt-btn-sm rt-btn-auto"
              onClick={() => handleSync(false)}
              disabled={!site.token_set}
              title="Те саме що авто-синк за розкладом: нові/змінені товари. Якщо перший запуск — повний прохід по N сторінок."
            >⟳ Авто-синк</button>
          )}
          {site.platform === 'prom' && !syncing && !syncStatus?.running && (
            <button className="rt-btn rt-btn-sm"
              onClick={handleImportAll}
              disabled={!site.token_set}
              title={syncStatus?.import_all_resumable
                ? `Продовжити завантаження з місця зупинки (вже завантажено ${syncStatus.total_products} тов.)`
                : 'Отримати всі товари без розмірів (для великих сайтів). Розміри заповнюються поступово Авто-синком.'}
              style={{ fontSize: 11 }}
            >{syncStatus?.import_all_resumable ? '📥 Продовжити' : '📥 Всі товари'}</button>
          )}
          {_isRealAdmin && site.platform === 'prom' && !syncing && !syncStatus?.running && (
            <button className="rt-btn rt-btn-sm"
              onClick={handleFillSizes}
              disabled={!site.token_set}
              title="Запустити GQL-запити розмірів для варіантів де розмір ще не отримано (size_fetched=False)"
              style={{ fontSize: 11 }}
            >📐 Отримати розміри</button>
          )}
          {_isRealAdmin && site.platform === 'prom' && !syncing && !syncStatus?.running && (
            <button className="rt-btn rt-btn-sm"
              onClick={handleRecheckDuplicateSizes}
              disabled={!site.token_set}
              title="Перезапитати GQL для товарів з підозрілими дублікатами розмірів (різні розміри, один повторюється) — однорозмірні товари не чіпає"
              style={{ fontSize: 11 }}
            >🔁 Перевірити дублікати</button>
          )}
          <button className="rt-btn rt-btn-primary"
            onClick={() => handleSync(false)}
            disabled={syncing || syncStatus?.running || !site.token_set}
            title={!site.token_set ? 'Спочатку налаштуйте підключення до сайту' : ''}
          >
            {syncing || syncStatus?.running ? '⏳ Синхронізація…' : '↻ Спочатку'}
          </button>
        </div>
        )}
      </div>

      {/* Головні вкладки розділу */}
      <div style={{ display: 'flex', gap: 4, margin: '10px 0 14px', borderBottom: '1px solid var(--lg-border)', paddingBottom: 0 }}>
        {[
          { id: 'products',          label: 'Товари' },
          { id: 'empty-stock',       label: '📦 Пуста наявність' },
          { id: 'broken-bindings',   label: '⚠️ Прив\'язки з помилкою' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setSiteTab(t.id)}
            style={{
              padding: '5px 14px', fontSize: 12,
              fontWeight: siteTab === t.id ? 600 : 400,
              color: siteTab === t.id ? 'var(--lg-text)' : 'var(--lg-text-2)',
              background: 'transparent', border: 'none',
              borderBottom: siteTab === t.id ? '2px solid var(--rt-accent,#6366f1)' : '2px solid transparent',
              cursor: 'pointer', marginBottom: -1,
            }}
          >{t.label}</button>
        ))}
      </div>

      {siteTab === 'empty-stock'     && <EmptyStockTab      site={site} />}
      {siteTab === 'broken-bindings' && <BrokenBindingsTab  site={site} />}

      {siteTab === 'products' && <>

      {/* Фильтры */}
      <div className="rt-filters-row">
        <input className="rt-input rt-search" placeholder="Пошук за артикулом або назвою…"
          value={search} onChange={e => handleSearch(e.target.value)} />
        <div className="rt-filter-btns">
          <button className={`rt-btn rt-btn-sm${!ignored && linked === null ? ' rt-btn-primary' : ''}`} onClick={() => handleLinkedFilter(null)}>Всі</button>
          <button className={`rt-btn rt-btn-sm${!ignored && linked === false ? ' rt-btn-primary' : ''}`} onClick={() => handleLinkedFilter(false)}>Без прив'язки</button>
          <button className={`rt-btn rt-btn-sm${!ignored && linked === true ? ' rt-btn-primary' : ''}`} onClick={() => handleLinkedFilter(true)}>Прив'язані</button>
          <button
            className={`rt-btn rt-btn-sm${ignored ? ' rt-btn-primary' : ''}`}
            onClick={() => handleIgnoredFilter(!ignored)}
            style={{ position: 'relative' }}
          >
            Приховані
            {ignoredCount > 0 && (
              <span style={{ marginLeft: 5, background: 'var(--lg-accent,#3b82f6)', color: '#fff', borderRadius: 8, padding: '0 5px', fontSize: 10, fontWeight: 700 }}>
                {ignoredCount}
              </span>
            )}
          </button>
          <button className="rt-btn rt-btn-sm" onClick={() => { setBulkLinkOpen(true); setBulkLinkResult(null); }}>
            Масова привязка
          </button>
        </div>
      </div>

      {/* Модал масової привязки */}
      {bulkLinkOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={e => { if (e.target === e.currentTarget) setBulkLinkOpen(false); }}>
          <div style={{
            background: 'var(--bg-card, #fff)', borderRadius: 12, padding: 28, width: 560, maxWidth: '95vw',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 10 }}>Масова привязка ALL_SKU → артикул сайту</div>
            <div style={{ fontSize: 12, color: 'var(--muted, #888)', marginBottom: 12 }}>
              Вставте список у форматі <code>ALL12345&nbsp;&nbsp;xx-12345</code> — по одному рядку. Колонки розділяйте табуляцією або пробілами.
            </div>
            <textarea
              rows={14}
              style={{ width: '100%', fontFamily: 'monospace', fontSize: 12, padding: 8, borderRadius: 6, border: '1px solid var(--border, #d1d5db)', resize: 'vertical', boxSizing: 'border-box' }}
              value={bulkLinkText}
              onChange={e => setBulkLinkText(e.target.value)}
              placeholder={'ALL17710\tac-17710\nALL11299\tad-11299\n…'}
            />
            {bulkLinkResult && (
              <div style={{ marginTop: 10, fontSize: 13, color: bulkLinkResult.error ? '#ef4444' : '#16a34a' }}>
                {bulkLinkResult.error
                  ? `Помилка: ${bulkLinkResult.error}`
                  : `Оновлено: ${bulkLinkResult.updated}, не знайдено: ${bulkLinkResult.not_found}, пропущено: ${bulkLinkResult.skipped}`
                    + (bulkLinkResult.invalid_sku > 0
                      ? `, невірний формат: ${bulkLinkResult.invalid_sku}` +
                        (bulkLinkResult.invalid_sku_examples?.length
                          ? ` (${bulkLinkResult.invalid_sku_examples.join(', ')})`
                          : '')
                      : '')
                    + (bulkLinkResult.not_found_examples?.length
                      ? `, не знайдено приклади: ${bulkLinkResult.not_found_examples.join(', ')}`
                      : '')
                }
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'flex-end' }}>
              <button className="rt-btn" onClick={() => setBulkLinkOpen(false)}>Закрити</button>
              <button className="rt-btn rt-btn-primary" disabled={bulkLinkLoading || !bulkLinkText.trim()} onClick={handleBulkLink}>
                {bulkLinkLoading ? 'Застосування…' : 'Застосувати'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Таблица */}
      {loading ? (
        <div className="rt-empty">Завантаження…</div>
      ) : loadError ? (
        <div className="rt-empty">
          Помилка завантаження.{' '}
          <button className="rt-btn rt-btn-sm" onClick={() => loadProducts(selectedSiteId)}>Повторити</button>
        </div>
      ) : products.length === 0 ? (
        <div className="rt-empty">
          {total === 0 ? 'Товарів немає — натисніть «Завантажити з сайту»' : 'Нічого не знайдено'}
        </div>
      ) : (
        <div className="rt-tbl-wrap" style={{ marginTop: 12 }}>
          <table className="rt-table rt-table-products">
            <thead>
              <tr>
                <th style={{ width: 48 }}></th>
                <th>Артикул сайту</th>
                <th>Назва</th>
                <th>Розміри</th>
                <th>Ціна ₴</th>
                <th>Наявн.</th>
                <th>ALL SKU</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {products.map(sp => (
                <tr key={sp.id} className={sp.all_sku ? 'rt-row-linked' : 'rt-row-unlinked'} style={{ cursor: 'pointer' }} onClick={() => openEdit(sp)}>
                  <td style={{ padding: '4px 6px' }}>
                    {sp.thumb
                      ? <img src={sp.thumb} alt="" className="rt-prod-thumb" />
                      : <div className="rt-prod-thumb rt-prod-thumb--empty" />
                    }
                  </td>
                  <td className="rt-cell-article">
                    <div>{sp.article_site || '—'}</div>
                    {sp.prom_id && (
                      <a
                        href={promProductUrl(site.domain, sp.prom_id)}
                        target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: 10, color: 'var(--muted,#888)', marginTop: 1, display: 'block', textDecoration: 'none' }}
                        onMouseEnter={e => e.currentTarget.style.textDecoration='underline'}
                        onMouseLeave={e => e.currentTarget.style.textDecoration='none'}
                        title={`Відкрити на сайті: ${promProductUrl(site.domain, sp.prom_id)}`}
                      >🔗 {sp.prom_id}</a>
                    )}
                    {sp.site_link && (
                      <a
                        href={sp.site_link.match(/^https?:\/\//) ? sp.site_link : `https://${sp.site_link}`}
                        target="_blank" rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{ fontSize: 10, color: 'var(--muted,#888)', marginTop: 1, display: 'block', textDecoration: 'none' }}
                        onMouseEnter={e => e.currentTarget.style.textDecoration='underline'}
                        onMouseLeave={e => e.currentTarget.style.textDecoration='none'}
                        title={`Відкрити на сайті: ${sp.site_link}`}
                      >🔗 на сайт</a>
                    )}
                  </td>
                  <td>{sp.title_ua || sp.title_ru || '—'}</td>
                  <td style={{ fontSize: 11, color: 'var(--muted,#888)' }}>
                    {sp.base_size && (
                      <span style={{
                        display: 'inline-block', marginRight: 4,
                        background: '#f59e0b', color: '#fff',
                        borderRadius: 3, padding: '0 4px', fontSize: 10, fontWeight: 700,
                        verticalAlign: 'middle',
                      }}>{sp.base_size}</span>
                    )}
                    {sp.sizes && sp.sizes.length > 0 ? sp.sizes.join(' ') : (sp.size || (!sp.base_size ? '—' : ''))}
                  </td>
                  <td>{sp.price ? `₴${sp.price.toFixed(0)}` : '—'}</td>
                  <td>
                    <span className={`rt-avail-dot${sp.available ? ' rt-avail-dot--on' : ''}`} />
                  </td>
                  <td className={sp.all_sku ? 'rt-cell-linked' : 'rt-cell-nolink'}>
                    {sp.all_sku || '—'}
                  </td>
                  <td onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    {ignored ? (
                      <button className="rt-btn rt-btn-xs" title="Відновити в основний список"
                        onClick={() => toggleIgnore(sp, false)}>↩ Відновити</button>
                    ) : (
                      <>
                        <button className="rt-btn rt-btn-xs" onClick={() => openEdit(sp)}>✎</button>
                        <button
                          className="rt-btn rt-btn-xs"
                          title={sp.price_manual ? 'Ручна ціна ввімкнена (автоматика не чіпає)' : 'Увімкнути ручну ціну'}
                          style={{
                            background: sp.price_manual ? 'var(--rt-warn-bg,#92400e22)' : 'transparent',
                            color:  sp.price_manual ? 'var(--rt-warn,#d97706)' : 'var(--muted,#888)',
                            border: '1px solid ' + (sp.price_manual ? 'var(--rt-warn,#d97706)' : 'var(--border,#ccc)'),
                          }}
                          onClick={() => togglePriceManual(sp)}
                        >🔒</button>
                        {!sp.all_sku && (
                          <button className="rt-btn rt-btn-xs" title="Приховати — перемістити до вкладки «Приховані»"
                            style={{ opacity: 0.55 }}
                            onClick={() => toggleIgnore(sp, true)}>🙈</button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Пагинация */}
      {total > LIMIT && (
        <div className="rt-pagination">
          <button className="rt-btn rt-btn-sm" disabled={offset === 0} onClick={() => handlePage(-1)}>← Назад</button>
          <span className="rt-hint">{offset + 1}–{Math.min(offset + LIMIT, total)} з {total}</span>
          <button className="rt-btn rt-btn-sm" disabled={offset + LIMIT >= total} onClick={() => handlePage(1)}>Далі →</button>
        </div>
      )}

      {/* Модалка: деталі товару + редагування */}
      {editProduct && (
        <div className="rt-overlay" onClick={e => e.target === e.currentTarget && closeEdit()}>
          <div className="rt-modal rt-modal--product">
            <div className="rt-modal-head">
              <span className="rt-modal-title">
                <span className="rt-cell-article" style={{ marginRight: 8 }}>{editProduct.article_site}</span>
                {editProduct.title_ua || editProduct.title_ru || ''}
              </span>
              <button className="rt-icon-btn" onClick={closeEdit}>✕</button>
            </div>
            <div className="rt-modal-body rt-modal-body--scroll">

              {/* Lightbox */}
              {lightboxUrl && (
                <div className="rt-lightbox" onClick={() => setLightboxUrl(null)}>
                  <button className="rt-lightbox-close" onClick={() => setLightboxUrl(null)}>✕</button>
                  <img src={lightboxUrl} alt="" className="rt-lightbox-img"
                    onClick={e => e.stopPropagation()} />
                </div>
              )}

              {/* Фото */}
              {editProduct.images?.length > 0 && (
                <div className="rt-product-gallery">
                  {editProduct.images.slice(0, 10).map((url, i) => (
                    <img
                      key={i}
                      src={url}
                      alt=""
                      className={`rt-gallery-thumb${i === 0 ? ' rt-gallery-thumb--main' : ''}`}
                      onClick={() => setLightboxUrl(url)}
                      onError={e => e.currentTarget.style.display = 'none'}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </div>
              )}

              {/* Виробник + характеристики — приховані за замовчуванням */}
              {(editProduct.producer || Object.keys(editProduct.chars || {}).length > 0) && (
                <>
                  <button
                    className="rt-chars-toggle-btn"
                    onClick={toggleShowChars}
                    type="button"
                  >
                    {showChars ? '▾' : '▸'} Характеристики товару
                  </button>
                  {showChars && (
                    <div className="rt-chars-block">
                      {editProduct.producer && (
                        <div className="rt-chars-row">
                          <span className="rt-chars-key">Виробник</span>
                          <span className="rt-chars-val">{editProduct.producer}</span>
                        </div>
                      )}
                      {Object.entries(editProduct.chars || {}).map(([k, v]) => (
                        <div key={k} className="rt-chars-row">
                          <span className="rt-chars-key">{k}</span>
                          <span className="rt-chars-val">{parseCharVal(v)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Редагування */}
              <div className="rt-chars-section-title">Редагування</div>
              <div className="rt-form-grid2">
                <div className="rt-field">
                  <label className="rt-label">Назва (UA)</label>
                  <input className="rt-input" value={editTitleUa}
                    onChange={e => setEditTitleUa(e.target.value)} placeholder="Назва українською" />
                </div>
                <div className="rt-field">
                  <label className="rt-label">Назва (RU)</label>
                  <input className="rt-input" value={editTitleRu}
                    onChange={e => setEditTitleRu(e.target.value)} placeholder="Название на русском" />
                </div>
                <div className="rt-field">
                  <label className="rt-label">Ціна ₴</label>
                  <input className="rt-input" type="number" min="0" value={editPrice}
                    onChange={e => setEditPrice(e.target.value)} placeholder="0" />
                </div>
                <div className="rt-field">
                  <label className="rt-label">Наявність</label>
                  <div className="rt-toggle-wrap" style={{ marginTop: 6 }}
                    onClick={() => setEditAvailable(v => !v)}>
                    <div className={`rt-toggle ${editAvailable ? 'rt-toggle--on' : ''}`}>
                      <div className="rt-toggle-knob" />
                    </div>
                    <span style={{ fontSize: 13 }}>{editAvailable ? 'В наявності' : 'Немає'}</span>
                  </div>
                </div>
              </div>

              {/* ALL SKU */}
              <div className="rt-field" style={{ marginTop: 4, marginBottom: 4 }}>
                <label className="rt-label">Прив'язка ALL SKU</label>
                <input className="rt-input" placeholder="ALL01234"
                  value={editAllSku} onChange={e => setEditAllSku(e.target.value)} />
                <div className="rt-hint">Залиште порожнім щоб прибрати прив'язку</div>
              </div>

              {/* Пряма прив'язка до одного товару постачальника (в обхід ALL SKU) */}
              {editProduct?.article_site && (
                <DirectLinkQuickAdd site={site} articleSite={editProduct.article_site} />
              )}

              {/* Підбір постачальника */}
              {_hasSupplierSuggest && (
                <div className="rt-suggestions-block">
                  {suggestionsLoading && (
                    <div className="rt-hint" style={{ marginBottom: 8 }}>🔍 Пошук відповідностей…</div>
                  )}
                  {!suggestionsLoading && suggestions.length > 0 && (
                    <>
                      <div className="rt-chars-section-title" style={{ marginBottom: 6 }}>
                        Можливі відповідності у постачальників
                      </div>
                      <div className="rt-suggest-grid">
                        {suggestions.map((s, i) => (
                          <div
                            key={i}
                            className={`rt-suggest-card${editAllSku === s.product_sku ? ' rt-suggest-card--selected' : ''}`}
                            onClick={() => setSuggestPreview(s)}
                            onDoubleClick={() => { setEditAllSku(s.product_sku); setSuggestPreview(null); }}
                            title="Одне натискання — перегляд\nПодвійне — вибір"
                          >
                            {s.photo
                              ? <img src={s.photo} alt="" className="rt-suggest-thumb"
                                  onError={e => e.currentTarget.style.display = 'none'} />
                              : <div className="rt-suggest-nophoto">📷</div>
                            }
                            <div className="rt-suggest-meta">
                              <div className="rt-suggest-sku">{s.product_sku}</div>
                              <div className="rt-suggest-code" title={s.supplier_code}>{s.supplier_code}</div>
                              <div className="rt-suggest-sup">{s.supplier_name}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {!suggestionsLoading && suggestions.length === 0 && editProduct?.article_site && (
                    <div className="rt-hint" style={{ marginBottom: 8, color: 'var(--text-secondary)' }}>
                      Відповідностей не знайдено
                    </div>
                  )}

                  {/* Overlay порівняння двох фото */}
                  {suggestPreview && (
                    <div className="rt-suggest-compare-overlay" onClick={() => setSuggestPreview(null)}>
                      <div className="rt-suggest-compare-box" onClick={e => e.stopPropagation()}>
                        <div className="rt-suggest-compare-photos">
                          <div className="rt-suggest-compare-side">
                            <div className="rt-suggest-compare-label">Товар на сайті</div>
                            {(editProduct?.thumb || editProduct?.images?.[0])
                              ? <img src={editProduct.thumb || editProduct.images[0]} alt="" className="rt-suggest-compare-img" />
                              : <div className="rt-suggest-nophoto" style={{ width: 280, height: 280 }}>📷</div>
                            }
                          </div>
                          <div className="rt-suggest-compare-side">
                            <div className="rt-suggest-compare-label">Постачальник</div>
                            {suggestPreview.photo
                              ? <img src={suggestPreview.photo} alt="" className="rt-suggest-compare-img" />
                              : <div className="rt-suggest-nophoto" style={{ width: 200, height: 200 }}>📷</div>
                            }
                          </div>
                        </div>
                        <div className="rt-suggest-compare-info">
                          <span className="rt-suggest-sku">{suggestPreview.product_sku}</span>
                          {suggestPreview.supplier_code && <span className="rt-suggest-code">{suggestPreview.supplier_code}</span>}
                          {suggestPreview.supplier_name && <span className="rt-suggest-sup">{suggestPreview.supplier_name}</span>}
                        </div>
                        <div className="rt-suggest-compare-actions">
                          <button className="rt-btn rt-btn-primary" onClick={() => { setEditAllSku(suggestPreview.product_sku); setSuggestPreview(null); }}>
                            ✓ Вибрати {suggestPreview.product_sku}
                          </button>
                          <button className="rt-btn" onClick={() => setSuggestPreview(null)}>Закрити</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Варіанти */}
              {variantsLoading ? (
                <div className="rt-hint" style={{ marginBottom: 10 }}>Завантаження варіантів…</div>
              ) : variants.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div className="rt-chars-section-title">Варіанти на сайті</div>
                  <div className="rt-tbl-wrap">
                    <table className="rt-table">
                      <thead>
                        <tr><th>ID</th><th>Артикул</th><th>Розмір</th><th>Ціна ₴</th><th>Наявн.</th><th>К-сть</th></tr>
                      </thead>
                      <tbody>
                        {/* Рядок основного товару */}
                        {editProduct?.prom_id && (
                          <tr style={{ background: 'rgba(245,158,11,0.08)' }}>
                            <td style={{ fontSize: 10, color: 'var(--muted,#888)', fontFamily: 'monospace' }}>
                              <a href={promProductUrl(site.domain, editProduct.prom_id)}
                                 target="_blank" rel="noopener noreferrer"
                                 style={{ color: 'inherit', textDecoration: 'none' }}
                                 onMouseEnter={e => e.currentTarget.style.textDecoration='underline'}
                                 onMouseLeave={e => e.currentTarget.style.textDecoration='none'}
                              >{editProduct.prom_id} 🔗</a>
                            </td>
                            <td style={{ fontSize: 11 }}>{editProduct.article_site || '—'}</td>
                            <td>
                              {editProduct.base_size
                                ? <span style={{ background: '#f59e0b', color: '#fff', borderRadius: 3, padding: '1px 5px', fontWeight: 700, fontSize: 12 }}>{editProduct.base_size}</span>
                                : '—'}
                              <span style={{ fontSize: 10, color: 'var(--muted,#888)', marginLeft: 6 }}>основний</span>
                            </td>
                            <td>{editProduct.price ? `₴${editProduct.price}` : '—'}</td>
                            <td><span className={`rt-avail-dot${editProduct.available ? ' rt-avail-dot--on' : ''}`} /></td>
                            <td>—</td>
                          </tr>
                        )}
                        {variants.map(v => (
                          <tr key={v.id}>
                            <td style={{ fontSize: 10, color: 'var(--muted,#888)', fontFamily: 'monospace' }}>{v.external_id || '—'}</td>
                            <td style={{ fontSize: 11 }}>{v.article || '—'}</td>
                            <td>{v.size || '—'}</td>
                            <td>{v.price ? `₴${v.price}` : '—'}</td>
                            <td><span className={`rt-avail-dot${v.available ? ' rt-avail-dot--on' : ''}`} /></td>
                            <td>{v.quantity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Результат відправки */}
              {pushResult && (
                <div className={`rt-test-result ${pushResult.ok ? 'rt-ok' : 'rt-err'}`}
                  style={{ marginBottom: 8 }}>
                  {pushResult.text}
                </div>
              )}
              {editError && <div className="rt-error" style={{ marginBottom: 8 }}>{editError}</div>}

              <div className="rt-modal-actions">
                <button className="rt-btn" onClick={closeEdit}>Скасувати</button>
                <div style={{ flex: 1 }} />
                <button className="rt-btn" onClick={saveLocal} disabled={editSaving || pushing}>
                  {editSaving ? 'Збереження…' : 'Зберегти'}
                </button>
                {_isRealAdmin && (
                  <button className="rt-btn rt-btn-primary" onClick={saveAndPush}
                    disabled={editSaving || pushing}>
                    {pushing ? '⏳ Відправка…' : '↑ Зберегти та відправити на сайт'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      </>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Вкладка "Аналіз" (отсутствующие товары и размеры)
// ─────────────────────────────────────────────────────────────────────────────

function ExcludeModal({ item, currentSiteId, onClose, onDone }) {
  const [excludeAll, setExcludeAll] = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [allSites,   setAllSites]   = useState([]);

  // Завантажуємо список доступних юзеру сайтів
  useEffect(() => {
    retailAPI.getSites().then(r => setAllSites(r.data || [])).catch(() => {});
  }, []);

  async function handleSave() {
    setSaving(true); setError('');
    try {
      if (excludeAll) {
        // "всі сайти" = тільки ті, до яких є доступ у поточного юзера
        const siteIds = allSites.map(s => s.id);
        await retailAPI.addExclude({ sku: item.sku, exclude_all: false, site_ids: siteIds, note: '' });
      } else {
        await retailAPI.addExclude({ sku: item.sku, exclude_all: false, site_ids: [currentSiteId], note: '' });
      }
      onDone(item.sku);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Помилка');
    } finally { setSaving(false); }
  }

  return (
    <div className="rt-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="rt-modal rt-modal--sm" onClick={e => e.stopPropagation()}>
        <div className="rt-modal-head">
          <span className="rt-modal-title">Не додавати товар</span>
          <button className="rt-icon-btn" onClick={onClose}>✕</button>
        </div>
        <div className="rt-modal-body">
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{item.sku}</div>
            <div className="rt-hint">{item.name}</div>
          </div>
          <div className="rt-toggle-wrap" style={{ marginBottom: 16 }}
            onClick={() => setExcludeAll(v => !v)}>
            <div className={`rt-toggle ${excludeAll ? 'rt-toggle--on' : ''}`}><div className="rt-toggle-knob" /></div>
            <div>
              <div style={{ fontSize: 13 }}>{excludeAll ? `Для всіх доступних сайтів (${allSites.length})` : 'Тільки для поточного сайту'}</div>
              <div className="rt-hint">
                {excludeAll
                  ? `Буде прихований на: ${allSites.map(s => s.name).join(', ') || '…'}`
                  : 'Буде прихований лише на цьому сайті'}
              </div>
            </div>
          </div>
          {error && <div className="rt-error" style={{ marginBottom: 8 }}>{error}</div>}
          <div className="rt-modal-actions">
            <button className="rt-btn" onClick={onClose}>Скасувати</button>
            <button className="rt-btn rt-btn-danger-fill" onClick={handleSave} disabled={saving || allSites.length === 0}>
              {saving ? 'Збереження…' : 'Не додавати'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AnalysisTab({ site, filterBrand, setFilterBrand, filterModel, setFilterModel }) {
  const selectedSiteId = site.id;
  const [view,         setView]         = useState('missing-products');
  const [items,        setItems]        = useState([]);
  const [total,        setTotal]        = useState(0);
  const [loading,      setLoading]      = useState(false);
  const [offset,       setOffset]       = useState(0);
  const [excludeItem,  setExcludeItem]  = useState(null);
  const [viewProduct,  setViewProduct]  = useState(null);
  const [filterSort,   setFilterSort]   = useState('newest');

  const _atUser    = useAuthStore((state) => state.user);
  const _atIsAdmin = !!(_atUser?.is_admin && !_atUser?.impersonated_by);
  const canAddProduct = _atIsAdmin || !!(_atUser?.permissions?.includes('retail_publish'));

  function openAddProduct(it) {
    // Префікс "addp_" — НЕ просто "sku": у App.jsx/HomePage.jsx вже є власна
    // логіка, яка бачить голий ?sku=... в URL як пряме посилання на товар у
    // публічному каталозі і відкриває ЙОГО замість адмінки (hasSku-перевірка,
    // App.jsx:44) — без префікса наш deep-link на форму додавання товару на
    // сайт відкривав би звичайну картку товару в каталозі.
    const params = new URLSearchParams({
      tab: 'retail', action: 'add-product', addp_site: String(selectedSiteId), addp_sku: it.sku,
      addp_name: it.name || '', addp_brand: it.brand || '', addp_model: it.model || '',
      addp_sizes: it.sizes || '', addp_color: it.color || '', addp_season: it.season || '',
      addp_photo_url: it.photo_url || '',
    });
    window.open(`${window.location.origin}${window.location.pathname}?${params.toString()}`, '_blank');
  }
  // Бренд/модель приходять пропсами з SiteDetailTab — зберігаються там, щоб
  // не скидатись при переключенні на іншу під-вкладку й назад (ця компонента
  // розмонтовується, а батьківська — ні). Поріг розмірів лишаємо локальним.
  const [filterMinSizes, setFilterMinSizes] = useState(0);
  const [filterSeason, setFilterSeason] = useState('');
  // Опції для дропдаунів (бренди / моделі)
  const [brands,  setBrands]  = useState([]);
  const [models,  setModels]  = useState([]);
  // Список «не додавати»
  const [excludes,     setExcludes]     = useState([]);
  const [excLoading,   setExcLoading]   = useState(false);
  const [excLoadError, setExcLoadError] = useState(false);
  const [loadError,    setLoadError]    = useState(false);
  const [restoring,    setRestoring]    = useState({});
  const [canAddSizes,  setCanAddSizes]  = useState(false);
  const [addingSize,   setAddingSize]   = useState({}); // {site_product_id: true} — йде запит
  const LIMIT = 50;

  async function handleAddSizes(it, sizes) {
    if (!sizes.length) return;
    setAddingSize(prev => ({ ...prev, [it.site_product_id]: true }));
    try {
      const r = await retailAPI.addMissingSizes(selectedSiteId, it.site_product_id, sizes);
      const results = r.data?.results || {};
      const okSizes  = Object.entries(results).filter(([, v]) => v.ok).map(([s]) => s);
      const errSizes = Object.entries(results).filter(([, v]) => !v.ok);
      if (okSizes.length) {
        setItems(prev => prev.map(x => x.site_product_id !== it.site_product_id ? x : {
          ...x,
          missing_sizes: x.missing_sizes.filter(s => !okSizes.includes(s)),
          addable_sizes: (x.addable_sizes || []).filter(s => !okSizes.includes(s)),
          site_sizes:    [...x.site_sizes, ...okSizes].sort(),
        }));
      }
      if (errSizes.length) {
        alert(errSizes.map(([s, v]) => `${s}: ${v.error}`).join('\n'));
      }
    } catch (e) {
      alert(e?.response?.data?.detail || 'Помилка додавання розміру');
    } finally {
      setAddingSize(prev => ({ ...prev, [it.site_product_id]: false }));
    }
  }

  const load = useCallback(async (siteId, v, off = 0, fb = '', fm = '', fms = 0, fs = 'sku', fsn = '') => {
    if (!siteId) return;
    setLoading(true);
    setLoadError(false);
    setItems([]);
    try {
      if (v === 'missing-products') {
        const params = { offset: off, limit: LIMIT, sort: fs };
        if (fb)  params.brand     = fb;
        if (fm)  params.model     = fm;
        if (fms) params.min_sizes = fms;
        if (fsn) params.season    = fsn;
        const r = await retailAPI.getMissingProducts(siteId, params);
        setItems(r.data.items);
        setTotal(r.data.total);
        setOffset(off);
      } else {
        const r = await retailAPI.getMissingSizes(siteId, { offset: off, limit: LIMIT });
        setItems(r.data.items);
        setTotal(r.data.total);
        setOffset(off);
        setCanAddSizes(!!r.data.can_add_sizes);
      }
    } catch { setLoadError(true); }
    finally { setLoading(false); }
  }, []);

  // Завантаження брендів (один раз при відкритті)
  const loadBrands = useCallback(async (siteId, season = '') => {
    try {
      const r = await retailAPI.getMissingProductsOptions(siteId, season ? { season } : {});
      setBrands(r.data.brands || []);
      return r.data.brands || [];
    } catch { return null; }
  }, []);

  // Завантаження моделей при виборі бренду
  const loadModels = useCallback(async (siteId, brand, season = '') => {
    if (!brand) { setModels([]); return; }
    try {
      const r = await retailAPI.getMissingProductsOptions(siteId, season ? { brand, season } : { brand });
      setModels(r.data.models || []);
    } catch {}
  }, []);

  const loadExcludes = useCallback(async () => {
    setExcLoading(true);
    setExcLoadError(false);
    try {
      const r = await retailAPI.listExcludes();
      setExcludes(r.data);
    } catch { setExcLoadError(true); }
    finally { setExcLoading(false); }
  }, []);

  useEffect(() => {
    // Бренд/модель НЕ скидаємо — вони зберігаються в батьківській компоненті
    // саме для того, щоб пережити розмонтування цієї під-вкладки.
    setFilterMinSizes(0);
    setFilterSeason('');
    setFilterSort('newest');
    setBrands([]); setModels([]);
    if (view === 'excludes') { loadExcludes(); return; }
    load(selectedSiteId, view, 0, filterBrand, filterModel, 0, 'newest');
    if (view === 'missing-products') {
      loadBrands(selectedSiteId);
      if (filterBrand) loadModels(selectedSiteId, filterBrand);
    }
  }, [selectedSiteId, view]); // eslint-disable-line

  const handlePage = (dir) => {
    const newOff = Math.max(0, offset + dir * LIMIT);
    load(selectedSiteId, view, newOff, filterBrand, filterModel, filterMinSizes, filterSort, filterSeason);
  };

  const applyFilter = (fb, fm, fms, fs = filterSort, fsn = filterSeason) => {
    load(selectedSiteId, view, 0, fb, fm, fms, fs, fsn);
  };

  // Сезон звужує і списки брендів/моделей, і видачу. Якщо вибраний бренд (чи модель)
  // у новому сезоні вже відсутній — скидаємо їх, щоб не показувати порожній результат.
  const handleSeason = async (s) => {
    const sn = filterSeason === s ? '' : s;
    setFilterSeason(sn);
    const bl = await loadBrands(selectedSiteId, sn);
    let fb = filterBrand, fm = filterModel;
    if (fb && bl && !bl.includes(fb)) { fb = ''; fm = ''; setFilterBrand(''); setFilterModel(''); setModels([]); }
    else if (fb) {
      try {
        const r = await retailAPI.getMissingProductsOptions(selectedSiteId, sn ? { brand: fb, season: sn } : { brand: fb });
        const ml = r.data.models || [];
        setModels(ml);
        if (fm && !ml.includes(fm)) { fm = ''; setFilterModel(''); }
      } catch {}
    }
    applyFilter(fb, fm, filterMinSizes, filterSort, sn);
  };

  const hasFilter = !!(filterBrand || filterModel || filterMinSizes > 0 || filterSeason);

  function handleExcludeDone(sku) {
    setItems(prev => prev.filter(it => it.sku !== sku));
    setTotal(t => Math.max(0, t - 1));
  }

  async function handleRestore(sku) {
    setRestoring(r => ({ ...r, [sku]: true }));
    try {
      await retailAPI.removeExclude(sku);
      setExcludes(prev => prev.filter(e => e.sku !== sku));
    } catch {}
    finally { setRestoring(r => ({ ...r, [sku]: false })); }
  }

  return (
    <div>
      <div className="rt-section-head">
        <div>
          <div className="rt-section-title">Аналіз відповідності</div>
          <div className="rt-hint">Порівняння товарів та розмірів сайту з системою ALL</div>
        </div>
      </div>

      <div className="rt-filter-btns" style={{ marginBottom: 16 }}>
        <button className={`rt-btn rt-btn-sm${view === 'missing-products' ? ' rt-btn-primary' : ''}`}
          onClick={() => { setView('missing-products'); setItems([]); }}>
          Відсутні товари
        </button>
        <button className={`rt-btn rt-btn-sm${view === 'missing-sizes' ? ' rt-btn-primary' : ''}`}
          onClick={() => { setView('missing-sizes'); setItems([]); }}>
          Відсутні розміри
        </button>
        <button className={`rt-btn rt-btn-sm${view === 'excludes' ? ' rt-btn-primary' : ''}`}
          onClick={() => { setView('excludes'); setItems([]); }}>
          Не додавати
          {excludes.length > 0 && <span className="rt-tab-badge" style={{ marginLeft: 4 }}>{excludes.length}</span>}
        </button>
      </div>

      {view === 'excludes' ? (
        excLoading ? (
          <div className="rt-empty">Завантаження…</div>
        ) : excLoadError ? (
          <div className="rt-empty">
            Помилка завантаження.{' '}
            <button className="rt-btn rt-btn-sm" onClick={loadExcludes}>Повторити</button>
          </div>
        ) : excludes.length === 0 ? (
          <div className="rt-empty">Список порожній</div>
        ) : (
          <>
            <div className="rt-hint" style={{ marginBottom: 8 }}>
              Товарів у списку «не додавати»: <strong>{excludes.length}</strong>
            </div>
            <div className="rt-tbl-wrap">
              <table className="rt-table">
                <thead>
                  <tr><th>SKU</th><th>Область</th><th>Примітка</th><th></th></tr>
                </thead>
                <tbody>
                  {excludes.map(e => (
                    <tr key={e.sku}>
                      <td className="rt-cell-article">{e.sku}</td>
                      <td>
                        {e.exclude_all
                          ? <span style={{ color: 'var(--rt-danger)', fontSize: 12 }}>Всі сайти</span>
                          : e.site_ids?.length === 1
                            ? <span style={{ color: 'var(--lg-text-2)', fontSize: 12 }}>1 сайт</span>
                            : <span style={{ color: 'var(--muted,#888)', fontSize: 12 }}>{e.site_ids?.length || 0} сайтів</span>}
                      </td>
                      <td className="rt-hint">{e.note || '—'}</td>
                      <td>
                        <button
                          className="rt-btn rt-btn-xs"
                          onClick={() => handleRestore(e.sku)}
                          disabled={restoring[e.sku]}
                        >
                          {restoring[e.sku] ? '…' : '↩ Повернути'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      ) : loading ? (
        <div className="rt-empty">Завантаження…</div>
      ) : loadError ? (
        <div className="rt-empty">
          Помилка завантаження.{' '}
          <button className="rt-btn rt-btn-sm" onClick={() => load(selectedSiteId, view, 0)}>Повторити</button>
        </div>
      ) : items.length === 0 ? (
        <div className="rt-empty">
          {view === 'missing-products'
            ? 'Всі товари ALL прив\'язані до сайту або приховані'
            : 'Всі розміри відповідають — відмінно!'}
        </div>
      ) : view === 'missing-products' ? (
        <>
          {/* ── Фільтри ── */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
            <select
              className="rt-input rt-input-sm"
              style={{ minWidth: 140 }}
              value={filterBrand}
              onChange={e => {
                const b = e.target.value;
                setFilterBrand(b); setFilterModel('');
                loadModels(selectedSiteId, b, filterSeason);
                applyFilter(b, '', filterMinSizes);
              }}
            >
              <option value="">Всі бренди</option>
              {brands.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            <select
              className="rt-input rt-input-sm"
              style={{ minWidth: 160 }}
              value={filterModel}
              disabled={!filterBrand}
              onChange={e => { const m = e.target.value; setFilterModel(m); applyFilter(filterBrand, m, filterMinSizes); }}
            >
              <option value="">Всі моделі</option>
              {models.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <span className="rt-hint" style={{ whiteSpace: 'nowrap' }}>Мін. розмірів:</span>
              {[0, 5, 8, 11].map(n => (
                <button key={n}
                  className={`rt-btn rt-btn-xs${filterMinSizes === n ? ' rt-btn-primary' : ''}`}
                  onClick={() => { setFilterMinSizes(n); applyFilter(filterBrand, filterModel, n); }}
                >
                  {n === 0 ? 'Всі' : n === 11 ? '36–46' : `≥${n}`}
                </button>
              ))}
            </div>
            <div className="season-filter-options" style={{ margin: 0 }}>
              {['Зима', 'Лето', 'Весна-Осень-Лето'].map(s => (
                <button key={s} type="button"
                  className={`season-btn${filterSeason === s ? ' active' : ''}`}
                  onClick={() => handleSeason(s)}>
                  {s}
                </button>
              ))}
            </div>
            <select
              className="rt-input rt-input-sm"
              style={{ minWidth: 140 }}
              value={filterSort}
              onChange={e => { const fs = e.target.value; setFilterSort(fs); applyFilter(filterBrand, filterModel, filterMinSizes, fs); }}
            >
              <option value="newest">Сортування: спочатку нові</option>
              <option value="sku">Сортування: за артикулом</option>
            </select>
            {hasFilter && (
              <button className="rt-btn rt-btn-xs" onClick={() => {
                setFilterBrand(''); setFilterModel(''); setFilterMinSizes(0); setFilterSeason('');
                loadBrands(selectedSiteId); setModels([]);
                applyFilter('', '', 0, filterSort, '');
              }}>
                ✕ Скинути
              </button>
            )}
          </div>

          <div className="rt-hint" style={{ marginBottom: 8 }}>
            Товарів в ALL без прив'язки: <strong>{total}</strong>
            <span style={{ marginLeft: 8, fontSize: 11 }}>· без наявності та «не додавати» — вже виключені</span>
          </div>
          <div className="rt-missing-grid">
            {items.map(it => (
              <div key={it.sku} className="rt-missing-card"
                   onClick={() => setViewProduct(it)}
                   title={[it.name, it.brand, it.model].filter(Boolean).join(' · ')}>
                <div className="rt-missing-card-img">
                  {it.photo_url
                    ? <img src={it.photo_url} alt={it.sku} loading="lazy" />
                    : <span className="rt-missing-card-placeholder">📦</span>}
                  {it.sizes && <span className="rt-missing-card-sizes">{it.sizes}</span>}
                </div>
                <div className="rt-missing-card-body">
                  <span className="rt-missing-card-sku">{it.sku}</span>
                  <div className="rt-missing-card-actions" onClick={e => e.stopPropagation()}>
                    {canAddProduct && (
                      <button className="rt-btn rt-btn-xs rt-btn-primary"
                        onClick={() => openAddProduct(it)}
                        title="Відкрити форму додавання товару на цей сайт (в новій вкладці)">
                        +
                      </button>
                    )}
                    <button className="rt-btn rt-btn-xs rt-btn-danger"
                      onClick={() => setExcludeItem(it)}
                      title="Не додавати на сайт">
                      ✕
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="rt-hint" style={{ marginBottom: 8 }}>
            Товарів з відсутніми розмірами: <strong>{total}</strong>
          </div>
          {!canAddSizes && (
            <div className="rt-hint" style={{ marginBottom: 8, color: '#f59e0b' }}>
              Щоб додавати відсутні розміри на сайт одразу звідси — спочатку заповніть
              «Характеристика розміру» в налаштуваннях сайту (кнопка редагування сайту).
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map(it => {
              const addable = it.addable_sizes || [];
              const isAdding = !!addingSize[it.site_product_id];
              return (
                <div key={it.site_product_id} className="rt-missing-sizes-card">
                  <div className="rt-missing-sizes-head">
                    <span className="rt-cell-article">{it.article_site}</span>
                    <span>{it.title_ua || it.title_ru}</span>
                    <span className="rt-hint" style={{ marginLeft: 'auto' }}>ALL: {it.all_sku}</span>
                  </div>
                  <div className="rt-sizes-compare">
                    <div>
                      <span className="rt-label" style={{ fontSize: 11 }}>На сайті</span>
                      <div className="rt-size-chips">
                        {(it.site_sizes || []).map(s => <span key={s} className="rt-size-chip rt-size-chip--ok">{s}</span>)}
                        {(!it.site_sizes || it.site_sizes.length === 0) && <span className="rt-hint">—</span>}
                      </div>
                    </div>
                    <div>
                      <span className="rt-label" style={{ fontSize: 11, color: 'var(--rt-danger)' }}>Відсутні</span>
                      <div className="rt-size-chips">
                        {(it.missing_sizes || []).map(s => (
                          <span key={s} className="rt-size-chip rt-size-chip--miss" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {s}
                            {canAddSizes && addable.includes(s) && (
                              <button
                                title={`Додати розмір ${s} на сайт`}
                                disabled={isAdding}
                                onClick={() => handleAddSizes(it, [s])}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit',
                                          fontWeight: 700, padding: 0, lineHeight: 1 }}
                              >+</button>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  {canAddSizes && addable.length > 0 && (
                    <button className="rt-btn rt-btn-xs rt-btn-primary" style={{ marginTop: 8 }}
                      disabled={isAdding}
                      onClick={() => handleAddSizes(it, addable)}
                    >
                      {isAdding ? 'Додавання…' : `+ Додати всі наявні (${addable.length})`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {view !== 'excludes' && total > LIMIT && (
        <div className="rt-pagination" style={{ marginTop: 16 }}>
          <button className="rt-btn rt-btn-sm" disabled={offset === 0} onClick={() => handlePage(-1)}>← Назад</button>
          <span className="rt-hint">{offset + 1}–{Math.min(offset + LIMIT, total)} з {total}</span>
          <button className="rt-btn rt-btn-sm" disabled={offset + LIMIT >= total} onClick={() => handlePage(1)}>Далі →</button>
        </div>
      )}

      {excludeItem && (
        <ExcludeModal
          item={excludeItem}
          currentSiteId={selectedSiteId}
          onClose={() => setExcludeItem(null)}
          onDone={handleExcludeDone}
        />
      )}
      {viewProduct && (
        <ProductModal
          product={viewProduct}
          onClose={() => setViewProduct(null)}
          onPrev={null}
          onNext={null}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Вкладка "Оновлення" (конфиг синхронизации + ручное обновление наличия)
// ─────────────────────────────────────────────────────────────────────────────

const INTERVAL_OPTIONS = [
  { value: 30,   label: 'Кожні 30 хвилин' },
  { value: 60,   label: 'Щогодини' },
  { value: 120,  label: 'Кожні 2 години' },
  { value: 360,  label: 'Кожні 6 годин' },
  { value: 720,  label: 'Двічі на день' },
  { value: 1440, label: 'Раз на день' },
];

const PAGES_OPTIONS = [
  { value: 1,  label: '1 стор. (500 тов.)' },
  { value: 3,  label: '3 стор. (1500 тов.)' },
  { value: 5,  label: '5 стор. (2500 тов.) — рекомендовано' },
  { value: 10, label: '10 стор. (5000 тов.)' },
  { value: 0,  label: 'Без ліміту (весь каталог)' },
];

// Карточка настроек одного сайта
function PromBulkPresenceButton({ siteId }) {
  const [busy,        setBusy]        = useState(false);
  const [result,      setResult]      = useState(null);
  const [err,         setErr]         = useState('');
  const [target,      setTarget]      = useState('ready_for_shipment');
  const [onlyInStock, setOnlyInStock] = useState(true);

  const labelMap = { available: 'В наявності', ready_for_shipment: 'Готов до відправки', not_available: 'Немає в наявності' };

  async function handleBulk() {
    const scope = onlyInStock ? 'тільки для товарів в наявності' : 'для ВСІХ товарів';
    if (!window.confirm(`Встановити "${labelMap[target]}" ${scope}?`)) return;
    setBusy(true); setResult(null); setErr('');
    try {
      const r = await retailAPI.bulkSetPresence(siteId, target, onlyInStock);
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Помилка');
    } finally { setBusy(false); }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <select
          className="rt-input rt-select"
          style={{ width: 'auto' }}
          value={target}
          onChange={e => setTarget(e.target.value)}
          disabled={busy}
        >
          <option value="available">В наявності</option>
          <option value="ready_for_shipment">Готов до відправки</option>
          <option value="not_available">Немає в наявності</option>
        </select>
        <button className="rt-btn rt-btn-sm" onClick={handleBulk} disabled={busy}>
          {busy ? 'Оновлення…' : 'Встановити'}
        </button>
        {result && <span className="rt-hint" style={{ color: 'var(--color-ok, #22c55e)' }}>✓ Оновлено: {result.updated}</span>}
        {err && <span className="rt-hint" style={{ color: 'var(--color-error, #ef4444)' }}>{err}</span>}
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none' }}>
        <input type="checkbox" checked={onlyInStock} onChange={e => setOnlyInStock(e.target.checked)} disabled={busy} />
        <span className="rt-hint" style={{ margin: 0 }}>Тільки товари в наявності (не зачіпати відсутні)</span>
      </label>
    </div>
  );
}

function SiteUpdateCard({ site, sites, onDirtyChange }) {
  const currentUser = useAuthStore((state) => state.user);
  const isAdmin = !!(currentUser?.is_admin && !currentUser?.impersonated_by);
  // Спільні правила постачальників — доступно адмінам і тим, у кого є право retail_site_admin
  const canManageSupplierRules = isAdmin || !!(currentUser?.permissions?.includes('retail_site_admin'));

  const [cardTab,       setCardTab]       = useState(isAdmin ? 'changes' : 'settings');
  const [config,        setConfig]        = useState(null);
  const [saving,        setSaving]        = useState(false);
  const [updating,      setUpdating]      = useState(false);
  const [updateResult,  setUpdateResult]  = useState(null);
  const [error,         setError]         = useState('');
  const [pending,       setPending]       = useState(null);
  const [pendingLoad,   setPendingLoad]   = useState(false);
  const [updatingOne,   setUpdatingOne]   = useState(null); // site_product_id
  const [manualPrices,  setManualPrices]  = useState({});  // { site_product_id: '1234' }
  const [suppliers,     setSuppliers]     = useState([]);
  const [stockLog,      setStockLog]      = useState(null);
  const [logLoading,    setLogLoading]    = useState(false);
  const [logSearch,     setLogSearch]     = useState('');
  // Пошук у журналі йде по всій базі (на сервері), а не лише серед останніх 200 записів
  useEffect(() => {
    if (cardTab !== 'log') return undefined;
    const t = setTimeout(async () => {
      try { const r = await retailAPI.getStockLog(site.id, 200, logSearch.trim()); setStockLog(r.data); }
      catch {}
    }, 400);
    return () => clearTimeout(t);
  }, [logSearch, cardTab, site.id]);
  const [logAvailFilter, setLogAvailFilter] = useState(null); // null=всі, true=є, false=нема
  const [shareRulesOpen,    setShareRulesOpen]    = useState(false);
  const [shareRulesTargets, setShareRulesTargets] = useState([]);   // [site_id, ...]
  const [shareRulesRunning, setShareRulesRunning] = useState(false);
  const [shareRulesResult,  setShareRulesResult]  = useState(null);
  const [unshareRunning,    setUnshareRunning]    = useState(false);

  // ── Тест-оновлення ──
  const [testProductSearch, setTestProductSearch] = useState('');
  const [testProducts,      setTestProducts]      = useState([]);
  const [testProductsLoading, setTestProductsLoading] = useState(false);
  const [testSelected,      setTestSelected]      = useState(null);   // { id, title, article_site, all_sku }
  const [testPresence,      setTestPresence]      = useState(null);   // null=авто, 'available'|'ready_for_shipment'|'not_available'
  const [testSaveDb,        setTestSaveDb]        = useState(false);  // true = після відправки записати в БД як звичайний синк
  const [testBaseOnly,      setTestBaseOnly]      = useState(false);  // true = тільки батьківський товар (без розмірів)
  const [testPriceMode,     setTestPriceMode]     = useState('off'); // 'off'|'auto'|'manual'
  const [testPrice,         setTestPrice]         = useState('');
  const [testOldPrice,      setTestOldPrice]      = useState('');
  const [testDiscountMode,  setTestDiscountMode]  = useState('off'); // 'auto'|'manual'|'off'
  const [testDiscountType,  setTestDiscountType]  = useState('percent');
  const [testDiscountValue, setTestDiscountValue] = useState('');
  const [testDiscountDesc,  setTestDiscountDesc]  = useState('');
  // Дата початку знижки на Prom завжди сьогодні (бекенд сам підставляє) —
  // окремого стану для неї вже не потрібно, керується лише дата закінчення.
  const [testDiscountEnd, setTestDiscountEnd] = useState('');
  const [testRunning,       setTestRunning]       = useState(false);
  const [testVariants,      setTestVariants]      = useState([]);       // варіанти обраного товару
  const [testVariantId,     setTestVariantId]     = useState(null);     // null = всі
  const [oldPriceRunning,   setOldPriceRunning]   = useState(false);
  const [oldPriceResult,    setOldPriceResult]    = useState(null);
  const [testResult,        setTestResult]        = useState(null);
  const [testError,         setTestError]         = useState('');

  const loadPending = useCallback(async () => {
    setPendingLoad(true);
    try {
      const r = await retailAPI.getPendingUpdates(site.id);
      setPending(r.data);
    } catch (e) { setError(e?.response?.data?.detail || 'Помилка завантаження'); }
    finally { setPendingLoad(false); }
  }, [site.id]);

  useEffect(() => {
    retailAPI.getSyncConfig(site.id).then(r => { setConfig(r.data); savedConfigRef.current = r.data; }).catch(() => {});
    retailAPI.getSiteSuppliers(site.id).then(r => setSuppliers(r.data.suppliers || [])).catch(() => {});
    loadPending();
  }, [site.id]); // eslint-disable-line

  // ── Відстеження незбережених змін у налаштуваннях ──
  const savedConfigRef = useRef(null);
  const isDirty = !!(config && savedConfigRef.current && JSON.stringify(config) !== JSON.stringify(savedConfigRef.current));

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    const handler = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Оновлення наявності/цін тепер запускається у фоні на сервері (щоб уникнути 504
  // на великих каталогах) — опитуємо статус, поки воно виконується.
  useEffect(() => {
    if (!updating) return;
    const id = setInterval(async () => {
      const r = await retailAPI.getUpdateStockStatus(site.id).catch(() => null);
      if (r && !r.data.running) {
        setUpdating(false);
        if (r.data.result) {
          setUpdateResult(r.data.result);
          if (!r.data.result.ok) {
            setError(r.data.result.error || 'Помилка оновлення');
          }
        }
        loadPending();
      }
    }, 3000);
    return () => clearInterval(id);
  }, [updating, site.id]); // eslint-disable-line

  async function handleSaveConfig() {
    if (!config) return;
    setSaving(true); setError('');
    try {
      const r = await retailAPI.updateSyncConfig(site.id, {
        auto_enabled:           config.auto_enabled,
        interval_minutes:       config.interval_minutes,
        pages_per_run:          config.pages_per_run,
        auto_stock_enabled:     config.auto_stock_enabled,
        stock_interval_minutes: config.stock_interval_minutes,
        supplier_rules:         config.supplier_rules || [],
        prom_presence_mode:     config.prom_presence_mode,
        prom_main_substitution: config.prom_main_substitution,
        stock_update_mode:      config.stock_update_mode,
        price_update_enabled:   config.price_update_enabled,
        price_only_increase:    config.price_only_increase,
        markup_type:            config.markup_type || null,
        markup_value:           config.markup_value ?? 0,
        markup_value2:          config.markup_value2 ?? 0,
        markup_no_round50:      config.markup_no_round50,
        markup_min_value:       config.markup_min_value ?? 0,
        old_price_type:         config.old_price_type || null,
        old_price_value:        config.old_price_value ?? 0,
        prom_discount_enabled:     config.prom_discount_enabled,
        prom_discount_description: config.prom_discount_description || '',
        prom_discount_type:        config.prom_discount_type || null,
        prom_discount_value:       config.prom_discount_value ?? 0,
        prom_discount_date_start:  config.prom_discount_date_start || null,
        prom_discount_date_end:    config.prom_discount_date_end || null,
        prom_discount_auto_renew:  config.prom_discount_auto_renew,
        sku_transform_enabled:  config.sku_transform_enabled,
        sku_transform_type:     config.sku_transform_type || null,
        sku_transform_value:    config.sku_transform_value || '',
      });
      setConfig(prev => {
        const merged = { ...prev, ...r.data };
        savedConfigRef.current = merged;
        return merged;
      });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Помилка');
    } finally { setSaving(false); }
  }

  async function handleShareRules() {
    if (!shareRulesTargets.length) return;
    setShareRulesRunning(true);
    setShareRulesResult(null);
    try {
      const r = await retailAPI.shareSupplierRules(site.id, shareRulesTargets);
      setShareRulesResult(r.data);
      if (r.data.ok) {
        setConfig(c => {
          const merged = { ...c, supplier_rules: r.data.rules, shared_rules_with: [...(c.shared_rules_with || []), ...r.data.updated_site_ids] };
          savedConfigRef.current = merged;
          return merged;
        });
      }
    } catch (e) {
      setShareRulesResult({ ok: false, error: e?.response?.data?.detail || e?.message });
    } finally { setShareRulesRunning(false); }
  }

  async function handleUnshareRules() {
    if (!window.confirm('Відв\'язати цей сайт від спільних правил постачальників?\nВін збереже поточні правила, але далі вони редагуються окремо.')) return;
    setUnshareRunning(true);
    try {
      const r = await retailAPI.unshareSupplierRules(site.id);
      setConfig(c => {
        const merged = { ...c, supplier_rules: r.data.rules, shared_rules_with: [] };
        savedConfigRef.current = merged;
        return merged;
      });
      setShareRulesResult(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Помилка');
    } finally { setUnshareRunning(false); }
  }

  async function handleUpdateStock(force = false) {
    setUpdating(true); setUpdateResult(null); setError('');
    try {
      // Запускається у фоні на сервері — результат прийде через polling-ефект вище
      // (getUpdateStockStatus), а не з цієї відповіді.
      await retailAPI.updateStock(site.id, force);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      const msg = detail || e?.message || 'Помилка';
      setError(status ? `[${status}] ${msg}` : msg);
      setUpdating(false);
    }
  }

  async function handleUpdateOne(siteProductId, manualPrice) {
    setUpdatingOne(siteProductId); setError('');
    try {
      await retailAPI.updateStockOne(site.id, siteProductId, manualPrice ?? null);
      // Видаляємо товар локально — без перезавантаження всього списку
      setPending(prev => {
        if (!prev) return prev;
        const newItems = prev.items.filter(i => i.site_product_id !== siteProductId);
        return { ...prev, items: newItems, total: prev.total - 1 };
      });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Помилка оновлення');
    } finally { setUpdatingOne(null); }
  }

  async function handleTogglePriceManual(item) {
    const newVal = !item.price_manual;
    try {
      await retailAPI.updateProductField(site.id, item.site_product_id, { price_manual: newVal });
      setPending(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map(i =>
            i.site_product_id === item.site_product_id ? { ...i, price_manual: newVal } : i
          ),
        };
      });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Помилка зміни режиму ціни');
    }
  }

  return (
    <div className="rt-form-card" style={{ marginBottom: 16 }}>
      {/* Заголовок сайта */}
      <div className="rt-form-card-title">
        <span>{site.name}</span>
        <a href={site.domain?.match(/^https?:\/\//) ? site.domain : `https://${site.domain}`} target="_blank" rel="noopener noreferrer"
           className="rt-hint" style={{ fontWeight: 400, fontSize: 12, color: 'inherit', textDecoration: 'none' }}
           onMouseEnter={e => e.currentTarget.style.textDecoration='underline'}
           onMouseLeave={e => e.currentTarget.style.textDecoration='none'}
        >{site.domain}</a>
      </div>

      {/* Попередження про протермінований токен Prom.ua */}
      {config?.prom_token_error && isAdmin && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
          padding: '10px 14px', marginBottom: 12, color: '#b91c1c', fontSize: 13,
        }}>
          <strong>⚠ Токен Prom.ua:</strong> {config.prom_token_error}
        </div>
      )}

      {/* Внутрішні вкладки: Зміни / Налаштування / Лог змін (адмін) */}
      <div className="rt-tabs rt-tabs--sub" style={{ marginBottom: 16 }}>
        {isAdmin && (
          <button
            className={`rt-tab${cardTab === 'changes' ? ' rt-tab--active' : ''}`}
            onClick={() => setCardTab('changes')}
          >
            Зміни
            {pending && pending.total > 0 && (
              <span className="rt-tab-badge" style={{ marginLeft: 4 }}>{pending.total}</span>
            )}
          </button>
        )}
        <button
          className={`rt-tab${cardTab === 'settings' ? ' rt-tab--active' : ''}`}
          onClick={() => setCardTab('settings')}
        >
          Налаштування
        </button>
        {isAdmin && (
          <button
            className={`rt-tab${cardTab === 'log' ? ' rt-tab--active' : ''}`}
            onClick={async () => {
              setCardTab('log');
              if (!stockLog) {
                setLogLoading(true);
                try {
                  const r = await retailAPI.getStockLog(site.id);
                  setStockLog(r.data);
                } catch {} finally { setLogLoading(false); }
              }
            }}
          >
            Лог змін
          </button>
        )}
        {isAdmin && (
          <button
            className={`rt-tab${cardTab === 'test' ? ' rt-tab--active' : ''}`}
            onClick={() => { setCardTab('test'); setTestResult(null); setTestError(''); }}
          >
            🧪 Тест
          </button>
        )}
      </div>

      {/* ── Вкладка: Зміни — тільки адмін ── */}
      {isAdmin && cardTab === 'changes' && (
        <div>
          <div className="rt-hint" style={{ marginBottom: 10 }}>
            Зміни наявності прив'язаних товарів. <strong>Відправляються тільки після натискання кнопки.</strong>
          </div>
          {pending?.discount_pending && (
            <div style={{
              marginBottom: 10, padding: '6px 10px', borderRadius: 6, fontSize: 13,
              background: 'var(--rt-warn-bg,#92400e22)', color: 'var(--rt-warn,#d97706)',
            }}>
              🏷 Знижка змінилась у налаштуваннях — буде відправлена при наступному оновленні
              (навіть якщо ціна й наявність товарів нижче не змінились).
            </div>
          )}
          {pendingLoad ? (
            <div className="rt-hint" style={{ marginBottom: 10 }}>Завантаження змін…</div>
          ) : pending && pending.total > 0 ? (
            <div className="rt-pending-list" style={{ marginBottom: 12 }}>
              {pending.items.map(item => (
                <div key={item.site_product_id} className="rt-pending-item">
                  <div className="rt-pending-item-title">
                    {/* Кнопка ручної ціни 🔒 */}
                    <button
                      className="rt-btn rt-btn-sm"
                      style={{
                        flexShrink: 0, fontSize: 13, minWidth: 28, padding: '2px 6px',
                        background: item.price_manual ? 'var(--rt-warn-bg,#92400e22)' : 'transparent',
                        color: item.price_manual ? 'var(--rt-warn,#d97706)' : 'var(--muted,#888)',
                        border: '1px solid ' + (item.price_manual ? 'var(--rt-warn,#d97706)' : 'var(--border,#ccc)'),
                      }}
                      title={item.price_manual ? 'Ручна ціна ввімкнена — автоматика не чіпає' : 'Увімкнути ручну ціну'}
                      onClick={() => handleTogglePriceManual(item)}
                    >🔒</button>

                    {/* Поле ручної ціни (тільки коли ввімкнена) */}
                    {item.price_manual && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          placeholder={item.current_price ? Math.round(item.current_price) : 'ціна'}
                          value={manualPrices[item.site_product_id] ?? ''}
                          onChange={e => setManualPrices(p => ({ ...p, [item.site_product_id]: e.target.value }))}
                          style={{ width: 72, fontSize: 12, padding: '2px 5px', borderRadius: 4, border: '1px solid var(--border,#ccc)' }}
                        />
                        <button
                          className="rt-btn rt-btn-sm"
                          style={{ flexShrink: 0, fontSize: 11, minWidth: 60 }}
                          disabled={updatingOne === item.site_product_id || updating || !manualPrices[item.site_product_id]}
                          onClick={() => handleUpdateOne(item.site_product_id, Number(manualPrices[item.site_product_id]))}
                        >
                          {updatingOne === item.site_product_id ? '…' : '↑ Ціна'}
                        </button>
                      </div>
                    )}

                    <button
                      className="rt-btn rt-btn-sm"
                      style={{ flexShrink: 0, fontSize: 11, minWidth: 72 }}
                      disabled={updatingOne === item.site_product_id || updating}
                      onClick={() => handleUpdateOne(item.site_product_id)}
                    >
                      {updatingOne === item.site_product_id ? '…' : '↑ Оновити'}
                    </button>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="rt-cell-article" style={{ flexShrink: 0 }}>{item.article_site}</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</span>
                        {/* Посилання на товар на сайті */}
                        {(() => {
                          let href = null;
                          if (site.platform === 'prom' && item.prom_id) {
                            href = `https://prom.ua/p${item.prom_id}-p.html`;
                          } else if (item.site_link) {
                            href = item.site_link.match(/^https?:\/\//) ? item.site_link : `https://${item.site_link}`;
                          }
                          return href ? (
                            <a href={href} target="_blank" rel="noopener noreferrer"
                               onClick={e => e.stopPropagation()}
                               style={{ flexShrink: 0, fontSize: 11, color: 'var(--muted,#888)', textDecoration: 'none', lineHeight: 1 }}
                               title="Відкрити на сайті">
                              🔗
                            </a>
                          ) : null;
                        })()}
                      </div>
                      <div style={{ display: 'flex', gap: 12, marginTop: 2, fontSize: 11, color: 'var(--muted,#888)' }}>
                        {item.all_sku && <span>Alldrop: {item.all_sku}</span>}
                        {item.prom_id && <span>Prom ID: {item.prom_id}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="rt-pending-changes">
                    {item.changes.map(ch => (
                      <span key={ch.size} className={`rt-pending-change ${ch.new_available ? 'rt-pending-in' : 'rt-pending-out'}`}
                            style={ch.new_price != null && ch.was_available === ch.new_available ? { background: 'var(--rt-warn-bg, #92400e22)', color: 'var(--rt-warn, #d97706)', borderColor: 'var(--rt-warn, #d97706)' } : {}}>
                        {ch.external_id && <span style={{ fontSize: 10, opacity: 0.7, marginRight: 3 }}>#{ch.external_id}</span>}
                        {ch.size}
                        {ch.was_available !== ch.new_available && (
                          <>{': '}{ch.was_available ? 'є' : 'нема'}{' → '}{ch.new_available ? 'є' : 'нема'}</>
                        )}
                        {ch.new_price != null && (
                          <span style={{ marginLeft: 4 }}>
                            {'₴'}{Math.round(ch.was_price || 0)}{' → '}{'₴'}{Math.round(ch.new_price)}
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : pending && pending.total === 0 ? (
            <div className="rt-hint" style={{ marginBottom: 12, color: 'var(--rt-ok, #22a84a)' }}>
              ✓ Всі товари актуальні — змін немає
            </div>
          ) : null}
          {updateResult && (
            <div className={`rt-test-result ${updateResult.ok ? 'rt-ok' : 'rt-err'}`} style={{ marginBottom: 10 }}>
              {updateResult.ok
                ? `✓ Оновлено ${updateResult.updated} позицій`
                : `✗ ${updateResult.message || 'Помилка'}`}
            </div>
          )}
          {error && <div className="rt-error" style={{ marginBottom: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button className="rt-btn rt-btn-primary" onClick={() => handleUpdateStock(false)} disabled={updating}>
              {updating ? '⏳ Оновлення…' : '↑ Оновити всі'}
            </button>
            {isAdmin && (
              <button
                className="rt-btn"
                style={{ borderColor: 'var(--rt-warn, #d97706)', color: 'var(--rt-warn, #d97706)' }}
                disabled={updating}
                onClick={() => {
                  if (window.confirm(
                    '♻️ Перезаписати ціни та наявність?\n\n' +
                    'Усі прив\'язані товари отримають актуальні дані з каталогу AllDrop — ' +
                    'навіть якщо зовнішньо ціна/наявність не змінились.\n\n' +
                    'Використовуй якщо менеджер вручну виставив неправильні значення на сайті.'
                  )) handleUpdateStock(true);
                }}
              >
                ♻️ Перезаписати всі ціни/наявність
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Вкладка: Налаштування ── */}
      {cardTab === 'settings' && (<>

      {/* Авто-оновлення наявності — тільки адмін */}
      {isAdmin && (
      <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--lg-border)' }}>
        <div className="rt-label" style={{ marginBottom: 8 }}>Авто-оновлення наявності</div>
        <div className="rt-hint" style={{ marginBottom: 12 }}>
          Автоматично відправляє зміни наявності прив'язаних товарів на сайт за розкладом.
        </div>
        {config ? (
          <>
            <div className="rt-toggle-wrap"
              onClick={() => setConfig(c => ({ ...c, auto_stock_enabled: !c.auto_stock_enabled }))}
              style={{ marginBottom: 12 }}>
              <div className={`rt-toggle ${config.auto_stock_enabled ? 'rt-toggle--on' : ''}`}>
                <div className="rt-toggle-knob" />
              </div>
              <span style={{ fontSize: 13 }}>
                {config.auto_stock_enabled ? 'Увімкнено' : 'Вимкнено'}
              </span>
            </div>
            {config.auto_stock_enabled && (
              <div className="rt-field" style={{ marginBottom: 12 }}>
                <label className="rt-label">Інтервал оновлення</label>
                <select className="rt-input rt-select"
                  value={config.stock_interval_minutes ?? 60}
                  onChange={e => setConfig(c => ({ ...c, stock_interval_minutes: parseInt(e.target.value) }))}>
                  {INTERVAL_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            )}
            {config.last_stock_at && (
              <div className="rt-hint" style={{ marginBottom: 8 }}>
                Останнє авто-оновлення: {fmtDate(config.last_stock_at)}
              </div>
            )}
          </>
        ) : (
          <div className="rt-hint">Завантаження…</div>
        )}
      </div>
      )}

      {/* Prom.ua — специфічні налаштування (тільки адмін) */}
      {isAdmin && site.platform === 'prom' && config && (
        <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--lg-border)' }}>
          <div className="rt-label" style={{ marginBottom: 4 }}>Prom.ua — наявність</div>
          <div className="rt-hint" style={{ marginBottom: 10 }}>
            Значення наявності, яке відправляється на сайт при оновленні для «в наявності».
          </div>
          <div className="rt-toggle-wrap"
            onClick={() => setConfig(c => ({
              ...c,
              prom_presence_mode: c.prom_presence_mode === 'ready_for_shipment'
                ? 'available' : 'ready_for_shipment',
            }))}>
            <div className={`rt-toggle ${config.prom_presence_mode === 'ready_for_shipment' ? 'rt-toggle--on' : ''}`}>
              <div className="rt-toggle-knob" />
            </div>
            <span style={{ fontSize: 13 }}>
              {config.prom_presence_mode === 'ready_for_shipment'
                ? 'Готов до відправки (для топ-продавців)'
                : 'В наявності (стандарт)'}
            </span>
          </div>
          <div style={{ marginTop: 14 }}>
            <div className="rt-label" style={{ marginBottom: 8 }}>Режим оновлення наявності</div>
            {[
              {
                value: 'full',
                label: 'Режим 1 — повний',
                hint: 'Всі розміри та розновидності оновлюються. Основний товар слідує своєму розміру — якщо його розміру немає в наявності, але є інші, він потрапляє в список під заміну.',
              },
              {
                value: 'base_always_available',
                label: 'Режим 2 — основний завжди в наявності',
                hint: 'Оновлюються тільки розновидності. Основний товар завжди в наявності — стає недоступним лише якщо немає жодного розміру в наявності і сам його розмір теж відсутній.',
              },
            ].map(opt => (
              <div key={opt.value}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10, cursor: 'pointer' }}
                onClick={() => setConfig(c => ({ ...c, stock_update_mode: opt.value }))}
              >
                <div style={{
                  width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                  border: `2px solid ${(config.stock_update_mode || 'full') === opt.value ? 'var(--accent)' : 'var(--border)'}`,
                  background: (config.stock_update_mode || 'full') === opt.value ? 'var(--accent)' : 'transparent',
                  boxSizing: 'border-box',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {(config.stock_update_mode || 'full') === opt.value && (
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />
                  )}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{opt.label}</div>
                  <div className="rt-hint" style={{ marginTop: 2 }}>{opt.hint}</div>
                </div>
              </div>
            ))}
          </div>
          {isAdmin && (
            <div style={{ marginTop: 14 }}>
              <PromBulkPresenceButton siteId={site.id} />
            </div>
          )}
        </div>
      )}

      {/* Правила постачальників */}
      <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--lg-border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <div className="rt-label">Правила постачальників</div>
          {canManageSupplierRules && (sites || []).filter(s => s.id !== site.id).length > 0 && (
            <button
              className="rt-btn rt-btn-sm"
              onClick={() => { setShareRulesOpen(v => !v); setShareRulesResult(null); }}
            >🔗 {(config?.shared_rules_with || []).length > 0 ? 'Додати сайти до спільних' : 'Об\'єднати з іншими сайтами'}</button>
          )}
        </div>
        <div className="rt-hint" style={{ marginBottom: 8 }}>
          Виключіть постачальника повністю або налаштуйте фільтр по конкретних SKU (Alldrop).
        </div>

        {(config?.shared_rules_with || []).length > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            marginBottom: 10, padding: '6px 10px', borderRadius: 6, fontSize: 13,
            background: 'var(--lg-hover,#f5f5f5)',
          }}>
            <span>🔗 Спільні правила з: <strong>
              {config.shared_rules_with
                .map(sid => (sites || []).find(s => s.id === sid)?.name || `#${sid}`)
                .join(', ')}
            </strong> — зміна тут одразу застосується і там.</span>
            {canManageSupplierRules && (
              <button className="rt-btn rt-btn-sm" disabled={unshareRunning} onClick={handleUnshareRules}>
                {unshareRunning ? '⏳…' : '✂ Відв\'язати цей сайт'}
              </button>
            )}
          </div>
        )}

        {shareRulesOpen && (
          <div style={{ marginBottom: 12, padding: 10, borderRadius: 8, background: 'var(--lg-hover,#f5f5f5)' }}>
            <div className="rt-hint" style={{ marginBottom: 8 }}>
              Правила постачальників цього сайту (як вище) стануть <strong>спільними</strong> з вибраними сайтами:
              зміна правила тут або на будь-якому з них одразу застосується до всіх. Поточні власні правила вибраних
              сайтів будуть <strong>замінені</strong> цими. У списку тільки сайти, до яких у вас є доступ.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 200, overflowY: 'auto', marginBottom: 8 }}>
              {(sites || [])
                .filter(s => s.id !== site.id && !(config?.shared_rules_with || []).includes(s.id))
                .map(s => (
                  <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={shareRulesTargets.includes(s.id)}
                      onChange={e => setShareRulesTargets(prev =>
                        e.target.checked ? [...prev, s.id] : prev.filter(id => id !== s.id)
                      )}
                    />
                    {s.name} <span className="rt-hint">({s.domain})</span>
                  </label>
                ))}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                className="rt-btn rt-btn-sm rt-btn-primary"
                disabled={shareRulesRunning || shareRulesTargets.length === 0}
                onClick={() => {
                  if (!window.confirm(`Об'єднати правила постачальників з ${shareRulesTargets.length} сайт(ами)? Їхні поточні правила буде замінено.`)) return;
                  handleShareRules();
                }}
              >
                {shareRulesRunning ? '⏳ Об\'єднання…' : `Об'єднати з обраними (${shareRulesTargets.length})`}
              </button>
              <button
                className="rt-btn rt-btn-sm"
                onClick={() => { setShareRulesOpen(false); setShareRulesTargets([]); setShareRulesResult(null); }}
              >Скасувати</button>
            </div>
            {shareRulesResult && (
              <div style={{
                marginTop: 8, fontSize: 12, padding: '4px 8px', borderRadius: 4,
                background: shareRulesResult.ok ? '#e6f4e6' : '#fee', color: shareRulesResult.ok ? '#2a7a2a' : '#c00',
              }}>
                {shareRulesResult.ok
                  ? `✅ Об'єднано з ${shareRulesResult.updated_site_ids.length} сайт(ами)` +
                    (shareRulesResult.skipped_no_access?.length ? `, пропущено без доступу: ${shareRulesResult.skipped_no_access.length}` : '')
                  : `❌ ${shareRulesResult.error}`}
              </div>
            )}
          </div>
        )}

        {config && (
          <>
            {(config.supplier_rules || []).some(r => r.mode === 'solo') && (
              <div className="rt-hint" style={{ marginBottom: 8, color: 'var(--accent)' }}>
                🔒 Увімкнено «Тільки цей постачальник» — враховується лише він, решта правил нижче ігноруються.
              </div>
            )}
            {(config.supplier_rules || []).length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                {(config.supplier_rules || []).map((rule, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ minWidth: 120, fontWeight: 500, fontSize: 13, paddingTop: 4 }}>{rule.supplier}</span>
                    <select
                      className="rt-input rt-select"
                      value={rule.mode}
                      style={{ width: 'auto' }}
                      onChange={e => setConfig(c => {
                        const rules = [...(c.supplier_rules || [])];
                        rules[idx] = { ...rules[idx], mode: e.target.value, skus: rules[idx].skus || [] };
                        return { ...c, supplier_rules: rules };
                      })}
                    >
                      <option value="exclude_all">Виключити повністю</option>
                      <option value="exclude_skus">Виключити SKU</option>
                      <option value="include_only">Тільки ці SKU</option>
                      <option value="solo">🔒 Тільки цей постачальник</option>
                    </select>
                    {rule.mode !== 'exclude_all' && rule.mode !== 'solo' && (
                      <input
                        className="rt-input"
                        style={{ flex: 1, minWidth: 140, fontSize: 12 }}
                        placeholder="SKU через пробіл або кому (напр. ALL001 ALL002)"
                        value={(rule.skus || []).join(' ')}
                        onChange={e => setConfig(c => {
                          const rules = [...(c.supplier_rules || [])];
                          const skus = e.target.value.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
                          rules[idx] = { ...rules[idx], skus };
                          return { ...c, supplier_rules: rules };
                        })}
                      />
                    )}
                    <button
                      onClick={() => setConfig(c => ({
                        ...c,
                        supplier_rules: (c.supplier_rules || []).filter((_, i) => i !== idx),
                      }))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--rt-danger, #c0392b)', padding: '2px 4px', lineHeight: 1 }}
                      title="Видалити правило"
                    >×</button>
                  </div>
                ))}
              </div>
            )}
            {suppliers.filter(s => !(config.supplier_rules || []).some(r => r.supplier === s)).length > 0 && (
              <select
                className="rt-input rt-select"
                value=""
                onChange={e => {
                  const sup = e.target.value;
                  if (!sup) return;
                  setConfig(c => ({
                    ...c,
                    supplier_rules: [...(c.supplier_rules || []), { supplier: sup, mode: 'exclude_all', skus: [] }],
                  }));
                }}
                style={{ maxWidth: 320 }}
              >
                <option value="">+ Додати правило для постачальника</option>
                {suppliers
                  .filter(s => !(config.supplier_rules || []).some(r => r.supplier === s))
                  .map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
            {(config.supplier_rules || []).length === 0 && suppliers.length === 0 && (
              <div className="rt-hint">Немає прив'язаних товарів із постачальниками</div>
            )}
          </>
        )}
      </div>

      {/* ── Правило артикулу ── */}
      {config && isAdmin && (
      <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--lg-border)' }}>
        <div className="rt-label" style={{ marginBottom: 8 }}>Артикул</div>

        <div className="rt-toggle-wrap"
          onClick={() => setConfig(c => ({ ...c, sku_transform_enabled: !c.sku_transform_enabled }))}
          style={{ marginBottom: 10 }}>
          <div className={`rt-toggle ${config.sku_transform_enabled ? 'rt-toggle--on' : ''}`}>
            <div className="rt-toggle-knob" />
          </div>
          <span style={{ fontSize: 13 }}>Автоматичне формування артикулу (трансформація)</span>
        </div>

        {config.sku_transform_enabled && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
            <select className="rt-input" style={{ width: 160 }}
              value={config.sku_transform_type || ''}
              onChange={e => setConfig(c => ({ ...c, sku_transform_type: e.target.value || null }))}>
              <option value="">— Тип трансформації —</option>
              <option value="add_prefix">Додати префікс</option>
              <option value="prefix_before_dash">Префікс + код до першого дефіса (Krosseller)</option>
              <option value="prefix_after_dash">Префікс + код між дефісами (Stepin)</option>
            </select>
            {(config.sku_transform_type === 'add_prefix' || config.sku_transform_type === 'prefix_before_dash' || config.sku_transform_type === 'prefix_after_dash') && (<>
              <input className="rt-input" style={{ width: 100 }}
                placeholder="Префікс (ALL)"
                value={config.sku_transform_value || ''}
                onChange={e => setConfig(c => ({ ...c, sku_transform_value: e.target.value }))} />
              {config.sku_transform_type === 'add_prefix' && (
                <span className="rt-hint">
                  Приклад: <b>{(config.sku_transform_value || 'ALL') + '20002'}</b>
                  {' '}← артикул сайту <b>20002</b>
                </span>
              )}
              {config.sku_transform_type === 'prefix_before_dash' && (
                <span className="rt-hint">
                  Приклад: <b>{(config.sku_transform_value || 'ALL') + '000174'}</b>
                  {' '}← артикул сайту <b>000174-Бежевий-36</b>
                </span>
              )}
              {config.sku_transform_type === 'prefix_after_dash' && (
                <span className="rt-hint">
                  Приклад: <b>{(config.sku_transform_value || 'ALL') + '17615'}</b>
                  {' '}← артикул сайту <b>ac-17615-40</b>
                </span>
              )}
            </>)}
          </div>
        )}
        <div className="rt-hint">
          При синку артикул сайту трансформується → шукається в каталозі.
          Ручний маппінг (вкладка «Товари») завжди має вищий пріоритет.
        </div>
      </div>
      )}

      {/* ── Налаштування ціни — видимо всім ── */}
      {config && (
      <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--lg-border)' }}>
        <div className="rt-label" style={{ marginBottom: 8 }}>Ціна на сайті</div>
        <div className="rt-hint" style={{ marginBottom: 12 }}>
          Ціна розраховується як максимальна ціна серед активних постачальників + наценка.
          Якщо оновлення ввімкнено — застосовується при авто- та ручному оновленні.
        </div>

        {/* Ввімкнути оновлення ціни */}
        <div className="rt-toggle-wrap"
          onClick={() => setConfig(c => ({ ...c, price_update_enabled: !c.price_update_enabled }))}
          style={{ marginBottom: 10 }}>
          <div className={`rt-toggle ${config.price_update_enabled ? 'rt-toggle--on' : ''}`}>
            <div className="rt-toggle-knob" />
          </div>
          <span style={{ fontSize: 13 }}>Оновлювати ціну при синхронізації</span>
        </div>

        {config.price_update_enabled && (<>
          {/* Тільки збільшення */}
          <div className="rt-toggle-wrap"
            onClick={() => setConfig(c => ({ ...c, price_only_increase: !c.price_only_increase }))}
            style={{ marginBottom: 12 }}>
            <div className={`rt-toggle ${config.price_only_increase ? 'rt-toggle--on' : ''}`}>
              <div className="rt-toggle-knob" />
            </div>
            <span style={{ fontSize: 13 }}>Тільки збільшення ціни (не знижувати)</span>
          </div>

          {/* Тип наценки */}
          <div className="rt-field" style={{ marginBottom: 10 }}>
            <label className="rt-label">Наценка</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <select className="rt-input rt-select" style={{ width: 140 }}
                value={config.markup_type || ''}
                onChange={e => setConfig(c => ({ ...c, markup_type: e.target.value || null }))}>
                <option value="">— без наценки —</option>
                <option value="fixed">Фіксована (грн)</option>
                <option value="percent">Відсоток (%, ×50↑)</option>
                <option value="fixed_percent">Фіксована сума + відсоток</option>
              </select>
              {config.markup_type && (
                <input type="number" className="rt-input" style={{ width: 90 }}
                  min={0} step={config.markup_type === 'percent' ? 0.1 : 10}
                  value={config.markup_value ?? 0}
                  onChange={e => setConfig(c => ({ ...c, markup_value: e.target.value === '' ? '' : parseFloat(e.target.value) }))}
                  onBlur={() => setConfig(c => ({ ...c, markup_value: parseFloat(String(c.markup_value)) || 0 }))} />
              )}
              {config.markup_type === 'fixed' && <span style={{ fontSize: 12, opacity: .7 }}>грн</span>}
              {config.markup_type === 'percent' && (
                <span style={{ fontSize: 12, opacity: .7 }}>% (округл. {config.markup_no_round50 ? 'до цілого' : 'кратно 50↑'})</span>
              )}
              {config.markup_type === 'fixed_percent' && <span style={{ fontSize: 12, opacity: .7 }}>грн, потім +</span>}
              {config.markup_type === 'fixed_percent' && (<>
                <input type="number" className="rt-input" style={{ width: 90 }}
                  min={0} step={0.1}
                  value={config.markup_value2 ?? 0}
                  onChange={e => setConfig(c => ({ ...c, markup_value2: e.target.value === '' ? '' : parseFloat(e.target.value) }))}
                  onBlur={() => setConfig(c => ({ ...c, markup_value2: parseFloat(String(c.markup_value2)) || 0 }))} />
                <span style={{ fontSize: 12, opacity: .7 }}>% (округл. {config.markup_no_round50 ? 'до цілого' : 'кратно 50↑'})</span>
              </>)}
            </div>
            {(config.markup_type === 'percent' || config.markup_type === 'fixed_percent') && (
              <div className="rt-toggle-wrap"
                onClick={() => setConfig(c => ({ ...c, markup_no_round50: !c.markup_no_round50 }))}
                style={{ marginTop: 6 }}>
                <div className={`rt-toggle ${config.markup_no_round50 ? 'rt-toggle--on' : ''}`}>
                  <div className="rt-toggle-knob" />
                </div>
                <span style={{ fontSize: 13 }}>Не округляти до 50 (округлювати до цілого)</span>
              </div>
            )}
            {(config.markup_type === 'percent' || config.markup_type === 'fixed_percent') && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <span style={{ fontSize: 13 }}>Наценка не менше</span>
                <input type="number" className="rt-input" style={{ width: 90 }}
                  min={0} step={10}
                  value={config.markup_min_value ?? 0}
                  onChange={e => setConfig(c => ({ ...c, markup_min_value: e.target.value === '' ? '' : parseFloat(e.target.value) }))}
                  onBlur={() => setConfig(c => ({ ...c, markup_min_value: parseFloat(String(c.markup_min_value)) || 0 }))} />
                <span style={{ fontSize: 12, opacity: .7 }}>грн</span>
              </div>
            )}
            {(config.markup_type === 'percent' || config.markup_type === 'fixed_percent') && config.markup_min_value > 0 && (
              <div className="rt-hint" style={{ marginTop: 4 }}>
                Якщо {config.markup_value2 ? 'відсоткова частина наценки' : `${config.markup_value || 0}%`} дає менше {config.markup_min_value} грн — буде взято {config.markup_min_value} грн.
              </div>
            )}
            {config.markup_type === 'fixed_percent' && (
              <div className="rt-hint" style={{ marginTop: 4 }}>
                Ціна = (собівартість + {config.markup_value || 0} грн) × (1 + {config.markup_value2 || 0}% / 100)
              </div>
            )}
          </div>

          {/* Стара ціна (Horoshop) */}
          {site.platform === 'horoshop' && (
            <div className="rt-field" style={{ marginBottom: 10 }}>
              <label className="rt-label">Стара ціна (закреслена)</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <select className="rt-input rt-select" style={{ width: 140 }}
                  value={config.old_price_type || ''}
                  onChange={e => setConfig(c => ({ ...c, old_price_type: e.target.value || null }))}>
                  <option value="">— не встановлювати —</option>
                  <option value="fixed">Ціна + фікс. сума (грн)</option>
                  <option value="percent">Ціна + відсоток (%)</option>
                </select>
                {config.old_price_type && (
                  <input type="number" className="rt-input" style={{ width: 90 }}
                    min={0} step={config.old_price_type === 'percent' ? 0.1 : 10}
                    value={config.old_price_value ?? 0}
                    onChange={e => setConfig(c => ({ ...c, old_price_value: e.target.value === '' ? '' : parseFloat(e.target.value) }))}
                    onBlur={() => setConfig(c => ({ ...c, old_price_value: parseFloat(String(c.old_price_value)) || 0 }))} />
                )}
                {config.old_price_type === 'fixed' && <span style={{ fontSize: 12, opacity: .7 }}>грн</span>}
                {config.old_price_type === 'percent' && <span style={{ fontSize: 12, opacity: .7 }}>%</span>}
              </div>
              <div className="rt-hint" style={{ marginTop: 4 }}>
                Стара ціна = ціна сайту + надбавка (відображається закресленою поряд з ціною)
              </div>

              {/* Кнопка масового проставлення (тільки адмін) */}
              {isAdmin && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {config.old_price_type && (
                      <button
                        className="rt-btn rt-btn-sm"
                        disabled={oldPriceRunning}
                        onClick={async () => {
                          if (!window.confirm('Проставити стару ціну по ВСІХ товарах сайту?\nЦе відправить реальний запит на сайт.')) return;
                          setOldPriceRunning(true);
                          setOldPriceResult(null);
                          try {
                            const r = await retailAPI.bulkSetOldPrice(site.id, false);
                            setOldPriceResult(r.data);
                          } catch (e) {
                            setOldPriceResult({ ok: false, error: e?.response?.data?.detail || e?.message });
                          } finally { setOldPriceRunning(false); }
                        }}
                      >
                        {oldPriceRunning ? '⏳ Відправка…' : '🏷 Проставити стару ціну по всіх'}
                      </button>
                    )}
                    <button
                      className="rt-btn rt-btn-sm"
                      disabled={oldPriceRunning}
                      style={{ color: '#c00' }}
                      onClick={async () => {
                        if (!window.confirm('Прибрати стару ціну (скинути в 0) по ВСІХ товарах сайту?\nЦе відправить реальний запит на сайт.')) return;
                        setOldPriceRunning(true);
                        setOldPriceResult(null);
                        try {
                          const r = await retailAPI.bulkSetOldPrice(site.id, true);
                          setOldPriceResult(r.data);
                        } catch (e) {
                          setOldPriceResult({ ok: false, error: e?.response?.data?.detail || e?.message });
                        } finally { setOldPriceRunning(false); }
                      }}
                    >
                      {oldPriceRunning ? '⏳ Відправка…' : '🗑 Прибрати стару ціну по всіх'}
                    </button>
                  </div>
                  {oldPriceResult && (
                    <div style={{
                      marginTop: 6, fontSize: 12, padding: '4px 8px', borderRadius: 4,
                      background: oldPriceResult.ok ? '#e6f4e6' : '#fee',
                      color: oldPriceResult.ok ? '#2a7a2a' : '#c00',
                    }}>
                      {oldPriceResult.ok
                        ? `✅ Оновлено ${oldPriceResult.updated} варіантів${oldPriceResult.skipped ? `, пропущено ${oldPriceResult.skipped}` : ''}`
                        : `❌ ${oldPriceResult.error}`}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Знижка (Prom.ua) */}
          {site.platform === 'prom' && (
            <div className="rt-field" style={{ marginBottom: 10 }}>
              <div className="rt-toggle-wrap"
                onClick={() => setConfig(c => ({ ...c, prom_discount_enabled: !c.prom_discount_enabled }))}
                style={{ marginBottom: 10 }}>
                <div className={`rt-toggle ${config.prom_discount_enabled ? 'rt-toggle--on' : ''}`}>
                  <div className="rt-toggle-knob" />
                </div>
                <span style={{ fontSize: 13 }}>Знижка на товари</span>
              </div>

              {config.prom_discount_enabled && (<>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                  <select className="rt-input rt-select" style={{ width: 140 }}
                    value={config.prom_discount_type || ''}
                    onChange={e => setConfig(c => ({ ...c, prom_discount_type: e.target.value || null }))}>
                    <option value="">— тип знижки —</option>
                    <option value="percent">Відсоток (%)</option>
                    <option value="fixed">Фіксована сума</option>
                  </select>
                  <input type="number" className="rt-input" style={{ width: 90 }}
                    min={0} step={config.prom_discount_type === 'percent' ? 0.1 : 10}
                    value={config.prom_discount_value ?? 0}
                    onChange={e => setConfig(c => ({ ...c, prom_discount_value: e.target.value === '' ? '' : parseFloat(e.target.value) }))}
                    onBlur={() => setConfig(c => ({ ...c, prom_discount_value: parseFloat(String(c.prom_discount_value)) || 0 }))} />
                  {config.prom_discount_type === 'percent' && <span style={{ fontSize: 12, opacity: .7 }}>%</span>}
                  {config.prom_discount_type === 'fixed' && <span style={{ fontSize: 12, opacity: .7 }}>грн</span>}
                </div>

                <input className="rt-input" style={{ width: '100%', marginBottom: 8 }}
                  placeholder="Опис знижки (необов'язково)"
                  value={config.prom_discount_description || ''}
                  onChange={e => setConfig(c => ({ ...c, prom_discount_description: e.target.value }))} />

                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span className="rt-hint">З</span>
                    <input type="date" className="rt-input" style={{ width: 150 }}
                      value={todayInputDate()} disabled
                      title="Дата початку знижки на Prom завжди сьогодні" />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span className="rt-hint">до (макс. 30 днів)</span>
                    <input type="date" className="rt-input" style={{ width: 150 }}
                      min={todayInputDate()} max={maxDiscountEndInputDate()}
                      value={ddmmyyyyToInput(config.prom_discount_date_end)}
                      onChange={e => setConfig(c => ({ ...c, prom_discount_date_end: inputToDdmmyyyy(e.target.value) }))} />
                  </div>
                </div>

                <div className="rt-toggle-wrap"
                  onClick={() => setConfig(c => ({ ...c, prom_discount_auto_renew: !c.prom_discount_auto_renew }))}
                  style={{ marginBottom: 4 }}>
                  <div className={`rt-toggle ${config.prom_discount_auto_renew ? 'rt-toggle--on' : ''}`}>
                    <div className="rt-toggle-knob" />
                  </div>
                  <span style={{ fontSize: 13 }}>Автопродовжувати</span>
                </div>
                <div className="rt-hint">
                  Коли термін дії мине — сервіс сам проставить нові дати (та сама тривалість, від сьогодні).
                  Без автопродовження знижка просто перестане діяти після дати закінчення.
                </div>
              </>)}
            </div>
          )}
        </>)}
      </div>
      )}

      {/* Автосинхронізація каталогу — тільки адмін */}
      {isAdmin && (
      <div>
        <div className="rt-label" style={{ marginBottom: 8 }}>Автосинхронізація каталогу</div>
        <div className="rt-hint" style={{ marginBottom: 12 }}>
          Завантажує список товарів із сайту. Не змінює ціни/наявність.
        </div>
        {config ? (
          <>
            <div className="rt-toggle-wrap" onClick={() => setConfig(c => ({ ...c, auto_enabled: !c.auto_enabled }))} style={{ marginBottom: 12 }}>
              <div className={`rt-toggle ${config.auto_enabled ? 'rt-toggle--on' : ''}`}><div className="rt-toggle-knob" /></div>
              <span style={{ fontSize: 13 }}>
                {config.auto_enabled ? 'Увімкнено' : 'Вимкнено'}
              </span>
            </div>
            {config.auto_enabled && (
              <>
                <div className="rt-field" style={{ marginBottom: 12 }}>
                  <label className="rt-label">Інтервал запуску</label>
                  <select className="rt-input rt-select" value={config.interval_minutes}
                    onChange={e => setConfig(c => ({ ...c, interval_minutes: parseInt(e.target.value) }))}>
                    {INTERVAL_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="rt-field" style={{ marginBottom: 12 }}>
                  <label className="rt-label">Сторінок за запуск</label>
                  <select className="rt-input rt-select" value={config.pages_per_run ?? 5}
                    onChange={e => setConfig(c => ({ ...c, pages_per_run: parseInt(e.target.value) }))}>
                    {PAGES_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <div className="rt-hint">
                    Авто-синк продовжує з останнього offset — поступово обходить весь каталог
                  </div>
                </div>
              </>
            )}
            {config.last_sync_at && (
              <div className="rt-hint" style={{ marginBottom: 8 }}>
                Остання синхр.: {fmtDate(config.last_sync_at)}
                {config.last_sync_count != null && ` · ${config.last_sync_count} тов.`}
              </div>
            )}
            {config.last_error && (
              <div style={{ color: 'var(--rt-danger)', fontSize: 12, marginBottom: 8 }}>
                ⚠ {config.last_error}
              </div>
            )}
          </>
        ) : (
          <div className="rt-hint">Завантаження…</div>
        )}
        {error && <div className="rt-error" style={{ marginBottom: 8 }}>{error}</div>}
      </div>
      )}

      {/* Кнопка збереження (для адміна — зберігає всі налаштування; для юзера — тільки правила) */}
      <div style={{ marginTop: 16 }}>
        {error && !isAdmin && <div className="rt-error" style={{ marginBottom: 8 }}>{error}</div>}
        <button className="rt-btn rt-btn-sm rt-btn-primary" onClick={handleSaveConfig} disabled={saving || !config}>
          {saving ? 'Збереження…' : 'Зберегти налаштування'}
        </button>
      </div>

      </>)}

      {/* ── Вкладка: Тест-оновлення ── */}
      {isAdmin && cardTab === 'test' && (() => {
        const isProm = site.platform === 'prom';

        const loadVariants = async (productId) => {
          try {
            const r = await retailAPI.getSiteProductVariants(site.id, productId, { available_only: false });
            setTestVariants(r.data || []);
          } catch { setTestVariants([]); }
        };

        const selectProduct = (p) => {
          setTestSelected(p);
          setTestProductSearch(p.article_site || p.title_ua || p.title_ru || String(p.id));
          setTestProducts([]);
          setTestVariantId(null);
          setTestBaseOnly(false);
          setTestVariants([]);
          setTestResult(null);
          loadVariants(p.id);
        };

        const searchProducts = async (q) => {
          if (!q || q.length < 2) return;
          setTestProductsLoading(true);
          try {
            const r = await retailAPI.getSiteProducts(site.id, { q, limit: 30, linked: true });
            const items = r.data?.items || r.data || [];
            setTestProducts(items);
            // Автовибір якщо рівно 1 результат
            if (items.length === 1) {
              selectProduct(items[0]);
            }
          } catch { setTestProducts([]); } finally { setTestProductsLoading(false); }
        };

        const runTest = async () => {
          if (!testSelected) return;
          setTestRunning(true);
          setTestResult(null);
          setTestError('');
          try {
            const body = { site_product_id: testSelected.id };
            if (isProm && testSaveDb) body.save_to_db = true;
            if (testBaseOnly) body.base_only = true;
            else if (testVariantId !== null) body.variant_id = testVariantId;
            if (testPresence !== null) body.override_presence = testPresence;
            if (testPriceMode === 'off') {
              body.skip_price = true;
            } else if (testPriceMode === 'manual') {
              if (testPrice !== '')    body.override_price     = parseFloat(testPrice);
              else                     body.skip_price         = true;
              if (testOldPrice !== '') body.override_old_price = parseFloat(testOldPrice);
            }
            if (isProm && testDiscountMode === 'off') {
              body.override_discount_enabled = false;
            } else if (isProm && testDiscountMode === 'manual') {
              body.override_discount_enabled = true;
              body.override_discount_type        = testDiscountType;
              body.override_discount_value       = testDiscountValue === '' ? 0 : parseFloat(testDiscountValue);
              body.override_discount_description = testDiscountDesc || '';
              body.override_discount_date_end    = inputToDdmmyyyy(testDiscountEnd);
            }
            const r = await retailAPI.testUpdateProduct(site.id, body);
            setTestResult(r.data);
          } catch (e) {
            setTestError(e?.response?.data?.detail || e?.message || 'Помилка');
          } finally { setTestRunning(false); }
        };

        return (
          <div>
            <div className="rt-hint" style={{ marginBottom: 12 }}>
              Відправляє реальний запит на сайт. <strong>БД не змінюється</strong>{isProm ? ', якщо не поставлена галочка «Записати в БД» (нижче)' : ''}. Використовуй для перевірки коректності алгоритму.
            </div>

            {/* ── 1. Вибір товару ── */}
            <div style={{ marginBottom: 14 }}>
              <div className="rt-label" style={{ marginBottom: 6 }}>1. Товар</div>

              {!testSelected ? (
                <>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      className="rt-input rt-search"
                      style={{ flex: '1 1 200px', minWidth: 160 }}
                      placeholder="Артикул або назва…"
                      value={testProductSearch}
                      onChange={e => {
                        setTestProductSearch(e.target.value);
                        setTestProducts([]);
                      }}
                      onKeyDown={e => { if (e.key === 'Enter') searchProducts(testProductSearch); }}
                    />
                    <button
                      className="rt-btn rt-btn-sm rt-btn-primary"
                      disabled={testProductsLoading || testProductSearch.length < 2}
                      onClick={() => searchProducts(testProductSearch)}
                    >
                      {testProductsLoading ? '…' : '🔍 Знайти'}
                    </button>
                  </div>

                  {testProducts.length > 1 && (
                    <div style={{
                      border: '1px solid var(--lg-border)', borderRadius: 6, marginTop: 6,
                      maxHeight: 220, overflowY: 'auto', background: 'var(--bg, #fff)',
                    }}>
                      <div className="rt-hint" style={{ padding: '4px 10px', borderBottom: '1px solid var(--lg-border)' }}>
                        Знайдено {testProducts.length} — оберіть:
                      </div>
                      {testProducts.map(p => (
                        <div
                          key={p.id}
                          style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--lg-border)' }}
                          onClick={() => selectProduct(p)}
                        >
                          <strong>{p.article_site}</strong>
                          {p.all_sku && <span style={{ color: 'var(--muted,#888)', marginLeft: 6 }}>→ {p.all_sku}</span>}
                          {(p.title_ua || p.title_ru) && (
                            <span style={{ marginLeft: 8, color: 'var(--muted,#888)', fontSize: 11 }}>
                              {(p.title_ua || p.title_ru).slice(0, 60)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {!testProductsLoading && testProducts.length === 0 && testProductSearch.length >= 2 && (
                    <div className="rt-hint" style={{ marginTop: 6, color: '#c60' }}>
                      Нічого не знайдено. Натисни «🔍 Знайти» або перевір артикул.
                    </div>
                  )}
                </>
              ) : (
                <div style={{
                  padding: '8px 12px', background: 'var(--lg-hover,#f5f5f5)',
                  borderRadius: 6, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                }}>
                  <span>✅ <strong>{testSelected.article_site}</strong></span>
                  {testSelected.all_sku && <span style={{ color: 'var(--muted,#888)' }}>→ {testSelected.all_sku}</span>}
                  {(testSelected.title_ua || testSelected.title_ru) && (
                    <span style={{ color: 'var(--muted,#888)', fontSize: 11 }}>
                      {(testSelected.title_ua || testSelected.title_ru).slice(0, 60)}
                    </span>
                  )}
                  <button
                    className="rt-btn rt-btn-sm"
                    onClick={() => {
                      setTestSelected(null); setTestProductSearch(''); setTestProducts([]);
                      setTestVariants([]); setTestVariantId(null); setTestResult(null);
                    }}
                  >✕ Змінити</button>
                </div>
              )}
            </div>

            {/* ── 1б. Ціль оновлення ── */}
            {testSelected && (
              <div style={{ marginBottom: 14 }}>
                <div className="rt-label" style={{ marginBottom: 6 }}>
                  1б. Ціль оновлення
                  {testVariants.length > 0 && (
                    <span className="rt-hint" style={{ marginLeft: 8, fontWeight: 400 }}>
                      {testVariants.length} варіантів
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button
                    className={`rt-btn rt-btn-sm${(!testBaseOnly && testVariantId === null) ? ' rt-btn-primary' : ''}`}
                    onClick={() => { setTestBaseOnly(false); setTestVariantId(null); }}
                  >Всі варіанти{isProm ? ' + батьківський' : ''}</button>
                  {isProm && (
                    <button
                      className={`rt-btn rt-btn-sm${testBaseOnly ? ' rt-btn-primary' : ''}`}
                      onClick={() => { setTestBaseOnly(true); setTestVariantId(null); }}
                    >Тільки батьківський товар</button>
                  )}
                  {testVariants.map(v => (
                    <button
                      key={v.id}
                      className={`rt-btn rt-btn-sm${(!testBaseOnly && testVariantId === v.id) ? ' rt-btn-primary' : ''}`}
                      onClick={() => { setTestBaseOnly(false); setTestVariantId(v.id); }}
                      title={`article: ${v.article || '—'}  ext_id: ${v.external_id || '—'}`}
                      style={{ position: 'relative' }}
                    >
                      {v.size || v.article || v.external_id || `#${v.id}`}
                      <span style={{
                        display: 'inline-block', marginLeft: 4, width: 6, height: 6,
                        borderRadius: '50%', background: v.available ? '#4c4' : '#c44',
                        verticalAlign: 'middle',
                      }} />
                    </button>
                  ))}
                </div>
                <div className="rt-hint" style={{ marginTop: 4 }}>
                  🟢 є в наявності у БД · 🔴 немає. «Тільки батьківський товар» — не чіпає розміри-варіанти.
                </div>
              </div>
            )}

            {/* ── 2. Наявність / статус ── */}
            <div style={{ marginBottom: 14 }}>
              <div className="rt-label" style={{ marginBottom: 6 }}>2. Наявність</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { v: null,  label: 'Авто (з постачальника)' },
                  { v: 'available', label: '✅ В наявності' },
                  ...(isProm ? [{ v: 'ready_for_shipment', label: '📦 Готовий до відправки' }] : []),
                  { v: 'not_available', label: '❌ Немає в наявності' },
                ].map(opt => (
                  <button
                    key={String(opt.v)}
                    className={`rt-btn rt-btn-sm${testPresence === opt.v ? ' rt-btn-primary' : ''}`}
                    onClick={() => setTestPresence(opt.v)}
                  >{opt.label}</button>
                ))}
              </div>
              {testPresence === 'ready_for_shipment' && (
                <div className="rt-hint" style={{ marginTop: 4 }}>
                  Відправиться незалежно від того, що обрано в налаштуваннях сайту (presence_mode).
                </div>
              )}
            </div>

            {/* ── 3. Ціна ── */}
            <div style={{ marginBottom: 16 }}>
              <div className="rt-label" style={{ marginBottom: 6 }}>3. Ціна</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                <button
                  className={`rt-btn rt-btn-sm${testPriceMode === 'off' ? ' rt-btn-primary' : ''}`}
                  onClick={() => setTestPriceMode('off')}
                >Не відправляти</button>
                <button
                  className={`rt-btn rt-btn-sm${testPriceMode === 'auto' ? ' rt-btn-primary' : ''}`}
                  onClick={() => setTestPriceMode('auto')}
                >Авто (з налаштувань)</button>
                <button
                  className={`rt-btn rt-btn-sm${testPriceMode === 'manual' ? ' rt-btn-primary' : ''}`}
                  onClick={() => setTestPriceMode('manual')}
                >Вручну</button>
              </div>
              {testPriceMode === 'manual' && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span className="rt-hint">Ціна $</span>
                    <input
                      className="rt-input"
                      style={{ width: 90 }}
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={testPrice}
                      onChange={e => setTestPrice(e.target.value)}
                    />
                  </div>
                  {!isProm && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span className="rt-hint">Стара ціна $</span>
                      <input
                        className="rt-input"
                        style={{ width: 90 }}
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={testOldPrice}
                        onChange={e => setTestOldPrice(e.target.value)}
                      />
                    </div>
                  )}
                  <span className="rt-hint">(порожньо = не відправляти)</span>
                </div>
              )}
            </div>

            {/* ── 4. Знижка (тільки Prom) ── */}
            {isProm && (
              <div style={{ marginBottom: 16 }}>
                <div className="rt-label" style={{ marginBottom: 6 }}>4. Знижка</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                  <button
                    className={`rt-btn rt-btn-sm${testDiscountMode === 'auto' ? ' rt-btn-primary' : ''}`}
                    onClick={() => setTestDiscountMode('auto')}
                  >Авто (з налаштувань)</button>
                  <button
                    className={`rt-btn rt-btn-sm${testDiscountMode === 'manual' ? ' rt-btn-primary' : ''}`}
                    onClick={() => setTestDiscountMode('manual')}
                  >Вручну</button>
                  <button
                    className={`rt-btn rt-btn-sm${testDiscountMode === 'off' ? ' rt-btn-primary' : ''}`}
                    onClick={() => setTestDiscountMode('off')}
                  >Без знижки</button>
                </div>
                {testDiscountMode === 'manual' && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <select className="rt-input rt-select" style={{ width: 140 }}
                      value={testDiscountType}
                      onChange={e => setTestDiscountType(e.target.value)}>
                      <option value="percent">Відсоток (%)</option>
                      <option value="fixed">Фіксована сума</option>
                    </select>
                    <input className="rt-input" style={{ width: 90 }}
                      type="number" min="0" step={testDiscountType === 'percent' ? 0.1 : 10}
                      placeholder="0"
                      value={testDiscountValue}
                      onChange={e => setTestDiscountValue(e.target.value)} />
                    <input className="rt-input" style={{ width: 160 }}
                      placeholder="Опис (необов'язково)"
                      value={testDiscountDesc}
                      onChange={e => setTestDiscountDesc(e.target.value)} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span className="rt-hint">з</span>
                      <input type="date" className="rt-input" style={{ width: 150 }}
                        value={todayInputDate()} disabled
                        title="Дата початку знижки на Prom завжди сьогодні" />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span className="rt-hint">до (макс. 30 днів)</span>
                      <input type="date" className="rt-input" style={{ width: 150 }}
                        min={todayInputDate()} max={maxDiscountEndInputDate()}
                        value={testDiscountEnd}
                        onChange={e => setTestDiscountEnd(e.target.value)} />
                    </div>
                    <span className="rt-hint">(порожня дата = сьогодні +30д)</span>
                  </div>
                )}
              </div>
            )}

            {/* ── Запис у БД (Prom) ── */}
            {isProm && (
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={testSaveDb} onChange={e => setTestSaveDb(e.target.checked)} />
                <span>Записати в БД те саме, що пише звичайний синк (наявність і ціна прийнятих позицій). Автосинк потім сам виправить сайт, якщо тест відрізняється від даних постачальника.</span>
              </label>
            )}

            {/* ── Кнопка ── */}
            <button
              className="rt-btn rt-btn-primary"
              disabled={!testSelected || testRunning}
              onClick={runTest}
              style={{ minWidth: 160 }}
            >
              {testRunning ? '⏳ Відправка…' : '🚀 Відправити тест'}
            </button>

            {/* ── Результат ── */}
            {testError && (
              <div style={{ marginTop: 14, padding: '8px 12px', background: '#fee', borderRadius: 6, color: '#c00', fontSize: 13 }}>
                ❌ {testError}
              </div>
            )}

            {testResult && (
              <div style={{ marginTop: 14 }}>
                <div style={{ marginBottom: 8, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, color: testResult.ok ? '#2a7a2a' : '#c00' }}>
                    {testResult.ok ? '✅ Відправлено' : '❌ Помилка'}
                  </span>
                  <span className="rt-hint">Варіантів: {testResult.updated}</span>
                  {testResult.saved_to_db != null && (
                    <span className="rt-hint">У БД записано: {testResult.saved_to_db}</span>
                  )}
                  {testResult.in_stock_sizes?.length > 0 && (
                    <span className="rt-hint">Розміри постачальника: {testResult.in_stock_sizes.join(', ')}</span>
                  )}
                  {testResult.in_stock_sizes?.length === 0 && testPresence === null && (
                    <span className="rt-hint" style={{ color: '#c60' }}>⚠ Постачальник: немає в наявності</span>
                  )}
                  {testResult.site_price != null && (
                    <span className="rt-hint">Ціна → <strong>${testResult.site_price}</strong></span>
                  )}
                  {testResult.old_price != null && (
                    <span className="rt-hint">Стара → <strong>${testResult.old_price}</strong></span>
                  )}
                  {testResult.discount && (
                    <span className="rt-hint">
                      Знижка → <strong>{testResult.discount.value}{testResult.discount.type === 'percent' ? '%' : ' грн'}</strong>
                      {' '}({testResult.discount.date_start} – {testResult.discount.date_end})
                    </span>
                  )}
                  {isProm && !testResult.discount && (
                    <span className="rt-hint" style={{ opacity: .6 }}>Знижка: не активна</span>
                  )}
                </div>

                {/* Таблиця варіантів */}
                {testResult.rows?.length > 0 && (
                  <div style={{ overflowX: 'auto', marginBottom: 12 }}>
                    <table className="rt-table" style={{ fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th>{isProm ? 'Prom ID' : 'Артикул'}</th>
                          <th>Розмір</th>
                          <th>Було</th>
                          <th>Стало</th>
                          {isProm && <th>Presence</th>}
                          {!isProm && <th>Наявність (текст)</th>}
                          {(testResult.site_price != null) && <th>Ціна</th>}
                          {(testResult.old_price  != null) && <th>Стара</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {testResult.rows.map((row, i) => (
                          <tr key={i}>
                            <td style={{ fontFamily: 'monospace', fontSize: 11 }}>
                              {row.external_id || row.article}
                              {row.label === 'base_product' && <span style={{ color: 'var(--muted,#888)' }}> (base)</span>}
                            </td>
                            <td>{row.size || <span style={{ color: 'var(--muted,#888)' }}>—</span>}</td>
                            <td style={{ color: row.was_available ? '#2a7a2a' : '#c00' }}>
                              {row.was_available ? '✅ є' : '❌ нема'}
                            </td>
                            <td style={{ color: row.new_available ? '#2a7a2a' : '#c00', fontWeight: 600 }}>
                              {row.new_available ? '✅ є' : '❌ нема'}
                            </td>
                            <td style={{ fontFamily: 'monospace', fontSize: 11 }}>
                              {row.payload?.presence || row.payload?.presence_name || (row.payload?.display_in_showcase != null ? (row.payload.display_in_showcase ? 'В наявності' : 'Немає') : '—')}
                            </td>
                            {testResult.site_price != null && <td>${row.payload?.price ?? '—'}</td>}
                            {testResult.old_price  != null && <td>${row.payload?.price_old ?? '—'}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Raw API response */}
                <details style={{ marginTop: 4 }}>
                  <summary className="rt-hint" style={{ cursor: 'pointer' }}>Відповідь API сайту</summary>
                  <pre style={{
                    marginTop: 6, padding: '8px 10px', fontSize: 11, fontFamily: 'monospace',
                    background: 'var(--lg-hover,#f5f5f5)', borderRadius: 6,
                    maxHeight: 240, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                  }}>
                    {JSON.stringify(testResult.api_response, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Вкладка: Лог змін ── */}
      {cardTab === 'log' && (
        <div>
          {/* Пошук і фільтри */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              className="rt-input rt-search"
              style={{ flex: '1 1 180px', minWidth: 140 }}
              placeholder="Пошук за SKU або артикулом…"
              value={logSearch}
              onChange={e => setLogSearch(e.target.value)}
            />
            <div className="rt-filter-btns">
              <button className={`rt-btn rt-btn-sm${logAvailFilter === null  ? ' rt-btn-primary' : ''}`} onClick={() => setLogAvailFilter(null)}>Всі</button>
              <button className={`rt-btn rt-btn-sm${logAvailFilter === true  ? ' rt-btn-primary' : ''}`} onClick={() => setLogAvailFilter(true)}>→ є</button>
              <button className={`rt-btn rt-btn-sm${logAvailFilter === false ? ' rt-btn-primary' : ''}`} onClick={() => setLogAvailFilter(false)}>→ нема</button>
              <button className={`rt-btn rt-btn-sm${logAvailFilter === 'price' ? ' rt-btn-primary' : ''}`} onClick={() => setLogAvailFilter('price')}>💰 ціна</button>
            </div>
            <button className="rt-btn rt-btn-sm" onClick={async () => {
              setLogLoading(true);
              try { const r = await retailAPI.getStockLog(site.id, 200, logSearch.trim()); setStockLog(r.data); }
              catch {} finally { setLogLoading(false); }
            }}>↻</button>
          </div>

          {logLoading ? (
            <div className="rt-hint">Завантаження…</div>
          ) : stockLog && stockLog.length > 0 ? (() => {
            const q = logSearch.trim().toLowerCase();
            const filtered = stockLog.filter(r => {
              if (logAvailFilter === 'price') {
                if (r.new_price == null) return false;
              } else if (logAvailFilter !== null) {
                if (r.new_available !== logAvailFilter) return false;
              }
              if (!q) return true;
              return (r.all_sku || '').toLowerCase().includes(q)
                  || (r.article_site || '').toLowerCase().includes(q)
                  || (r.size || '').toLowerCase().includes(q);
            });
            return filtered.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table className="rt-table" style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th>Час</th>
                      <th>ALL SKU</th>
                      <th>Артикул сайту</th>
                      <th>Розмір</th>
                      <th>Наявність було</th>
                      <th>Наявність стало</th>
                      <th>Ціна було</th>
                      <th>Ціна стало</th>
                      <th>Помилка</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(r => (
                      <tr key={r.id} style={r.error ? { background: 'rgba(239,68,68,.06)' } : undefined}>
                        <td style={{ whiteSpace: 'nowrap', color: 'var(--muted,#888)' }}>
                          {r.created_at ? fmtDate(r.created_at) : '—'}
                        </td>
                        <td className="rt-cell-article">{r.all_sku || '—'}</td>
                        <td>{r.article_site || '—'}</td>
                        <td>{r.size || '—'}</td>
                        <td style={{ color: r.was_available ? '#16a34a' : '#ef4444' }}>
                          {r.new_available !== r.was_available ? (r.was_available ? 'є' : 'нема') : '—'}
                        </td>
                        <td style={{ fontWeight: 600, color: r.new_available ? '#16a34a' : '#ef4444' }}>
                          {r.new_available !== r.was_available ? (r.new_available ? 'є' : 'нема') : '—'}
                        </td>
                        <td style={{ color: 'var(--muted,#888)' }}>
                          {r.was_price != null ? `₴${Math.round(r.was_price)}` : '—'}
                        </td>
                        <td style={{ fontWeight: r.new_price != null ? 600 : 400,
                                     color: r.new_price != null
                                       ? (r.new_price > (r.was_price || 0) ? '#ef4444' : '#16a34a')
                                       : 'inherit' }}>
                          {r.new_price != null ? `₴${Math.round(r.new_price)}` : '—'}
                        </td>
                        <td style={{ color: '#ef4444', maxWidth: 260, whiteSpace: 'normal' }}>
                          {r.error || ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="rt-hint" style={{ marginTop: 6 }}>
                  {filtered.length} з {stockLog.length} записів
                </div>
              </div>
            ) : (
              <div className="rt-hint">Нічого не знайдено</div>
            );
          })() : stockLog && stockLog.length === 0 ? (
            <div className="rt-hint">Змін ще не було</div>
          ) : null}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// Вкладка "Під заміну" — Prom товари де розмір основного не в наявності
// ─────────────────────────────────────────────────────────────────────────────

function SubstitutionTab({ site, isAdmin }) {
  const [items,   setItems]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied,  setCopied]  = useState(false);

  // Домен сайту без протоколу: "https://krossy.com.ua" → "krossy.com.ua"
  const cleanDomain = (site.domain || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
  const feedUrl     = `https://alldrop.ink/api/v1/retail/feed/${cleanDomain}`;

  async function load() {
    setLoading(true);
    try {
      const r = await retailAPI.getSubstitutionList(site.id);
      setItems(r.data || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }

  function copyFeedUrl() {
    navigator.clipboard.writeText(feedUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  if (items === null && !loading) load();

  return (
    <div style={{ padding: '16px 0' }}>

      {/* ── Блок фіду (тільки адмін) ──────────────────────────────── */}
      {isAdmin && <div style={{
        background: 'var(--bg-card, #f8fafc)',
        border: '1px solid var(--border, #e2e8f0)',
        borderRadius: 8,
        padding: '14px 16px',
        marginBottom: 20,
      }}>
        <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 13 }}>
          XML-фід для автозаміни розміру в Prom
        </div>

        {/* URL посилання */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <code style={{
            flex: 1,
            fontSize: 11,
            background: 'var(--bg, #fff)',
            border: '1px solid var(--border, #e2e8f0)',
            borderRadius: 4,
            padding: '5px 8px',
            wordBreak: 'break-all',
            color: 'var(--text, #1e293b)',
          }}>{feedUrl}</code>
          <button className="rt-btn rt-btn-sm" onClick={copyFeedUrl} style={{ whiteSpace: 'nowrap' }}>
            {copied ? '✓ Скопійовано' : '📋 Копіювати'}
          </button>
        </div>

        {/* Інструкція */}
        <div style={{ fontSize: 12, color: 'var(--text-muted, #64748b)' }}>
          <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text, #1e293b)' }}>
            Як налаштувати імпорт в кабінеті Prom:
          </div>
          <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
            <li>Зайти в кабінет Prom → <b>Товари → Імпорт/Експорт</b></li>
            <li>Вибрати <b>Імпорт з файлу за посиланням</b>, вставити URL вище</li>
            <li>У розділі <b>Інформація, яку потрібно оновити</b> — залишити тільки <b>Характеристики</b></li>
            <li>Увімкнути <b>Тільки оновлення</b> (не буде створювати нові товари)</li>
            <li>Статус товарів, яких немає у файлі → <b>Залишити без змін</b></li>
            <li>Встановити частоту оновлення — <b>кожну годину</b></li>
          </ol>
          <div style={{
            marginTop: 10,
            padding: '8px 10px',
            background: 'var(--warn-bg, #fffbeb)',
            border: '1px solid var(--warn-border, #fcd34d)',
            borderRadius: 6,
            fontSize: 11,
            color: 'var(--warn-text, #92400e)',
            lineHeight: 1.6,
          }}>
            ⚠️ <b>Перед першим запуском:</b> перевірте всі галочки ще раз — особливо «Тільки оновлення» та «Залишити без змін». Зробіть резервну копію товарів у Prom (Товари → Експорт) на випадок якщо щось піде не так.
          </div>
        </div>
      </div>}

      {/* ── Таблиця товарів під заміну ────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div className="rt-label" style={{ margin: 0 }}>Основний під заміну</div>
        <button className="rt-btn rt-btn-sm" onClick={load} disabled={loading}>↻</button>
        <span className="rt-hint">Товари, де розмір основного товару не в наявності, але є інші розміри</span>
      </div>
      {loading ? (
        <div className="rt-hint">Завантаження…</div>
      ) : !items || items.length === 0 ? (
        <div className="rt-hint">Список порожній — всі основні товари в наявності (або функція не увімкнена в налаштуваннях)</div>
      ) : (
        <div className="rt-tbl-wrap">
          <table className="rt-table" style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th>Артикул сайту</th>
                <th>ALL SKU</th>
                <th>Розмір основного</th>
                <th>Доступні розміри</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i}>
                  <td className="rt-cell-article">{it.article_site || '—'}</td>
                  <td>{it.all_sku || '—'}</td>
                  <td style={{ color: '#ef4444', fontWeight: 600 }}>{it.base_size || '—'}</td>
                  <td>
                    {(it.available_sizes || []).map(s => (
                      <span key={s} className="rt-size-chip rt-size-chip--ok" style={{ marginRight: 4 }}>{s}</span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="rt-hint" style={{ marginTop: 6 }}>{items.length} товарів</div>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Вкладка "Прив'язки" — персистентні article_site → all_sku
// ─────────────────────────────────────────────────────────────────────────────

function MappingsTab({ site }) {
  const [mappings, setMappings] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [search,   setSearch]   = useState('');

  // Прямі прив'язки: товар постачальника (без ALL SKU) → артикул сайту
  const [directLinks,   setDirectLinks]   = useState(null);
  const [dlLoading,     setDlLoading]     = useState(false);
  const [dlArticle,     setDlArticle]     = useState('');
  const [dlQuery,       setDlQuery]       = useState('');
  const [dlResults,     setDlResults]     = useState([]);
  const [dlSearching,   setDlSearching]   = useState(false);
  const [dlPicked,      setDlPicked]      = useState(null);
  const [dlSaving,      setDlSaving]      = useState(false);
  const [dlExists,      setDlExists]      = useState(null); // null=не перевірено, {exists,title}
  const [dlExistsCheck, setDlExistsCheck] = useState(false);
  const dlArticleTimerRef = useRef(null);

  async function load() {
    setLoading(true);
    try {
      const r = await retailAPI.getMappings(site.id);
      setMappings(r.data || []);
    } catch { setMappings([]); }
    finally { setLoading(false); }
  }

  async function loadDirectLinks() {
    setDlLoading(true);
    try {
      const r = await retailAPI.getDirectLinks(site.id);
      setDirectLinks(r.data || []);
    } catch { setDirectLinks([]); }
    finally { setDlLoading(false); }
  }

  async function handleDlSearch(q) {
    setDlQuery(q);
    setDlPicked(null);
    if (q.trim().length < 3) { setDlResults([]); return; }
    setDlSearching(true);
    try {
      const r = await retailAPI.searchSupplierItems(q.trim());
      setDlResults(r.data || []);
    } catch { setDlResults([]); }
    finally { setDlSearching(false); }
  }

  function handleDlArticleChange(val) {
    setDlArticle(val);
    setDlExists(null);
    if (dlArticleTimerRef.current) clearTimeout(dlArticleTimerRef.current);
    const trimmed = val.trim();
    if (!trimmed) return;
    dlArticleTimerRef.current = setTimeout(async () => {
      setDlExistsCheck(true);
      try {
        const r = await retailAPI.checkArticleExists(site.id, trimmed);
        setDlExists(r.data);
      } catch { setDlExists(null); }
      finally { setDlExistsCheck(false); }
    }, 400);
  }

  async function handleDlSave() {
    if (!dlPicked || !dlArticle.trim()) return;
    // Артикул не знайдено в нашій копії сайту — можлива друкарська помилка.
    // Не блокуємо (товар міг бути ще не просканований), але перепитуємо.
    if (dlExists && dlExists.exists === false) {
      if (!window.confirm(
        `Артикул «${dlArticle.trim()}» не знайдено в нашій копії сайту ${site.name || ''}.\n` +
        `Можливо, це друкарська помилка — перевірте артикул.\n\n` +
        `Прив'язати все одно (наприклад, товар ще не синхронізовано)?`
      )) return;
    }
    setDlSaving(true);
    try {
      await retailAPI.createDirectLink(site.id, {
        article_site: dlArticle.trim(),
        supplier_item_id: dlPicked.id,
      });
      setDlArticle(''); setDlQuery(''); setDlResults([]); setDlPicked(null); setDlExists(null);
      loadDirectLinks();
    } catch { alert('Помилка збереження прив\'язки'); }
    finally { setDlSaving(false); }
  }

  async function handleDlDelete(id) {
    if (!window.confirm('Видалити пряму прив\'язку?')) return;
    try {
      await retailAPI.deleteDirectLink(site.id, id);
      setDirectLinks(prev => prev.filter(l => l.id !== id));
    } catch { alert('Помилка видалення'); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Видалити прив\'язку?')) return;
    try {
      await retailAPI.deleteMapping(site.id, id);
      setMappings(prev => prev.filter(m => m.id !== id));
    } catch { alert('Помилка видалення'); }
  }

  if (mappings === null && !loading) load();
  if (directLinks === null && !dlLoading) loadDirectLinks();

  const q = search.trim().toLowerCase();
  const filtered = (mappings || []).filter(m =>
    !q || m.article_site.toLowerCase().includes(q) || m.all_sku.toLowerCase().includes(q)
  );

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div className="rt-label" style={{ margin: 0 }}>Персистентні прив'язки</div>
        <button className="rt-btn rt-btn-sm" onClick={load} disabled={loading}>↻</button>
      </div>
      <div className="rt-hint" style={{ marginBottom: 10 }}>
        Зберігаються незалежно від синхронізації. При очищенні і ресинку — застосовуються автоматично.
      </div>
      <input className="rt-input" placeholder="Пошук…" value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: 10, maxWidth: 320 }}
      />
      {loading ? (
        <div className="rt-hint">Завантаження…</div>
      ) : !mappings || mappings.length === 0 ? (
        <div className="rt-hint">Прив'язок поки немає. Вони з'являться автоматично коли ви зв'яжете товар з ALL SKU.</div>
      ) : (
        <div className="rt-tbl-wrap" style={{ maxWidth: 480 }}>
          <table className="rt-table" style={{ fontSize: 12, width: 'auto' }}>
            <thead>
              <tr>
                <th style={{ width: 180 }}>Артикул сайту</th>
                <th style={{ width: 180 }}>ALL SKU</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(m => (
                <tr key={m.id}>
                  <td className="rt-cell-article">{m.article_site}</td>
                  <td>{m.all_sku || <span style={{ color: 'var(--lg-text-3)', fontStyle: 'italic' }}>не прив'язувати</span>}</td>
                  <td>
                    <button className="rt-btn rt-btn-xs rt-btn-danger" title="Видалити — дозволити автопідбір знову" onClick={() => handleDelete(m.id)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="rt-hint" style={{ marginTop: 6 }}>{filtered.length} з {mappings.length} прив'язок</div>
        </div>
      )}

      <div style={{ borderTop: '1px solid var(--lg-border)', margin: '24px 0 16px' }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div className="rt-label" style={{ margin: 0 }}>🔗 Прямі прив'язки (товар постачальника → артикул сайту)</div>
        <button className="rt-btn rt-btn-sm" onClick={loadDirectLinks} disabled={dlLoading}>↻</button>
      </div>
      <div className="rt-hint" style={{ marginBottom: 10 }}>
        Для одиничних товарів без нормального каталожного SKU (напр. уцінка/шлюб) —
        прив'язує ОДИН конкретний рядок постачальника напряму до артикулу на сайті,
        в обхід ALL SKU. Наявність/ціна цього артикулу відтепер рахуються тільки з
        цього постачальника.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14, maxWidth: 520 }}>
        <input className="rt-input" placeholder="Пошук товару постачальника (частина артикулу/назви)…"
          value={dlQuery} onChange={e => handleDlSearch(e.target.value)}
        />
        {dlSearching && <div className="rt-hint">Пошук…</div>}
        {!dlSearching && dlResults.length > 0 && !dlPicked && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 220, overflowY: 'auto',
                        border: '1px solid var(--lg-border)', borderRadius: 6 }}>
            {dlResults.map(r => (
              <button key={r.id} onClick={() => setDlPicked(r)}
                style={{ textAlign: 'left', padding: '6px 8px', fontSize: 12, background: 'none', border: 'none',
                          borderBottom: '1px solid var(--lg-border)', cursor: 'pointer', color: 'var(--lg-text)' }}>
                <div style={{ fontWeight: 500 }}>{r.supplier_code}</div>
                <div style={{ opacity: 0.7 }}>
                  {r.supplier_name} · {r.price ? `${r.price}$` : '—'} · {r.sizes || 'без розмірів'}
                  {r.product_sku ? ` · SKU: ${r.product_sku}` : ''}
                </div>
              </button>
            ))}
          </div>
        )}
        {dlPicked && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
                        padding: '6px 8px', background: 'var(--lg-hover, rgba(0,0,0,.04))', borderRadius: 6 }}>
            <span style={{ flex: 1 }}>✓ {dlPicked.supplier_code} ({dlPicked.supplier_name})</span>
            <button onClick={() => { setDlPicked(null); setDlQuery(''); setDlResults([]); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--rt-danger, #c0392b)' }}>✕</button>
          </div>
        )}
        <input className="rt-input" placeholder="Артикул на сайті (напр. 1635200233731)"
          value={dlArticle} onChange={e => handleDlArticleChange(e.target.value)}
        />
        {dlExistsCheck && <div className="rt-hint">Перевірка артикулу…</div>}
        {!dlExistsCheck && dlExists && dlExists.exists && (
          <div className="rt-hint" style={{ color: '#22c55e' }}>
            ✓ Знайдено на сайті{dlExists.title ? `: ${dlExists.title}` : ''}
          </div>
        )}
        {!dlExistsCheck && dlExists && dlExists.exists === false && (
          <div className="rt-hint" style={{ color: '#f59e0b' }}>
            ⚠ Такого артикулу поки немає в нашій копії сайту — перевірте, чи немає помилки
          </div>
        )}
        <button className="rt-btn rt-btn-primary" style={{ alignSelf: 'flex-start' }}
          disabled={!dlPicked || !dlArticle.trim() || dlSaving}
          onClick={handleDlSave}
        >
          {dlSaving ? 'Збереження…' : '+ Прив\'язати'}
        </button>
      </div>

      {dlLoading ? (
        <div className="rt-hint">Завантаження…</div>
      ) : !directLinks || directLinks.length === 0 ? (
        <div className="rt-hint">Прямих прив'язок поки немає.</div>
      ) : (
        <div className="rt-tbl-wrap" style={{ maxWidth: 620 }}>
          <table className="rt-table" style={{ fontSize: 12, width: 'auto' }}>
            <thead>
              <tr>
                <th style={{ width: 160 }}>Артикул сайту</th>
                <th style={{ width: 260 }}>Товар постачальника</th>
                <th style={{ width: 90 }}>Стан</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {directLinks.map(l => (
                <tr key={l.id}>
                  <td className="rt-cell-article">{l.article_site}</td>
                  <td>{l.supplier_code} <span style={{ opacity: 0.6 }}>({l.supplier_name})</span></td>
                  <td>
                    {l.last_available === null ? '—' : l.last_available
                      ? <span style={{ color: '#22c55e' }}>в наявності</span>
                      : <span style={{ color: '#ef4444' }}>нема</span>}
                  </td>
                  <td>
                    <button className="rt-btn rt-btn-xs rt-btn-danger" title="Видалити" onClick={() => handleDlDelete(l.id)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="rt-hint" style={{ marginTop: 6 }}>{directLinks.length} прив'язок</div>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Вкладка "Розміри ⚠" — товари з відсутнім або підозрілим розміром
// ─────────────────────────────────────────────────────────────────────────────

const ALL_SIZES_LIMIT = 50;

function SuspiciousSizesTab({ site }) {
  // ── Проблемні розміри — завантажуємо ВСЕ один раз, далі фільтруємо локально ──
  const [allItems, setAllItems] = useState(null);  // всі записи з сервера
  const [loading,  setLoading]  = useState(false);
  const [filter,   setFilter]   = useState('all'); // 'all' | 'pending' | 'missing' | 'suspicious'
  const [page,     setPage]     = useState(1);
  const [perPage,  setPerPage]  = useState(200);

  // ── Всі товари ──
  const [allProducts, setAllProducts] = useState(null);
  const [allLoading,  setAllLoading]  = useState(false);
  const [allTotal,    setAllTotal]    = useState(0);
  const [allOffset,   setAllOffset]   = useState(0);
  const [allSearch,   setAllSearch]   = useState('');

  async function load() {
    setLoading(true);
    try {
      // Завантажуємо все за один запит (без фільтра issue, per_page=0 = без обмеження)
      const r = await retailAPI.getSuspiciousSizes(site.id, { page: 1, per_page: 0, issue: '' });
      setAllItems(r.data?.items || []);
    } catch { setAllItems([]); }
    finally { setLoading(false); }
  }

  async function loadAll(offset = 0, search = allSearch) {
    setAllLoading(true);
    try {
      const r = await retailAPI.getSiteProducts(site.id, { offset, limit: ALL_SIZES_LIMIT, q: search });
      setAllProducts(r.data?.items || []);
      setAllTotal(r.data?.total || 0);
      setAllOffset(offset);
    } catch { setAllProducts([]); }
    finally { setAllLoading(false); }
  }

  if (allItems === null && !loading)       load();
  if (allProducts === null && !allLoading) loadAll(0);

  // Локальна фільтрація — миттєва, без запиту.
  // issue знаходиться всередині bad_variants[], а не на рівні товару.
  const matchesFilter = (it, f) =>
    f === 'all' || (it.bad_variants || []).some(v => v.issue === f);

  const filtered   = (allItems || []).filter(it => matchesFilter(it, filter));
  const total      = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const safeP      = Math.min(page, totalPages);
  const items      = filtered.slice((safeP - 1) * perPage, safeP * perPage);

  return (
    <div style={{ padding: '16px 0' }}>

      {/* ── Заголовок ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <div className="rt-label" style={{ margin: 0 }}>Підозрілі розміри</div>
        <button className="rt-btn rt-btn-sm" onClick={() => { setAllItems(null); setAllProducts(null); }}
          disabled={loading || allLoading}>↻</button>
        <span className="rt-hint">
          «Не отримано» — GQL ще не запускався · «Немає атрибуту» — запустили, розміру немає · «Підозрілий» — поза 30–50 · «Дублікат» — розміри різняться, але один повторюється, АБО всі варіанти мають один розмір, хоча для SKU відомий реальний розмір з каталогу (підозра на баг вибору атрибута)
        </span>
      </div>

      {/* ── Фільтри ── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {[
          { key: 'all',        label: 'Всі' },
          { key: 'pending',    label: 'Не отримано (GQL)' },
          { key: 'missing',    label: 'Немає атрибуту' },
          { key: 'suspicious', label: 'Підозрілий розмір' },
          { key: 'duplicate',  label: 'Дублікат розміру' },
        ].map(f => {
          const cnt = (allItems || []).filter(it => matchesFilter(it, f.key)).length;
          return (
            <button key={f.key}
              className={`rt-btn rt-btn-sm${filter === f.key ? ' rt-btn-active' : ''}`}
              onClick={() => { setFilter(f.key); setPage(1); }}
              style={filter === f.key ? { background: 'var(--accent)', color: '#fff' } : {}}
              disabled={loading}
            >
              {f.label}
              {!loading && <span style={{ marginLeft: 4, opacity: .7, fontSize: 11 }}>({cnt})</span>}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="rt-hint">Завантаження…</div>
      ) : total === 0 ? (
        <div className="rt-hint" style={{ marginBottom: 24 }}>
          {filter === 'all' ? 'Підозрілих розмірів не знайдено — все гаразд!' : 'У цьому фільтрі немає товарів'}
        </div>
      ) : (
        <>
          {/* ── Панель пагінації (зверху) ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <button className="rt-btn rt-btn-sm" disabled={safeP <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}>←</button>
            <span className="rt-hint" style={{ fontWeight: 500 }}>
              Сторінка {safeP} з {totalPages} · {total} товарів
            </span>
            <button className="rt-btn rt-btn-sm" disabled={safeP >= totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}>→</button>
            <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-secondary)' }}>Показувати:</span>
            {[50, 100, 200, 500].map(n => (
              <button key={n}
                className={`rt-btn rt-btn-sm${perPage === n ? ' rt-btn-active' : ''}`}
                style={perPage === n ? { background: 'var(--accent)', color: '#fff' } : {}}
                onClick={() => { setPerPage(n); setPage(1); }}
                disabled={loading}
              >{n}</button>
            ))}
          </div>

          <div className="rt-tbl-wrap" style={{ marginBottom: 8 }}>
            <table className="rt-table" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th>Артикул сайту</th>
                  <th>Назва</th>
                  <th>Prom ID</th>
                  <th>Розмір</th>
                  <th>Проблема</th>
                  <th>ID варіанта</th>
                </tr>
              </thead>
              <tbody>
                {items.flatMap(it => {
                  const visibleVars = filter === 'all'
                    ? it.bad_variants
                    : it.bad_variants.filter(v => v.issue === filter);
                  return visibleVars.map((v, vi) => (
                    <tr key={`${it.site_product_id}-${vi}`}>
                      <td className="rt-cell-article">{it.article_site || '—'}</td>
                      <td style={{ maxWidth: 220 }}>{it.title}</td>
                      <td>
                        {it.prom_id
                          ? <a href={promProductUrl(site.domain, it.prom_id)}
                               target="_blank" rel="noopener noreferrer"
                               style={{ color: 'inherit', textDecoration: 'none', fontFamily: 'monospace', fontSize: 11 }}
                               onMouseEnter={e => e.currentTarget.style.textDecoration='underline'}
                               onMouseLeave={e => e.currentTarget.style.textDecoration='none'}
                            >{it.prom_id} 🔗</a>
                          : '—'}
                      </td>
                      <td style={{
                        color: v.issue === 'missing'   ? '#ef4444'
                             : v.issue === 'pending'    ? '#6366f1'
                             : v.issue === 'duplicate'  ? '#db2777'
                             : '#f59e0b',
                        fontWeight: 600,
                      }}>
                        {v.size || '—'}
                      </td>
                      <td>
                        <span style={{
                          display: 'inline-block', padding: '1px 6px', borderRadius: 3, fontSize: 11,
                          background: v.issue === 'missing'   ? '#fee2e2'
                                    : v.issue === 'pending'    ? '#ede9fe'
                                    : v.issue === 'duplicate'  ? '#fce7f3'
                                    : '#fef3c7',
                          color:      v.issue === 'missing'   ? '#b91c1c'
                                    : v.issue === 'pending'    ? '#4338ca'
                                    : v.issue === 'duplicate'  ? '#9d174d'
                                    : '#92400e',
                        }}>
                          {v.issue === 'missing'   ? 'немає атрибуту'
                         : v.issue === 'pending'    ? 'не отримано'
                         : v.issue === 'duplicate'  ? 'дублікат розміру'
                         : 'підозрілий'}
                        </span>
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-secondary)' }}>
                        {v.external_id || '—'}
                      </td>
                    </tr>
                  ));
                })}
              </tbody>
            </table>
          </div>

          {/* ── Пагінація (знизу теж) ── */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
              <button className="rt-btn rt-btn-sm" disabled={safeP <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}>←</button>
              <span className="rt-hint">Сторінка {safeP} з {totalPages}</span>
              <button className="rt-btn rt-btn-sm" disabled={safeP >= totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}>→</button>
            </div>
          )}
        </>
      )}

    </div>
  );
}


// Вкладка управління користувачами сайту (тільки для адміна)
// ─────────────────────────────────────────────────────────────────────────────

function SiteUsersTab({ site }) {
  const [allUsers,    setAllUsers]    = useState(null);  // всі активні не-адміни
  const [grantedIds,  setGrantedIds]  = useState(null);  // Set<userId> — поточний стан в БД
  const [search,      setSearch]      = useState('');
  const [dropOpen,    setDropOpen]    = useState(false);
  const [busy,        setBusy]        = useState(null);  // userId в процесі
  const [error,       setError]       = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    Promise.all([
      authAPI.getUsers(),
      retailAPI.getSiteUsers(site.id),
    ]).then(([ur, sr]) => {
      setAllUsers((ur.data || []).filter(u => u.is_active && !u.is_admin));
      setGrantedIds(new Set(sr.data.user_ids || []));
    }).catch(() => setError('Помилка завантаження'));
  }, [site.id]);

  const granted = allUsers ? allUsers.filter(u => grantedIds?.has(u.id)) : [];

  const q = search.trim().toLowerCase();
  const suggestions = !allUsers ? [] : allUsers.filter(u =>
    !grantedIds?.has(u.id) &&
    (u.email.toLowerCase().includes(q) || (u.full_name || '').toLowerCase().includes(q))
  );

  const handleGrant = async (u) => {
    setBusy(u.id);
    setError('');
    try {
      await retailAPI.grantSiteAccess(site.id, u.id);
      setGrantedIds(prev => new Set([...prev, u.id]));
      setSearch('');
      setDropOpen(false);
    } catch {
      setError('Помилка при додаванні');
    } finally {
      setBusy(null);
    }
  };

  const handleRevoke = async (userId) => {
    setBusy(userId);
    setError('');
    try {
      await retailAPI.revokeSiteAccess(site.id, userId);
      setGrantedIds(prev => { const n = new Set(prev); n.delete(userId); return n; });
    } catch {
      setError('Помилка при видаленні');
    } finally {
      setBusy(null);
    }
  };

  if (error && !allUsers) return <div className="rt-hint" style={{ color: '#ef4444', padding: 16 }}>{error}</div>;
  if (!allUsers || !grantedIds) return <div className="rt-hint" style={{ padding: 16 }}>Завантаження...</div>;

  return (
    <div style={{ padding: '16px 0', maxWidth: 480 }}>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
        Додайте користувачів, які матимуть доступ до цього сайту. Якщо список порожній — сайт
        недоступний жодному користувачу (крім адміністратора).
      </div>

      {/* Поточні користувачі з доступом */}
      {granted.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
          {granted.map(u => (
            <div key={u.id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 12px', borderRadius: 8,
              background: 'rgba(34,197,94,.07)',
              border: '1px solid rgba(34,197,94,.25)',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, fontSize: 13 }}>{u.full_name || u.email}</div>
                {u.full_name && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{u.email}</div>}
              </div>
              <button
                onClick={() => handleRevoke(u.id)}
                disabled={busy === u.id}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 18, lineHeight: 1, color: 'var(--text-secondary)',
                  padding: '2px 4px', flexShrink: 0,
                }}
                title="Видалити доступ"
              >
                {busy === u.id ? '…' : '×'}
              </button>
            </div>
          ))}
        </div>
      )}

      {granted.length === 0 && (
        <div style={{
          marginBottom: 16, padding: '10px 14px', borderRadius: 8, fontSize: 13,
          background: 'rgba(239,68,68,.07)', border: '1px solid rgba(239,68,68,.2)',
          color: 'var(--text-secondary)',
        }}>
          Жодного користувача не додано — сайт недоступний нікому
        </div>
      )}

      {/* Поле пошуку */}
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          className="rt-input"
          placeholder="Знайти користувача за email або ім'ям..."
          value={search}
          style={{ width: '100%' }}
          onChange={e => { setSearch(e.target.value); setDropOpen(true); }}
          onFocus={() => setDropOpen(true)}
          onBlur={() => setTimeout(() => setDropOpen(false), 150)}
        />

        {dropOpen && search.trim().length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
            background: 'var(--bg-card, #fff)',
            border: '1px solid var(--border-color, #e5e7eb)',
            borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,.12)',
            maxHeight: 240, overflowY: 'auto', marginTop: 4,
          }}>
            {suggestions.length === 0 ? (
              <div style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-secondary)' }}>
                Нічого не знайдено
              </div>
            ) : suggestions.map(u => (
              <button
                key={u.id}
                onMouseDown={() => handleGrant(u)}
                disabled={busy === u.id}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '9px 14px', background: 'none', border: 'none',
                  cursor: 'pointer', fontSize: 13,
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover, rgba(0,0,0,.04))'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                <span style={{ fontWeight: 500 }}>{u.full_name || u.email}</span>
                {u.full_name && <span style={{ color: 'var(--text-secondary)', marginLeft: 8, fontSize: 12 }}>{u.email}</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>{error}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Детальна вкладка сайту з під-табами
// ─────────────────────────────────────────────────────────────────────────────

function SiteDetailTab({ site, sites, onBack }) {
  const [subTab, setSubTab] = useState('products');
  const [unlinkedCatsCount, setUnlinkedCatsCount] = useState(0);
  const [missingCount, setMissingCount] = useState(null);  // кількість відсутніх товарів для бейджа
  const [updateDirty, setUpdateDirty] = useState(false);   // незбережені зміни у вкладці "Оновлення"
  // Фільтр бренд/модель у "Аналіз" — тримаємо тут (а не в самому AnalysisTab),
  // бо під-вкладка розмонтовується при переключенні на іншу й назад — без
  // цього вибраний бренд/модель щоразу скидався.
  const [analysisBrand, setAnalysisBrand] = useState('');
  const [analysisModel, setAnalysisModel] = useState('');
  useEffect(() => { setAnalysisBrand(''); setAnalysisModel(''); }, [site.id]);
  const currentUser = useAuthStore((state) => state.user);
  const isAdmin = !!(currentUser?.is_admin && !currentUser?.impersonated_by);
  const hasSubstitutionPerm = !!(currentUser?.permissions?.includes('substitution'));

  // Переключення під-вкладки з попередженням, якщо є незбережені зміни налаштувань
  const goTab = (t) => {
    if (t === subTab) return;
    if (updateDirty && subTab === 'update') {
      if (!window.confirm('У налаштуваннях є незбережені зміни. Перейти без збереження?')) return;
    }
    setSubTab(t);
  };
  const handleBack = () => {
    if (updateDirty && subTab === 'update') {
      if (!window.confirm('У налаштуваннях є незбережені зміни. Перейти без збереження?')) return;
    }
    onBack();
  };

  // Завантажуємо кількість відсутніх товарів одразу — щоб бейдж на "Аналіз" був видний без входу у вкладку
  useEffect(() => {
    retailAPI.getMissingProducts(site.id, { offset: 0, limit: 1 })
      .then(r => setMissingCount(r.data?.total ?? 0))
      .catch(() => {});
  }, [site.id]);

  return (
    <div>
      <div className="rt-breadcrumb">
        <button className="rt-btn rt-btn-sm" onClick={handleBack}>← Сайти</button>
        <span className="rt-breadcrumb-sep">/</span>
        <span className="rt-breadcrumb-name">{site.name}</span>
        <a
          className="rt-breadcrumb-domain"
          href={site.domain?.match(/^https?:\/\//) ? site.domain : `https://${site.domain}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none' }}
          onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
          onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
        >{site.domain}</a>
      </div>
      <div className="rt-tabs rt-tabs--sub">
        <button className={`rt-tab ${subTab === 'products' ? 'rt-tab--active' : ''}`} onClick={() => goTab('products')}>Взуття</button>
        <button className={`rt-tab ${subTab === 'clothing' ? 'rt-tab--active' : ''}`} onClick={() => goTab('clothing')}>Одяг</button>
        <button className={`rt-tab ${subTab === 'analysis' ? 'rt-tab--active' : ''}`} onClick={() => goTab('analysis')}>
          Аналіз
          {missingCount > 0 && (
            <span style={{
              marginLeft: 5, background: subTab === 'analysis' ? 'rgba(255,255,255,0.3)' : '#ef4444',
              color: '#fff', borderRadius: 9, padding: '1px 6px', fontSize: 11, fontWeight: 700, verticalAlign: 'middle',
            }}>{missingCount}</span>
          )}
        </button>
        <button className={`rt-tab ${subTab === 'update'   ? 'rt-tab--active' : ''}`} onClick={() => goTab('update')}>
          Оновлення{updateDirty && <span title="Є незбережені зміни" style={{ marginLeft: 4, color: '#ef4444' }}>●</span>}
        </button>
        {site.platform === 'prom' && (isAdmin || hasSubstitutionPerm || site.extra?.show_substitution) && (
          <button className={`rt-tab ${subTab === 'substitution' ? 'rt-tab--active' : ''}`} onClick={() => goTab('substitution')}>Під заміну</button>
        )}
        {site.platform === 'prom' && (
          <button className={`rt-tab ${subTab === 'suspicious' ? 'rt-tab--active' : ''}`} onClick={() => goTab('suspicious')}>Розміри ⚠</button>
        )}
        {isAdmin && (
          <button className={`rt-tab ${subTab === 'mappings' ? 'rt-tab--active' : ''}`} onClick={() => goTab('mappings')}>Прив'язки</button>
        )}
        <button className={`rt-tab ${subTab === 'site-cats' ? 'rt-tab--active' : ''}`} onClick={() => goTab('site-cats')}>
          Категорії{unlinkedCatsCount > 0 && (
            <span style={{
              marginLeft: 5, background: '#f59e0b', color: '#fff',
              borderRadius: 9, padding: '1px 6px', fontSize: 11, fontWeight: 700, verticalAlign: 'middle',
            }}>{unlinkedCatsCount}</span>
          )}
        </button>
        {isAdmin && (
          <button className={`rt-tab ${subTab === 'users' ? 'rt-tab--active' : ''}`} onClick={() => goTab('users')}>Доступ</button>
        )}
      </div>
      {subTab === 'products'     && <RetailErrorBoundary><SiteProductsTab   site={site} /></RetailErrorBoundary>}
      {subTab === 'analysis'     && <RetailErrorBoundary><AnalysisTab        site={site}
        filterBrand={analysisBrand} setFilterBrand={setAnalysisBrand}
        filterModel={analysisModel} setFilterModel={setAnalysisModel} /></RetailErrorBoundary>}
      {subTab === 'update'       && <RetailErrorBoundary><SiteUpdateCard     site={site} sites={sites} onDirtyChange={setUpdateDirty} /></RetailErrorBoundary>}
      {subTab === 'substitution' && (isAdmin || hasSubstitutionPerm || site.extra?.show_substitution) && <RetailErrorBoundary><SubstitutionTab site={site} isAdmin={isAdmin} /></RetailErrorBoundary>}
      {subTab === 'suspicious'   && <RetailErrorBoundary><SuspiciousSizesTab site={site} /></RetailErrorBoundary>}
      {subTab === 'mappings'     && isAdmin && <RetailErrorBoundary><MappingsTab        site={site} /></RetailErrorBoundary>}
      {subTab === 'users'        && isAdmin && <RetailErrorBoundary><SiteUsersTab    site={site} /></RetailErrorBoundary>}
      {subTab === 'clothing'     &&            <RetailErrorBoundary><ClothingTab       site={site} /></RetailErrorBoundary>}
      {subTab === 'site-cats'    && <RetailErrorBoundary><CategoriesSettingsTab site={site} isAdmin={isAdmin} onUnlinkedCount={setUnlinkedCatsCount} /></RetailErrorBoundary>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CategoriesSettingsTab — налаштування розділів сайту (взуття / одяг)
// ─────────────────────────────────────────────────────────────────────────────

const SECTION_LABELS = {
  shoes:    '🥾 Взуття',
  clothing: '👔 Одяг',
};

function CategoriesSettingsTab({ site, isAdmin, onUnlinkedCount }) {
  const [cats,    setCats]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState({});
  const [error,   setError]   = useState('');

  const reportCount = useCallback((list) => {
    if (onUnlinkedCount) onUnlinkedCount(list.filter(c => !c.section_type).length);
  }, [onUnlinkedCount]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await retailAPI.getCategories(site.id);
      setCats(r.data);
      reportCount(r.data);
    } catch { setError('Помилка завантаження категорій'); }
    finally { setLoading(false); }
  }, [site.id, reportCount]);

  useEffect(() => { load(); }, [load]);

  async function setSectionType(extId, type) {
    setSaving(prev => ({ ...prev, [extId]: true }));
    try {
      await retailAPI.patchCategory(site.id, extId, { section_type: type });
      setCats(prev => {
        const next = prev.map(c => c.external_id === extId ? { ...c, section_type: type } : c);
        reportCount(next);
        return next;
      });
    } catch { setError('Помилка збереження'); }
    finally { setSaving(prev => ({ ...prev, [extId]: false })); }
  }

  if (loading) return <div className="rt-loading">Завантаження категорій…</div>;
  if (error)   return <div className="rt-error">{error} <button className="rt-btn rt-btn-sm" onClick={load}>Повторити</button></div>;

  if (!cats.length) return (
    <div style={{ padding: 16, color: 'var(--text-secondary)' }}>
      Категорій не знайдено. Спочатку виконайте синхронізацію товарів — тоді тут з'являться розділи сайту.
    </div>
  );

  return (
    <div style={{ padding: 8 }}>
      <p style={{ margin: '0 0 12px', color: 'var(--text-secondary)', fontSize: 13 }}>
        Позначте кожен розділ сайту як «Взуття» або «Одяг». Товари з позначеного розділу будуть доступні у відповідній вкладці.
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table className="rt-table" style={{ minWidth: 480, tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            <col style={{ width: '22%' }} />
            <col style={{ width: '55%' }} />
            <col style={{ width: '23%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>Назва розділу</th>
              <th>Повний шлях</th>
              <th>Тип</th>
            </tr>
          </thead>
          <tbody>
            {cats.map(cat => (
              <tr key={cat.external_id}>
                <td style={{ fontWeight: 500, fontSize: 12, paddingRight: 8 }}>{cat.title || `#${cat.external_id}`}</td>
                <td style={{ fontSize: 11, color: 'var(--text-secondary)', wordBreak: 'break-word', lineHeight: 1.4 }}>
                  {cat.parent_path || '—'}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {(['shoes', 'clothing']).map(t => (
                    <button
                      key={t}
                      className={`rt-btn rt-btn-sm ${cat.section_type === t ? 'rt-btn-primary' : ''}`}
                      style={{ marginRight: 4, opacity: saving[cat.external_id] ? 0.6 : 1 }}
                      onClick={() => setSectionType(cat.external_id, cat.section_type === t ? null : t)}
                      disabled={!!saving[cat.external_id]}
                    >
                      {SECTION_LABELS[t]}
                    </button>
                  ))}
                  {!cat.section_type && (
                    <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>— не вибрано</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ClothingTab — вкладка одягу (аналог SiteProductsTab але для SM-артикулів)
// ─────────────────────────────────────────────────────────────────────────────

function ClothingTab({ site }) {
  const siteId = site.id;
  const [products,        setProducts]        = useState([]);
  const [total,           setTotal]           = useState(0);
  const [loading,         setLoading]         = useState(false);
  const [search,          setSearch]          = useState('');
  const [linked,          setLinked]          = useState(null);
  const [offset,          setOffset]          = useState(0);
  const [hasCats,         setHasCats]         = useState(true);
  const [loadError,       setLoadError]       = useState(false);
  // modal
  const [editProduct,     setEditProduct]     = useState(null);
  const [editAllSku,      setEditAllSku]      = useState('');
  const [editSaving,      setEditSaving]      = useState(false);
  const [editError,       setEditError]       = useState('');
  const [variants,        setVariants]        = useState([]);
  const [variantsLoading, setVariantsLoading] = useState(false);
  // Suggestions + lightbox
  const [suggestions,        setSuggestions]        = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestPreview,     setSuggestPreview]     = useState(null);
  const [lightboxUrl,        setLightboxUrl]        = useState(null);
  const _clAuthUser = useAuthStore((state) => state.user);
  const _clHasSupplierSuggest = !!(_clAuthUser?.permissions?.includes('supplier_suggest'));
  const LIMIT = 50;
  const searchTimerRef = useRef(null);

  const loadProducts = useCallback(async (opts = {}) => {
    setLoading(true);
    setLoadError(false);
    const { q = search, lnk = linked, off = 0 } = opts;
    try {
      const params = { q, offset: off, limit: LIMIT, section: 'clothing' };
      if (lnk === true)  params.linked = true;
      if (lnk === false) params.linked = false;
      const r = await retailAPI.getSiteProducts(siteId, params);
      setProducts(r.data.items);
      setTotal(r.data.total);
      setOffset(off);
      setHasCats(r.data.has_categories !== false);
    } catch { setLoadError(true); }
    finally { setLoading(false); }
  }, [siteId, search, linked]);

  useEffect(() => {
    loadProducts({ q: '', lnk: null, off: 0 });
    setSearch('');
    setLinked(null);
    setOffset(0);
  }, [siteId]); // eslint-disable-line

  async function openEdit(sp) {
    setEditProduct(sp);
    setEditAllSku(sp.all_sku || '');
    setEditError('');
    setSuggestions([]);
    setVariants([]);
    setVariantsLoading(true);
    try {
      const r = await retailAPI.getSiteProductVariants(siteId, sp.id, { available_only: false });
      setVariants(r.data);
    } catch {}
    finally { setVariantsLoading(false); }
    if (_clHasSupplierSuggest && sp.article_site) {
      setSuggestionsLoading(true);
      try {
        const r = await retailAPI.supplierSuggestions(sp.article_site);
        setSuggestions(r.data || []);
      } catch {}
      finally { setSuggestionsLoading(false); }
    }
  }

  async function saveEdit() {
    if (!isValidAllSku(editAllSku)) {
      setEditError('Невірний формат ALL SKU. Допустимо: ALL00000, SM00000, ALL00000Назва, SM00000Назва');
      return;
    }
    setEditSaving(true);
    setEditError('');
    try {
      // Навмисно шлемо editAllSku як є (навіть порожній рядок), а не null —
      // бекенд відрізняє "поле не чіпали" (null/відсутнє) від "очистили навмисно" (""),
      // інакше відв'язка не доходить до бази й товар "сам" залишається прив'язаним.
      await retailAPI.updateSiteProduct(siteId, editProduct.id, { all_sku: editAllSku.trim() });
      setProducts(prev => prev.map(p => p.id === editProduct.id ? { ...p, all_sku: editAllSku || null } : p));
      setEditProduct(null);
    } catch { setEditError('Помилка збереження'); }
    finally { setEditSaving(false); }
  }

  if (!hasCats && !products.length) return (
    <div style={{ padding: 16, color: 'var(--text-secondary)' }}>
      Розділи одягу не налаштовані. Перейдіть у вкладку <strong>«Категорії»</strong> і позначте відповідні розділи сайту як «👔 Одяг».
    </div>
  );

  return (
    <div>
      {/* Фільтри — один рядок */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="rt-input"
          placeholder="Пошук по артикулу, назві, SM-коду…"
          value={search}
          onChange={e => {
            const val = e.target.value;
            setSearch(val);
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
            searchTimerRef.current = setTimeout(() => loadProducts({ q: val, off: 0 }), 350);
          }}
          style={{ maxWidth: 260 }}
        />
        <div className="rt-filter-btns">
          <button className={`rt-btn rt-btn-sm${linked === null  ? ' rt-btn-primary' : ''}`} onClick={() => { setLinked(null);  loadProducts({ lnk: null,  off: 0 }); }}>Всі</button>
          <button className={`rt-btn rt-btn-sm${linked === false ? ' rt-btn-primary' : ''}`} onClick={() => { setLinked(false); loadProducts({ lnk: false, off: 0 }); }}>Без прив'язки</button>
          <button className={`rt-btn rt-btn-sm${linked === true  ? ' rt-btn-primary' : ''}`} onClick={() => { setLinked(true);  loadProducts({ lnk: true,  off: 0 }); }}>Прив'язані</button>
        </div>
        <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Всього: {total}</span>
      </div>

      {loadError && (
        <div className="rt-error">
          Помилка завантаження.{' '}
          <button className="rt-btn rt-btn-sm" onClick={() => loadProducts()}>Повторити</button>
        </div>
      )}

      {loading && <div className="rt-loading">Завантаження…</div>}

      {!loading && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table className="rt-table" style={{ minWidth: 700 }}>
              <thead>
                <tr>
                  <th>Артикул</th>
                  <th>Назва</th>
                  <th>Тип / колір</th>
                  <th>Розміри / наявність</th>
                  <th>SM-код</th>
                </tr>
              </thead>
              <tbody>
                {products.map(sp => {
                  const cp = sp.catalog;
                  return (
                    <tr key={sp.id} className={sp.all_sku ? 'rt-row-linked' : 'rt-row-unlinked'}
                      style={{ cursor: 'pointer' }} onClick={() => openEdit(sp)}>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{sp.article_site || '—'}</td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{sp.title_ua || sp.title_ru || '—'}</div>
                        {cp && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{cp.name}</div>}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {cp ? (
                          <>
                            <div>{cp.product_type}</div>
                            {cp.color && <div style={{ color: 'var(--text-secondary)' }}>{cp.color}</div>}
                          </>
                        ) : '—'}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {sp.no_sizes ? (
                          <span style={{ color: sp.available ? '#4caf50' : '#f44336' }}>
                            {sp.available ? '✓ В наявності' : '✗ Немає'}
                          </span>
                        ) : sp.sizes && sp.sizes.length ? (
                          <span style={{ wordBreak: 'break-word' }}>{sp.sizes.join(', ')}</span>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)' }}>—</span>
                        )}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {sp.all_sku ? (
                          <span style={{ color: '#4caf50', fontWeight: 600 }}>{sp.all_sku}</span>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!products.length && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>Товарів не знайдено</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Пагінація */}
          {total > LIMIT && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
              <button
                className="rt-btn rt-btn-sm"
                disabled={offset === 0}
                onClick={() => loadProducts({ off: Math.max(0, offset - LIMIT) })}
              >← Назад</button>
              <span style={{ fontSize: 13 }}>{offset + 1}–{Math.min(offset + LIMIT, total)} з {total}</span>
              <button
                className="rt-btn rt-btn-sm"
                disabled={offset + LIMIT >= total}
                onClick={() => loadProducts({ off: offset + LIMIT })}
              >Вперед →</button>
            </div>
          )}
        </>
      )}

      {/* Модал товару (деталі + варіанти + прив'язка SM) */}
      {editProduct && (
        <div className="rt-overlay" onClick={() => setEditProduct(null)}>
          <div className="rt-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>

            {/* Шапка */}
            <div className="rt-modal-header">
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>
                  {editProduct.title_ua || editProduct.title_ru || editProduct.article_site}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, fontFamily: 'monospace' }}>
                  {editProduct.article_site}
                  {editProduct.prom_id && (
                    <span style={{ marginLeft: 10 }}>
                      ID:{' '}
                      <a href={promProductUrl(site.domain, editProduct.prom_id)}
                        target="_blank" rel="noopener noreferrer"
                        style={{ color: 'inherit' }}
                        onClick={e => e.stopPropagation()}>
                        {editProduct.prom_id} 🔗
                      </a>
                    </span>
                  )}
                </div>
              </div>
              <button className="rt-modal-close" onClick={() => setEditProduct(null)}>✕</button>
            </div>

            <div className="rt-modal-body rt-modal-body--scroll">
              {/* Lightbox */}
              {lightboxUrl && (
                <div className="rt-lightbox" onClick={() => setLightboxUrl(null)}>
                  <button className="rt-lightbox-close" onClick={() => setLightboxUrl(null)}>✕</button>
                  <img src={lightboxUrl} alt="" className="rt-lightbox-img"
                    onClick={e => e.stopPropagation()} />
                </div>
              )}

              {/* Фото */}
              {editProduct.images?.length > 0 && (
                <div className="rt-product-gallery" style={{ marginBottom: 14 }}>
                  {editProduct.images.slice(0, 10).map((url, i) => (
                    <img
                      key={i}
                      src={url}
                      alt=""
                      className={`rt-gallery-thumb${i === 0 ? ' rt-gallery-thumb--main' : ''}`}
                      onClick={() => setLightboxUrl(url)}
                      onError={e => e.currentTarget.style.display = 'none'}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </div>
              )}

              {/* Варіанти на сайті */}
              <div className="rt-chars-section-title">Варіанти на сайті</div>
              {variantsLoading ? (
                <div className="rt-hint" style={{ marginBottom: 10 }}>Завантаження варіантів…</div>
              ) : variants.length > 0 ? (
                <div className="rt-tbl-wrap" style={{ marginBottom: 14 }}>
                  <table className="rt-table">
                    <thead>
                      <tr><th>ID</th><th>Артикул</th><th>Розмір</th><th>Ціна ₴</th><th>Наявн.</th><th>К-сть</th></tr>
                    </thead>
                    <tbody>
                      {editProduct.prom_id && (
                        <tr style={{ background: 'rgba(245,158,11,0.08)' }}>
                          <td style={{ fontSize: 10, color: 'var(--muted,#888)', fontFamily: 'monospace' }}>
                            <a href={promProductUrl(site.domain, editProduct.prom_id)}
                              target="_blank" rel="noopener noreferrer"
                              style={{ color: 'inherit', textDecoration: 'none' }}
                              onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                              onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                              onClick={e => e.stopPropagation()}
                            >{editProduct.prom_id} 🔗</a>
                          </td>
                          <td style={{ fontSize: 11 }}>{editProduct.article_site || '—'}</td>
                          <td>
                            {editProduct.base_size
                              ? <span style={{ background: '#f59e0b', color: '#fff', borderRadius: 3, padding: '1px 5px', fontWeight: 700, fontSize: 12 }}>{editProduct.base_size}</span>
                              : '—'}
                            <span style={{ fontSize: 10, color: 'var(--muted,#888)', marginLeft: 6 }}>основний</span>
                          </td>
                          <td>{editProduct.price ? `₴${editProduct.price}` : '—'}</td>
                          <td><span className={`rt-avail-dot${editProduct.available ? ' rt-avail-dot--on' : ''}`} /></td>
                          <td>—</td>
                        </tr>
                      )}
                      {variants.map(v => (
                        <tr key={v.id}>
                          <td style={{ fontSize: 10, color: 'var(--muted,#888)', fontFamily: 'monospace' }}>{v.external_id || '—'}</td>
                          <td style={{ fontSize: 11 }}>{v.article || '—'}</td>
                          <td>{v.size || '—'}</td>
                          <td>{v.price ? `₴${v.price}` : '—'}</td>
                          <td><span className={`rt-avail-dot${v.available ? ' rt-avail-dot--on' : ''}`} /></td>
                          <td>{v.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="rt-hint" style={{ marginBottom: 14 }}>Варіантів не знайдено</div>
              )}

              {/* Прив'язка SM */}
              <div className="rt-chars-section-title">Прив'язка до каталогу одягу</div>
              <div className="rt-field" style={{ marginBottom: 4 }}>
                <label className="rt-label">SM-код</label>
                <input
                  className="rt-input"
                  placeholder="SM00000"
                  value={editAllSku}
                  onChange={e => setEditAllSku(e.target.value.trim().toUpperCase())}
                  onKeyDown={e => e.key === 'Enter' && saveEdit()}
                />
                <div className="rt-hint">Залиште порожнім щоб прибрати прив'язку</div>
              </div>

              {/* Пряма прив'язка до одного товару постачальника (в обхід SM/ALL) */}
              {editProduct?.article_site && (
                <DirectLinkQuickAdd site={site} articleSite={editProduct.article_site} />
              )}

              {/* Підбір постачальника (одяг) */}
              {_clHasSupplierSuggest && (
                <div className="rt-suggestions-block">
                  {suggestionsLoading && (
                    <div className="rt-hint" style={{ marginBottom: 8 }}>🔍 Пошук відповідностей…</div>
                  )}
                  {!suggestionsLoading && suggestions.length > 0 && (
                    <>
                      <div className="rt-chars-section-title" style={{ marginBottom: 6 }}>
                        Можливі відповідності у постачальників
                      </div>
                      <div className="rt-suggest-grid">
                        {suggestions.map((s, i) => (
                          <div
                            key={i}
                            className={`rt-suggest-card${editAllSku === s.product_sku ? ' rt-suggest-card--selected' : ''}`}
                            onClick={() => setSuggestPreview(s)}
                            onDoubleClick={() => { setEditAllSku(s.product_sku); setSuggestPreview(null); }}
                            title="Одне натискання — перегляд\nПодвійне — вибір"
                          >
                            {s.photo
                              ? <img src={s.photo} alt="" className="rt-suggest-thumb"
                                  onError={e => e.currentTarget.style.display = 'none'} />
                              : <div className="rt-suggest-nophoto">📷</div>
                            }
                            <div className="rt-suggest-meta">
                              <div className="rt-suggest-sku">{s.product_sku}</div>
                              <div className="rt-suggest-code" title={s.supplier_code}>{s.supplier_code}</div>
                              <div className="rt-suggest-sup">{s.supplier_name}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {!suggestionsLoading && suggestions.length === 0 && editProduct?.article_site && (
                    <div className="rt-hint" style={{ marginBottom: 8, color: 'var(--text-secondary)' }}>
                      Відповідностей не знайдено
                    </div>
                  )}

                  {/* Overlay порівняння двох фото */}
                  {suggestPreview && (
                    <div className="rt-suggest-compare-overlay" onClick={() => setSuggestPreview(null)}>
                      <div className="rt-suggest-compare-box" onClick={e => e.stopPropagation()}>
                        <div className="rt-suggest-compare-photos">
                          <div className="rt-suggest-compare-side">
                            <div className="rt-suggest-compare-label">Товар на сайті</div>
                            {(editProduct?.thumb || editProduct?.images?.[0])
                              ? <img src={editProduct.thumb || editProduct.images[0]} alt="" className="rt-suggest-compare-img" />
                              : <div className="rt-suggest-nophoto" style={{ width: 280, height: 280 }}>📷</div>
                            }
                          </div>
                          <div className="rt-suggest-compare-side">
                            <div className="rt-suggest-compare-label">Постачальник</div>
                            {suggestPreview.photo
                              ? <img src={suggestPreview.photo} alt="" className="rt-suggest-compare-img" />
                              : <div className="rt-suggest-nophoto" style={{ width: 200, height: 200 }}>📷</div>
                            }
                          </div>
                        </div>
                        <div className="rt-suggest-compare-info">
                          <span className="rt-suggest-sku">{suggestPreview.product_sku}</span>
                          {suggestPreview.supplier_code && <span className="rt-suggest-code">{suggestPreview.supplier_code}</span>}
                          {suggestPreview.supplier_name && <span className="rt-suggest-sup">{suggestPreview.supplier_name}</span>}
                        </div>
                        <div className="rt-suggest-compare-actions">
                          <button className="rt-btn rt-btn-primary" onClick={() => { setEditAllSku(suggestPreview.product_sku); setSuggestPreview(null); }}>
                            ✓ Вибрати {suggestPreview.product_sku}
                          </button>
                          <button className="rt-btn" onClick={() => setSuggestPreview(null)}>Закрити</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {editError && <div className="rt-error" style={{ marginBottom: 8 }}>{editError}</div>}

              <div style={{ display: 'flex', gap: 8 }}>
                <button className="rt-btn rt-btn-primary" onClick={saveEdit} disabled={editSaving}>
                  {editSaving ? 'Збереження…' : 'Зберегти'}
                </button>
                <button className="rt-btn" onClick={() => setEditProduct(null)}>Скасувати</button>
                {editAllSku && (
                  <button className="rt-btn" style={{ marginLeft: 'auto', color: '#f44336' }}
                    onClick={() => setEditAllSku('')}>Очистити</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Root
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// ServerStatsTab — метрики сервера (тільки для адміна)
// ─────────────────────────────────────────────────────────────────────────────

function ServerStatsTab({ sites }) {
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const siteName = (id) => (sites || []).find(s => s.id === id)?.name || `#${id}`;

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await retailAPI.getServerStats();
      setStats(r.data);
    } catch { setError('Помилка завантаження'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  function Bar({ pct, warn = 70, crit = 85 }) {
    const color = pct >= crit ? '#ef4444' : pct >= warn ? '#f59e0b' : '#22c55e';
    return (
      <div style={{ background: 'var(--bg-card, rgba(0,0,0,.06))', borderRadius: 4, height: 8, overflow: 'hidden', marginTop: 4 }}>
        <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: color, transition: 'width .4s' }} />
      </div>
    );
  }

  function Metric({ label, value, sub, pct, warn, crit }) {
    const statusColor = pct != null
      ? (pct >= (crit ?? 85) ? '#ef4444' : pct >= (warn ?? 70) ? '#f59e0b' : '#22c55e')
      : 'var(--text-secondary)';
    return (
      <div style={{ background: 'var(--bg-card, rgba(0,0,0,.04))', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px', minWidth: 160 }}>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: statusColor }}>{value}</div>
        {sub  && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{sub}</div>}
        {pct != null && <Bar pct={pct} warn={warn} crit={crit} />}
      </div>
    );
  }

  if (loading) return <div className="rt-loading" style={{ padding: 24 }}>Завантаження…</div>;
  if (error)   return <div className="rt-error" style={{ padding: 16 }}>{error} <button className="rt-btn rt-btn-sm" onClick={load}>Повторити</button></div>;
  if (!stats)  return null;

  const { ram, swap, disk, load: ld, cpu_cores, uptime, db_size_mb, monitor_log,
          top_processes, gql_queue, running_syncs, scheduler } = stats;
  const uptimeStr = uptime ? `${uptime.days}д ${uptime.hours}г` : '—';

  return (
    <div style={{ padding: 16, maxWidth: 800 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Стан сервера</h3>
        <button className="rt-btn rt-btn-sm" onClick={load}>↻ Оновити</button>
      </div>

      {/* Картки метрик */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
        {ram && (
          <Metric
            label="RAM"
            value={`${ram.pct}%`}
            sub={`${ram.used_mb} / ${ram.total_mb} MB · вільно ${ram.avail_mb} MB`}
            pct={ram.pct} warn={75} crit={85}
          />
        )}
        {swap && (
          <Metric
            label="Swap"
            value={`${swap.used_mb} MB`}
            sub={`з ${swap.total_mb} MB (${swap.pct}%)`}
            pct={swap.pct} warn={20} crit={50}
          />
        )}
        {disk && (
          <Metric
            label="Диск"
            value={`${disk.pct}%`}
            sub={`${disk.used_gb} / ${disk.total_gb} GB · вільно ${disk.free_gb} GB`}
            pct={disk.pct} warn={70} crit={85}
          />
        )}
        {ld && (
          <Metric
            label={`CPU Load${cpu_cores ? ` (${cpu_cores} ядра)` : ''}`}
            value={String(ld['1m'])}
            sub={`5хв: ${ld['5m']} · 15хв: ${ld['15m']}`}
            pct={cpu_cores ? Math.round(ld['15m'] / cpu_cores * 100) : null}
            warn={60} crit={90}
          />
        )}
        {db_size_mb != null && (
          <Metric label="База даних" value={`${db_size_mb} MB`} sub="catalog.db" />
        )}
        <Metric label="Аптайм" value={uptimeStr} />
      </div>

      {/* Scheduler status */}
      {scheduler && (
        <div style={{ marginBottom: 16, padding: '8px 12px', borderRadius: 6, background: scheduler.alive ? 'rgba(34,197,94,.08)' : 'rgba(239,68,68,.10)', border: `1px solid ${scheduler.alive ? 'rgba(34,197,94,.25)' : 'rgba(239,68,68,.4)'}`, fontSize: 13, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <span>
            {scheduler.alive ? '🟢' : '🔴'} <b>Планувальник:</b> {scheduler.alive ? 'активний' : 'НЕ АКТИВНИЙ'}
          </span>
          {scheduler.last_tick && (
            <span style={{ color: 'var(--text-secondary)' }}>
              Останній тік: {new Date(scheduler.last_tick + 'Z').toLocaleTimeString('uk-UA', { timeZone: 'Europe/Kyiv' })}
            </span>
          )}
          {scheduler.stock_running?.length > 0 && (
            <span style={{ color: '#f59e0b' }}>
              ⏳ Зараз оновлюється: {scheduler.stock_running.map(id => `${siteName(id)} (#${id})`).join(', ')}
            </span>
          )}
        </div>
      )}

      {/* Активні синки */}
      {running_syncs?.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>
            ▶ Активні синхронізації ({running_syncs.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {running_syncs.map((s, i) => (
              <div key={i} style={{ background: 'var(--bg-card, rgba(0,0,0,.04))', borderRadius: 6, padding: '8px 12px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600 }}>{siteName(s.site_id)} <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>#{s.site_id}</span></span>
                <span style={{ color: 'var(--text-secondary)' }}>{s.phase}</span>
                {s.page != null && <span>сторінка {s.page}</span>}
                {s.size_total > 0 && (
                  <span style={{ marginLeft: 'auto', color: 'var(--text-secondary)' }}>
                    {s.size_done} / {s.size_total} товарів
                    <span style={{ display: 'inline-block', marginLeft: 8, width: 80, height: 6, background: 'var(--bg-card, rgba(0,0,0,.1))', borderRadius: 3, verticalAlign: 'middle' }}>
                      <span style={{ display: 'block', width: `${Math.min(100, Math.round(s.size_done / s.size_total * 100))}%`, height: '100%', background: '#3b82f6', borderRadius: 3 }} />
                    </span>
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Черга GQL */}
      {gql_queue?.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>
            📐 Очікують GQL-запит (розміри не отримано)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {gql_queue.map((q, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, padding: '5px 10px', background: 'var(--bg-card, rgba(0,0,0,.04))', borderRadius: 6 }}>
                <span style={{ fontWeight: 600, minWidth: 140 }}>{q.name}</span>
                <span style={{ marginLeft: 'auto', color: q.pending > 10000 ? '#ef4444' : q.pending > 1000 ? '#f59e0b' : '#22c55e', fontWeight: 700 }}>
                  {q.pending.toLocaleString()} варіантів
                </span>
              </div>
            ))}
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
              Всього: {gql_queue.reduce((a, q) => a + q.pending, 0).toLocaleString()} варіантів
            </div>
          </div>
        </div>
      )}

      {/* Топ процеси */}
      {top_processes?.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>
            Топ процеси за RAM
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr style={{ background: 'var(--bg-card, rgba(0,0,0,.06))', color: 'var(--text-secondary)' }}>
                  <th style={{ padding: '5px 8px', textAlign: 'right', borderRight: '1px solid var(--border)' }}>PID</th>
                  <th style={{ padding: '5px 8px', textAlign: 'right', borderRight: '1px solid var(--border)' }}>CPU%</th>
                  <th style={{ padding: '5px 8px', textAlign: 'right', borderRight: '1px solid var(--border)' }}>RAM%</th>
                  <th style={{ padding: '5px 8px', textAlign: 'right', borderRight: '1px solid var(--border)' }}>RSS MB</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left' }}>Команда</th>
                </tr>
              </thead>
              <tbody>
                {top_processes.map((p, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '4px 8px', textAlign: 'right', borderRight: '1px solid var(--border)', color: 'var(--text-secondary)' }}>{p.pid}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', borderRight: '1px solid var(--border)', color: parseFloat(p.cpu) > 50 ? '#f59e0b' : 'inherit' }}>{p.cpu}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', borderRight: '1px solid var(--border)', color: parseFloat(p.mem) > 10 ? '#ef4444' : parseFloat(p.mem) > 5 ? '#f59e0b' : 'inherit', fontWeight: parseFloat(p.mem) > 5 ? 700 : 400 }}>{p.mem}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', borderRight: '1px solid var(--border)' }}>{p.rss}</td>
                    <td style={{ padding: '4px 8px', color: 'var(--text-secondary)', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.raw || p.cmd}>{p.cmd}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Лог моніторингу */}
      {monitor_log?.length > 0 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>
            Останні записи моніторингу (кожні 6 годин)
          </div>
          <div style={{ background: 'var(--bg-code, rgba(0,0,0,.06))', borderRadius: 6, padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, lineHeight: 1.7 }}>
            {monitor_log.map((line, i) => (
              <div key={i} style={{ color: line.includes('ALERT') ? '#ef4444' : 'inherit' }}>{line}</div>
            ))}
          </div>
        </div>
      )}

      {/* Поради */}
      <div style={{ marginTop: 20, padding: '12px 16px', background: 'var(--bg-card, rgba(0,0,0,.04))', borderRadius: 8, fontSize: 13, color: 'var(--text-secondary)' }}>
        <strong>Пороги для апгрейду сервера:</strong><br />
        RAM ≥ 85% або вільно &lt; 150 MB · Swap &gt; 500 MB постійно · Диск ≥ 85% · Load15 ≥ 3<br />
        Telegram-сповіщення надходять автоматично при перевищенні.
      </div>
    </div>
  );
}


// ── Базові налаштування ────────────────────────────────────────────────────────
const PHOTO_FORMATS = ['1:1 (квадрат)', '4:5', '3:4', '16:9'];

function BaseSettingsTab({ sites, onRefresh, isAdmin = false }) {
  const activeSites = sites.filter(s => s.is_active);

  const [selectedSiteId, setSelectedSiteId] = useState(() => activeSites[0]?.id ?? null);
  const [activeSection,  setActiveSection]  = useState('main'); // 'main' | 'chars' | 'dict'

  const [settings,    setSettings]    = useState(() => {
    const init = {};
    activeSites.forEach(s => {
      const cn = s.extra?.char_names || {};
      init[s.id] = {
        photo_format:        s.extra?.photo_format        || '',
        default_category_id: s.extra?.default_category_id ? String(s.extra.default_category_id) : '',
        seo_enabled:         !!s.extra?.seo_enabled,
        labels_enabled:      !!s.extra?.labels_enabled,
        char_gender:         cn.gender   || '',
        char_material:       cn.material || '',
        char_season:         cn.season   || '',
        char_height:         cn.height   || '',
        char_features:       cn.features || '',
        char_model:          cn.model    || '',
      };
    });
    return init;
  });
  const [siteGroups,   setSiteGroups]   = useState([]);   // кастомні групи (тільки адмін)
  const [categories,   setCategories]   = useState({});
  const [catsLoading,  setCatsLoading]  = useState({});
  const [saving,       setSaving]       = useState({});
  const [saved,        setSaved]        = useState({});
  const [siteChars,    setSiteChars]    = useState({});
  const [charsLoading, setCharsLoading] = useState({});
  const [charsError,   setCharsError]   = useState({});

  async function loadCats(siteId, forceRefresh = false) {
    setCatsLoading(p => ({ ...p, [siteId]: true }));
    try {
      const r = await retailAPI.getCategories(siteId, forceRefresh);
      setCategories(p => ({ ...p, [siteId]: r.data }));
    } catch {
      setCategories(p => ({ ...p, [siteId]: [] }));
    } finally {
      setCatsLoading(p => ({ ...p, [siteId]: false }));
    }
  }

  async function loadChars(siteId) {
    setCharsLoading(p => ({ ...p, [siteId]: true }));
    setCharsError(p => ({ ...p, [siteId]: null }));
    try {
      const r = await retailAPI.getCharValues(siteId);
      setSiteChars(p => ({ ...p, [siteId]: r.data }));
    } catch {
      setCharsError(p => ({ ...p, [siteId]: 'Помилка завантаження' }));
    } finally {
      setCharsLoading(p => ({ ...p, [siteId]: false }));
    }
  }

  useEffect(() => {
    activeSites.forEach(s => {
      loadCats(s.id);
      if (s.platform === 'horoshop') loadChars(s.id);
    });
    if (isAdmin) {
      retailAPI.getSiteGroups().then(r => setSiteGroups(r.data)).catch(() => {});
    }
  }, []); // eslint-disable-line

  function setSiteField(siteId, field, value) {
    setSettings(p => ({ ...p, [siteId]: { ...p[siteId], [field]: value } }));
    setSaved(p => ({ ...p, [siteId]: false }));
  }

  async function saveSite(siteId) {
    setSaving(p => ({ ...p, [siteId]: true }));
    try {
      const { photo_format, default_category_id, seo_enabled, labels_enabled,
              char_gender, char_material, char_season, char_height, char_features,
              char_model } = settings[siteId] || {};
      if (isAdmin) {
        await retailAPI.updateSite(siteId, {
          extra: {
            photo_format:        photo_format || null,
            default_category_id: default_category_id ? parseInt(default_category_id) : null,
            seo_enabled:         seo_enabled    || false,
            labels_enabled:      labels_enabled || false,
            char_names: {
              gender:   char_gender   || null,
              material: char_material || null,
              season:   char_season   || null,
              height:   char_height   || null,
              features: char_features || null,
              model:    char_model    || null,
            },
          },
        });
      } else {
        await retailAPI.updateSiteDefaults(siteId, {
          default_category_id: default_category_id ? parseInt(default_category_id) : null,
        });
      }
      setSaved(p => ({ ...p, [siteId]: true }));
      onRefresh?.();
    } catch (e) {
      alert('Помилка збереження: ' + (e?.response?.data?.detail || String(e)));
    } finally {
      setSaving(p => ({ ...p, [siteId]: false }));
    }
  }

  if (!activeSites.length) {
    return <div className="rt-empty">Немає активних сайтів</div>;
  }

  const site = activeSites.find(s => s.id === selectedSiteId) || activeSites[0];
  const sv   = settings[site.id] || {};
  const cats = categories[site.id] || [];
  const isSaving     = saving[site.id];
  const isSaved      = saved[site.id];
  const isLoadingCats = catsLoading[site.id];
  const chars        = siteChars[site.id];
  const charsLoad    = charsLoading[site.id];
  const charsErr     = charsError[site.id];
  const isHoroshop   = site.platform === 'horoshop';

  // Словник: чи є дані
  const charMap = [
    { label: 'Стать',       key: 'genders',   svField: 'char_gender'   },
    { label: 'Сезон',       key: 'seasons',   svField: 'char_season'   },
    { label: 'Матеріал',    key: 'materials', svField: 'char_material' },
    { label: 'Висота',      key: 'heights',   svField: 'char_height'   },
    { label: 'Особливості', key: 'features',  svField: 'char_features' },
  ];
  const brands        = chars?.brands || [];
  const modelsByBrand = chars?.models_by_brand || {};
  const hasBrands     = brands.length > 0;
  const hasModels     = Object.keys(modelsByBrand).some(b => modelsByBrand[b]?.length > 0);
  const hasCharValues = chars && charMap.some(c => (chars[c.key] || []).length > 0);
  const hasDictData   = hasBrands || hasModels || hasCharValues;

  // Секції: для не-horoshop — тільки 'main'
  const sections = isHoroshop
    ? [
        { id: 'main',  label: 'Основне' },
        { id: 'chars', label: 'Характеристики' },
        { id: 'dict',  label: `Довідник${hasDictData ? '' : ''}` },
      ]
    : [{ id: 'main', label: 'Основне' }];

  return (
    <div style={{ display: 'flex', gap: 0, marginTop: 16, minHeight: 400 }}>

      {/* ── Ліва панель: список сайтів ── */}
      {(() => {
        // Для адміна — групуємо по кастомних групах сайтів, для звичайного — плоский список
        const groups = [];
        if (isAdmin && siteGroups.length > 0) {
          const seen = new Set();
          siteGroups.forEach(g => {
            const slist = activeSites.filter(s => (g.site_ids || []).includes(s.id));
            if (slist.length) {
              groups.push({ id: g.id, label: g.name, sites: slist });
              slist.forEach(s => seen.add(s.id));
            }
          });
          const rest = activeSites.filter(s => !seen.has(s.id));
          if (rest.length) groups.push({ id: null, label: 'Без групи', sites: rest });
        } else {
          groups.push({ id: null, label: isAdmin ? '' : '', sites: activeSites });
        }

        function SiteBtn({ s }) {
          const isActive = s.id === site.id;
          return (
            <button
              key={s.id}
              onClick={() => { setSelectedSiteId(s.id); setActiveSection('main'); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 12px 6px 14px',
                background: isActive ? 'var(--lg-hover, rgba(128,128,128,0.08))' : 'transparent',
                border: 'none',
                borderLeft: isActive ? `3px solid ${platformColor(s.platform)}` : '3px solid transparent',
                borderRadius: 0,
                cursor: 'pointer',
                textAlign: 'left',
                width: '100%',
                transition: 'background 0.1s',
              }}
            >
              <span style={{
                display: 'inline-block', width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                background: platformColor(s.platform),
              }} />
              <span style={{
                fontSize: 12, fontWeight: isActive ? 600 : 400,
                color: isActive ? 'var(--lg-text)' : 'var(--lg-text-2)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120,
              }}>{s.name}</span>
            </button>
          );
        }

        return (
          <div style={{
            width: 190, flexShrink: 0,
            borderRight: '1px solid var(--lg-border)',
            display: 'flex', flexDirection: 'column',
            paddingBottom: 8,
          }}>
            {!isAdmin && (
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--lg-text-3)', textTransform: 'uppercase', letterSpacing: '.06em', padding: '0 12px 8px' }}>
                Сайти
              </div>
            )}
            {groups.map((group, gi) => (
              <div key={group.id ?? gi}>
                {group.label && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: gi === 0 ? '0 12px 5px' : '10px 12px 5px',
                    borderTop: gi > 0 ? '1px solid var(--lg-border)' : 'none',
                  }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, color: 'var(--lg-text-3)',
                      textTransform: 'uppercase', letterSpacing: '.05em',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{group.label}</span>
                    <span style={{ fontSize: 10, color: 'var(--lg-text-3)', fontWeight: 400, flexShrink: 0 }}>
                      {group.sites.length}
                    </span>
                  </div>
                )}
                {group.sites.map(s => <SiteBtn key={s.id} s={s} />)}
              </div>
            ))}
          </div>
        );
      })()}

      {/* ── Права панель: налаштування вибраного сайту ── */}
      <div style={{ flex: 1, paddingLeft: 24, minWidth: 0 }}>

        {/* Заголовок сайту */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <span style={{
            display: 'inline-block', width: 9, height: 9, borderRadius: '50%',
            background: platformColor(site.platform), flexShrink: 0,
          }} />
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--lg-text)' }}>{site.name}</span>
          {site.domain && (
            <span style={{ fontSize: 12, color: 'var(--lg-text-3)', fontWeight: 400 }}>{site.domain}</span>
          )}
          <span style={{
            fontSize: 10, fontWeight: 600, padding: '2px 7px',
            background: isHoroshop ? 'rgba(139,92,246,0.10)' : 'var(--lg-surface-2,#f3f4f6)',
            color: isHoroshop ? '#7c3aed' : 'var(--lg-text-3)',
            borderRadius: 8, textTransform: 'uppercase', letterSpacing: '.04em',
          }}>{site.platform}</span>
        </div>

        {/* Мінітаби секцій */}
        {sections.length > 1 && (
          <div style={{
            display: 'flex', gap: 2, marginBottom: 18,
            borderBottom: '1px solid var(--lg-border)', paddingBottom: 0,
          }}>
            {sections.map(sec => (
              <button
                key={sec.id}
                onClick={() => setActiveSection(sec.id)}
                style={{
                  padding: '5px 14px',
                  fontSize: 12, fontWeight: activeSection === sec.id ? 600 : 400,
                  color: activeSection === sec.id ? 'var(--lg-text)' : 'var(--lg-text-2)',
                  background: 'transparent', border: 'none',
                  borderBottom: activeSection === sec.id ? '2px solid var(--rt-accent, #7c3aed)' : '2px solid transparent',
                  cursor: 'pointer', marginBottom: -1,
                  transition: 'color 0.1s',
                }}
              >
                {sec.label}
                {sec.id === 'dict' && hasDictData && (
                  <span style={{
                    marginLeft: 5, fontSize: 10, fontWeight: 400,
                    color: 'var(--rt-ok)', verticalAlign: 'middle',
                  }}>●</span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* ══ СЕКЦІЯ: Основне ══ */}
        {activeSection === 'main' && (
          <div style={{ maxWidth: 480 }}>

            {/* Формат фото */}
            {isAdmin && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--lg-text-2)', marginBottom: 5 }}>
                  Формат фото
                </div>
                <select
                  className="rt-input rt-select"
                  value={sv.photo_format || ''}
                  onChange={e => setSiteField(site.id, 'photo_format', e.target.value)}
                  style={{ maxWidth: 220 }}
                >
                  <option value="">— не вказано —</option>
                  {PHOTO_FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            )}

            {/* SEO + Labels — тільки Horoshop + адмін */}
            {isAdmin && isHoroshop && (
              <div style={{
                display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14,
                padding: '12px 14px',
                background: 'var(--lg-hover, rgba(128,128,128,0.04))',
                borderRadius: 8, border: '1px solid var(--lg-border)',
              }}>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => setSiteField(site.id, 'seo_enabled', !sv.seo_enabled)}
                >
                  <div className={`rt-toggle ${sv.seo_enabled ? 'rt-toggle--on' : ''}`} style={{ flexShrink: 0 }}>
                    <div className="rt-toggle-knob" />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--lg-text)' }}>SEO-поля</div>
                    <div style={{ fontSize: 11, color: 'var(--lg-text-3)' }}>title_seo / h1 / keywords</div>
                  </div>
                </div>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => setSiteField(site.id, 'labels_enabled', !sv.labels_enabled)}
                >
                  <div className={`rt-toggle ${sv.labels_enabled ? 'rt-toggle--on' : ''}`} style={{ flexShrink: 0 }}>
                    <div className="rt-toggle-knob" />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--lg-text)' }}>Стікери (labels)</div>
                    <div style={{ fontSize: 11, color: 'var(--lg-text-3)' }}>sale / new / hit</div>
                  </div>
                </div>
              </div>
            )}

            {/* Категорія за замовчуванням */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--lg-text-2)' }}>
                  Категорія за замовчуванням
                </span>
                {isHoroshop && (
                  <button
                    className="rt-btn rt-btn-sm"
                    style={{ padding: '1px 7px', fontSize: 11 }}
                    disabled={isLoadingCats}
                    onClick={() => loadCats(site.id, true)}
                  >↺ Оновити</button>
                )}
              </div>
              {isLoadingCats ? (
                <div style={{ fontSize: 11, color: 'var(--lg-text-3)' }}>Завантаження…</div>
              ) : (
                <select
                  className="rt-input rt-select"
                  value={sv.default_category_id || ''}
                  onChange={e => setSiteField(site.id, 'default_category_id', e.target.value)}
                  style={{ maxWidth: 380 }}
                >
                  <option value="">— не вибрано —</option>
                  {cats.map(c => (
                    <option key={c.id} value={c.external_id ?? c.id}>{c.title || c.external_id || c.id}</option>
                  ))}
                </select>
              )}
              <div style={{ fontSize: 11, color: 'var(--lg-text-3)', marginTop: 4 }}>
                Підставляється автоматично при створенні нового товару
              </div>
            </div>

            {/* Кнопка збереження */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 20 }}>
              <button className="rt-btn" onClick={() => saveSite(site.id)} disabled={isSaving}>
                {isSaving ? 'Збереження…' : 'Зберегти'}
              </button>
              {isSaved && <span style={{ fontSize: 12, color: 'var(--rt-ok)' }}>✓ Збережено</span>}
            </div>
          </div>
        )}

        {/* ══ СЕКЦІЯ: Характеристики (тільки Horoshop) ══ */}
        {activeSection === 'chars' && isHoroshop && (
          <div style={{ maxWidth: 520 }}>
            <div style={{ fontSize: 12, color: 'var(--lg-text-3)', marginBottom: 16 }}>
              {isAdmin
                ? 'Вкажіть значення поля «Name» з адмінки Horoshop (Каталог → Характеристики). Ці назви використовуються при публікації товарів.'
                : 'Маппінг характеристик встановлений адміністратором. Зверніться до адміна для зміни.'}
            </div>

            {/* Таблиця маппінгу */}
            <div style={{
              border: '1px solid var(--lg-border)', borderRadius: 8, overflow: 'hidden',
            }}>
              {[
                { label: 'Стать',       field: 'char_gender',   ph: 'stat',        hint: 'напр. stat або gender' },
                { label: 'Матеріал',    field: 'char_material', ph: 'materal',     hint: 'напр. materal або material' },
                { label: 'Сезон',       field: 'char_season',   ph: 'sezon',       hint: 'напр. sezon або season' },
                { label: 'Висота',      field: 'char_height',   ph: 'visota',      hint: 'напр. visota' },
                { label: 'Особливості', field: 'char_features', ph: 'osoblivost',  hint: 'напр. osoblivost' },
                { label: 'Модель',      field: 'char_model',    ph: 'model',       hint: 'напр. model' },
              ].map(({ label, field, ph, hint }, i) => (
                <div
                  key={field}
                  style={{
                    display: 'grid', gridTemplateColumns: '130px 1fr',
                    alignItems: 'center',
                    borderBottom: i < 5 ? '1px solid var(--lg-border)' : 'none',
                    background: i % 2 === 0 ? 'transparent' : 'var(--lg-hover, rgba(128,128,128,0.02))',
                  }}
                >
                  <div style={{ padding: '8px 12px', fontSize: 12, fontWeight: 600, color: 'var(--lg-text-2)' }}>
                    {label}
                  </div>
                  <div style={{ padding: '6px 10px 6px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      className="rt-input"
                      placeholder={ph}
                      value={sv[field] || ''}
                      disabled={!isAdmin}
                      onChange={e => isAdmin && setSiteField(site.id, field, e.target.value.trim())}
                      style={{
                        flex: 1, maxWidth: 180, fontSize: 12,
                        ...(!isAdmin ? { opacity: 0.6, cursor: 'default' } : {}),
                      }}
                    />
                    {!sv[field] && isAdmin && (
                      <span style={{ fontSize: 11, color: 'var(--lg-text-3)', fontStyle: 'italic' }}>{hint}</span>
                    )}
                    {sv[field] && (
                      <span style={{ fontSize: 11, color: 'var(--rt-ok)' }}>✓</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18 }}>
              <button className="rt-btn" onClick={() => saveSite(site.id)} disabled={isSaving || !isAdmin}>
                {isSaving ? 'Збереження…' : 'Зберегти'}
              </button>
              {isSaved && <span style={{ fontSize: 12, color: 'var(--rt-ok)' }}>✓ Збережено</span>}
            </div>
          </div>
        )}

        {/* ══ СЕКЦІЯ: Довідник (тільки Horoshop) ══ */}
        {activeSection === 'dict' && isHoroshop && (
          <div style={{ maxWidth: 560 }}>

            {charsLoad && (
              <div style={{ fontSize: 12, color: 'var(--lg-text-3)', padding: '20px 0' }}>
                Завантаження довідника…
              </div>
            )}

            {charsErr && !charsLoad && (
              <div style={{ fontSize: 12, color: 'var(--rt-error)', padding: '12px 0' }}>
                {charsErr}
                <button
                  className="rt-btn rt-btn-sm"
                  style={{ marginLeft: 10 }}
                  onClick={() => loadChars(site.id)}
                >Повторити</button>
              </div>
            )}

            {!charsLoad && !charsErr && !hasDictData && (
              <div style={{
                padding: '24px 20px', background: 'var(--lg-hover, rgba(128,128,128,0.04))',
                borderRadius: 8, border: '1px solid var(--lg-border)', color: 'var(--lg-text-3)',
                fontSize: 12, lineHeight: 1.6,
              }}>
                <div style={{ fontSize: 15, marginBottom: 6 }}>📂</div>
                Довідник ще порожній.<br />
                Він заповнюється автоматично під час синхронізації товарів.
              </div>
            )}

            {!charsLoad && hasDictData && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                {/* Бренди */}
                {hasBrands && (
                  <div>
                    <div style={{
                      fontSize: 11, fontWeight: 700, color: 'var(--lg-text-2)',
                      textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8,
                    }}>
                      Бренди <span style={{ fontWeight: 400, color: 'var(--lg-text-3)' }}>({brands.length})</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {brands.map(b => (
                        <span key={b} style={{
                          fontSize: 12, padding: '3px 10px',
                          background: 'var(--lg-surface-2, #f3f4f6)',
                          color: 'var(--lg-text)',
                          borderRadius: 20, border: '1px solid var(--lg-border)',
                        }}>{b}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Моделі по брендах */}
                {hasModels && (
                  <div>
                    <div style={{
                      fontSize: 11, fontWeight: 700, color: 'var(--lg-text-2)',
                      textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 10,
                    }}>
                      Моделі по брендах
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {Object.entries(modelsByBrand)
                        .filter(([, mods]) => mods?.length > 0)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([brand, mods]) => (
                          <div key={brand} style={{
                            padding: '8px 12px',
                            border: '1px solid var(--lg-border)', borderRadius: 8,
                            background: 'var(--lg-hover, rgba(128,128,128,0.02))',
                          }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--lg-text)', marginBottom: 6 }}>
                              {brand}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {[...mods].sort().map(m => (
                                <span key={m} style={{
                                  fontSize: 11, padding: '2px 8px',
                                  background: 'var(--lg-surface-2, #f3f4f6)',
                                  color: 'var(--lg-text-2)',
                                  borderRadius: 10, border: '1px solid var(--lg-border)',
                                }}>{m}</span>
                              ))}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Значення характеристик */}
                {hasCharValues && (
                  <div>
                    <div style={{
                      fontSize: 11, fontWeight: 700, color: 'var(--lg-text-2)',
                      textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 10,
                    }}>
                      Значення характеристик
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      {charMap.map(({ label, svField }) => {
                        const hsName    = sv[svField] || '';
                        const idEntries = (chars?.char_ids || {})[hsName] || [];
                        if (!idEntries.length) return null;
                        return (
                          <div key={svField} style={{
                            border: '1px solid var(--lg-border)', borderRadius: 8,
                            overflow: 'hidden', minWidth: 160,
                          }}>
                            <div style={{
                              padding: '6px 10px',
                              background: 'var(--lg-hover, rgba(128,128,128,0.04))',
                              borderBottom: '1px solid var(--lg-border)',
                              display: 'flex', alignItems: 'baseline', gap: 6,
                            }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--lg-text)' }}>{label}</span>
                              {hsName && (
                                <span style={{ fontSize: 10, color: 'var(--lg-text-3)', fontFamily: 'monospace' }}>{hsName}</span>
                              )}
                            </div>
                            <div style={{ padding: '4px 0' }}>
                              {idEntries.map((entry, i) => (
                                <div key={i} style={{
                                  display: 'grid', gridTemplateColumns: '32px 1fr auto',
                                  alignItems: 'center', gap: 6,
                                  padding: '3px 10px',
                                  borderBottom: i < idEntries.length - 1 ? '1px solid var(--lg-border)' : 'none',
                                }}>
                                  <span style={{ fontSize: 10, color: 'var(--lg-text-3)', fontFamily: 'monospace', textAlign: 'right' }}>
                                    {entry.id ?? '—'}
                                  </span>
                                  <span style={{ fontSize: 12, color: 'var(--lg-text)' }}>{entry.ua}</span>
                                  {entry.ru && entry.ru !== entry.ua && (
                                    <span style={{ fontSize: 11, color: 'var(--lg-text-3)' }}>{entry.ru}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Вкладка "Префікси": таблиця «слово з назви → префікс артикула»
// ─────────────────────────────────────────────────────────────────────────────
function BrandPrefixesTab() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [filter,  setFilter]  = useState('');
  const [draft,   setDraft]   = useState({ keyword: '', prefix: '', label: '' });
  const [edits,   setEdits]   = useState({});   // {id: {keyword, prefix, label}}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await retailAPI.getBrandPrefixes();
      setRows(r.data || []);
      setEdits({});
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Не вдалось завантажити таблицю');
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const val = (r, k) => (edits[r.id] ? edits[r.id][k] : r[k]);
  const setEdit = (r, k, v) => setEdits(prev => ({ ...prev, [r.id]: { keyword: r.keyword, prefix: r.prefix, label: r.label, ...(prev[r.id] || {}), [k]: v } }));

  async function saveRow(r) {
    try {
      await retailAPI.updateBrandPrefix(r.id, edits[r.id]);
      await load();
    } catch (e) { setError(e.response?.data?.detail || 'Помилка збереження'); }
  }
  async function removeRow(r) {
    if (!window.confirm(`Видалити «${r.keyword}» → ${r.prefix}?`)) return;
    try { await retailAPI.deleteBrandPrefix(r.id); await load(); }
    catch (e) { setError(e.response?.data?.detail || 'Помилка видалення'); }
  }
  async function addRow() {
    try {
      await retailAPI.createBrandPrefix(draft);
      setDraft({ keyword: '', prefix: '', label: '' });
      await load();
    } catch (e) { setError(e.response?.data?.detail || 'Помилка додавання'); }
  }

  const q = filter.trim().toLowerCase();
  const shown = rows.filter(r => !q || r.keyword.includes(q) || r.prefix.includes(q) || (r.label || '').toLowerCase().includes(q));

  return (
    <div className="rt-form-card" style={{ maxWidth: 760 }}>
      <div className="rt-form-card-title">Префікси брендів для артикулів</div>
      <p style={{ fontSize: 12, color: 'var(--lg-text-3)', margin: '0 0 10px' }}>
        Якщо в назві товару є слово зліва — артикул починається з префікса: ac-17710 (Horoshop), ac-17710M / ac-17710W (Prom).
        Якщо збігаються кілька слів, береться довше (jordan важливіше за nike). Діє для сайтів групи «Макс».
      </p>
      {error && <div className="rt-error" style={{ marginBottom: 8 }}>{error}</div>}
      <input className="rt-input" placeholder="Пошук…" value={filter} onChange={e => setFilter(e.target.value)} style={{ marginBottom: 8 }} />
      {loading ? <div>Завантаження…</div> : (
        <table className="rt-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', borderRight: '1px solid var(--lg-border, #ddd)' }}>Слово з назви</th>
              <th style={{ textAlign: 'left', borderRight: '1px solid var(--lg-border, #ddd)' }}>Префікс</th>
              <th style={{ textAlign: 'left', borderRight: '1px solid var(--lg-border, #ddd)' }}>Бренд</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {shown.map(r => (
              <tr key={r.id}>
                <td style={{ borderRight: '1px solid var(--lg-border, #ddd)' }}><input className="rt-input" value={val(r, 'keyword')} onChange={e => setEdit(r, 'keyword', e.target.value)} /></td>
                <td style={{ borderRight: '1px solid var(--lg-border, #ddd)', width: 90 }}><input className="rt-input" value={val(r, 'prefix')} onChange={e => setEdit(r, 'prefix', e.target.value)} /></td>
                <td style={{ borderRight: '1px solid var(--lg-border, #ddd)' }}><input className="rt-input" value={val(r, 'label')} onChange={e => setEdit(r, 'label', e.target.value)} /></td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {edits[r.id] && <button className="rt-btn rt-btn-sm" onClick={() => saveRow(r)}>Зберегти</button>}
                  <button className="rt-btn rt-btn-sm" onClick={() => removeRow(r)} title="Видалити">🗑</button>
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ borderRight: '1px solid var(--lg-border, #ddd)' }}><input className="rt-input" placeholder="нове слово" value={draft.keyword} onChange={e => setDraft({ ...draft, keyword: e.target.value })} /></td>
              <td style={{ borderRight: '1px solid var(--lg-border, #ddd)' }}><input className="rt-input" placeholder="ac" value={draft.prefix} onChange={e => setDraft({ ...draft, prefix: e.target.value })} /></td>
              <td style={{ borderRight: '1px solid var(--lg-border, #ddd)' }}><input className="rt-input" placeholder="Бренд" value={draft.label} onChange={e => setDraft({ ...draft, label: e.target.value })} /></td>
              <td><button className="rt-btn rt-btn-sm" onClick={addRow} disabled={!draft.keyword.trim() || !draft.prefix.trim()}>+ Додати</button></td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function RetailTab({ openSiteId, onSiteOpened, addProductPrefill } = {}) {
  const [innerTab,     setInnerTab]     = useState(() => addProductPrefill ? 'product' : 'sites');
  const [selectedSite, setSelectedSite] = useState(null);
  const [sites,        setSites]        = useState([]);
  const [platforms,    setPlatforms]    = useState([]);
  const [loadError,    setLoadError]    = useState(false);

  const _rtUser    = useAuthStore((state) => state.user);
  const _rtIsAdmin = !!(_rtUser?.is_admin && !_rtUser?.impersonated_by);
  const canPublish = _rtIsAdmin || !!(_rtUser?.permissions?.includes('retail_publish'));
  const canBrandPrefixes = _rtIsAdmin || !!(_rtUser?.permissions?.includes('brand_prefixes'));

  const loadData = useCallback(async () => {
    setLoadError(false);
    try {
      const [sr, pr] = await Promise.all([retailAPI.getSites(), retailAPI.getPlatforms()]);
      setSites(sr.data);
      setPlatforms(pr.data);
    } catch { setLoadError(true); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Відкриваємо конкретний сайт (напр. клік по сповіщенню про критичну
  // помилку в шапці) — щойно список сайтів завантажився.
  useEffect(() => {
    if (!openSiteId || sites.length === 0) return;
    const s = sites.find(s => s.id === openSiteId);
    if (s) {
      setInnerTab('sites');
      setSelectedSite(s);
    }
    onSiteOpened?.();
  }, [openSiteId, sites]); // eslint-disable-line

  // Якщо немає прав на "Новий товар" — залишаємось на сайтах
  useEffect(() => {
    if (!canPublish && innerTab === 'product') setInnerTab('sites');
  }, [canPublish, innerTab]);

  const activeSites = sites.filter(s => s.is_active);

  function handleTabChange(tab) {
    if (tab === 'product' && !canPublish) return;
    setInnerTab(tab);
    setSelectedSite(null);
  }

  return (
    <div className="rt-root">
      <div className="rt-tabs">
        <button className={`rt-tab ${innerTab === 'sites'    ? 'rt-tab--active' : ''}`} onClick={() => handleTabChange('sites')}>Сайти</button>
        {canPublish && (
          <button className={`rt-tab ${innerTab === 'product' ? 'rt-tab--active' : ''}`} onClick={() => handleTabChange('product')}>Новий товар</button>
        )}
        {canPublish && (
          <button className={`rt-tab ${innerTab === 'base'   ? 'rt-tab--active' : ''}`} onClick={() => handleTabChange('base')}>⚙ Базові налаштування</button>
        )}
        {canBrandPrefixes && (
          <button className={`rt-tab ${innerTab === 'prefixes' ? 'rt-tab--active' : ''}`} onClick={() => handleTabChange('prefixes')}>🏷 Префікси</button>
        )}
        {_rtIsAdmin && (
          <button className={`rt-tab ${innerTab === 'server' ? 'rt-tab--active' : ''}`} onClick={() => handleTabChange('server')}>🖥 Сервер</button>
        )}
      </div>

      {loadError && (
        <div className="rt-error" style={{ margin: '16px 0' }}>
          Помилка завантаження сайтів.{' '}
          <button className="rt-btn rt-btn-sm" onClick={loadData}>Повторити</button>
        </div>
      )}

      {innerTab === 'sites' && selectedSite === null && (
        <SitesTab sites={sites} platforms={platforms} onRefresh={loadData} onSelectSite={setSelectedSite} />
      )}
      {innerTab === 'sites' && selectedSite !== null && (
        <SiteDetailTab site={selectedSite} sites={sites} onBack={() => setSelectedSite(null)} />
      )}
      {innerTab === 'product' && canPublish && (
        <RetailErrorBoundary><ProductTab sites={activeSites} prefill={addProductPrefill} /></RetailErrorBoundary>
      )}
      {innerTab === 'base' && canPublish && (
        <RetailErrorBoundary><BaseSettingsTab sites={sites} onRefresh={loadData} isAdmin={_rtIsAdmin} /></RetailErrorBoundary>
      )}
      {innerTab === 'prefixes' && canBrandPrefixes && (
        <RetailErrorBoundary><BrandPrefixesTab /></RetailErrorBoundary>
      )}
      {innerTab === 'server' && _rtIsAdmin && (
        <RetailErrorBoundary><ServerStatsTab sites={sites} /></RetailErrorBoundary>
      )}
    </div>
  );
}
